/**
 * PatientDetailPanel
 *
 * Extração do dialog de detalhes de Patients.tsx em um componente reutilizável.
 * Toda a lógica de estado, busca de dados e JSX foi movida para cá.
 *
 * Usado em:
 *  - Patients.tsx  → dentro do Dialog existente
 *  - Agenda.tsx    → lado a lado com o formulário de agendamento
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Avatar, Chip, IconButton, Button, TextField, Checkbox,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
  Tooltip, Divider, Grid, Card, CardContent, Paper,
  Stack, Badge, Collapse, useTheme, useMediaQuery, Dialog,
  DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Radio, RadioGroup, FormControlLabel, FormLabel, Skeleton,
} from '@mui/material';
import {
  Close as CloseIcon, Edit as EditIcon,
  CalendarToday as CalendarIcon,
  FolderOpen as FolderIcon, AttachMoney as MoneyIcon, Warning as WarningIcon,
  Favorite as HeartIcon, Medication as MedicationIcon, Science as ScienceIcon,
  Description as DocIcon, TrendingUp as TrendingIcon, Schedule as ScheduleIcon,
  EventAvailable as EventIcon, AccessTime as TimeIcon, EventRepeat as EventRepeatIcon,
  Cake as CakeIcon, Bloodtype as BloodIcon, EventNote as EventNoteIcon,
  OpenInNew as OpenInNewIcon, UploadFile as UploadFileIcon, Download as DownloadIcon, DeleteOutline as DeleteIcon,
  AttachFile as AttachFileIcon, InsertDriveFile as FileIcon, Image as ImageIcon,
  ZoomIn as ZoomInIcon, Star as StarIcon, Folder as FolderDocIcon, HistoryEdu as ContractIcon,
  Timeline as TimelineIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  MedicalServices as MedicalIcon, PlayArrow as PlayIcon, Undo as UndoIcon,
  CheckCircle as CheckCircleIcon, Inventory as InventoryIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon, Add as AddIcon, Person as PersonIcon,
  Phone as PhoneIcon, Print as PrintIcon,
  Description as NfseIcon,
  Article as ReportIcon, GppGood as SignIcon, Send as SendIcon, LinkOff as LinkOffIcon,
  Visibility as VisibilityIcon,
  PostAdd as PostAddIcon,
  Assignment as AssessmentIcon,
  GridOn as OdontogramIcon, Healing as DentalTreatmentIcon,
  MedicalInformation as DiagnosisIcon,
  CardMembership as PlanSellIcon,
  QrCode2 as QrCode2Icon,
  AssignmentTurnedInOutlined,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { useCashRegisterPrompt } from '../hooks/useCashRegisterPrompt';
import { useNfseServiceCodePrompt } from '../hooks/useNfseServiceCodePrompt';
import { resolveEvolutionEntryFormat } from '../utils/clinicEvolutionFormat';
import {
  printClinicalEvolutionTextOnlyDocument,
  printPatientClinicalEvolutionsExport,
} from '../utils/clinicalEvolutionFullPrint';
import { todayYmdBr } from '../utils/localCalendarDate';
import PatientProfileAppointmentRow from './clinic/PatientProfileAppointmentRow';
import PatientAppointmentHistoryTable from './clinic/PatientAppointmentHistoryTable';
import {
  detectWeekendSessionHits,
  shiftDateToWeekday,
  ymdToLocalDate,
} from '../utils/agendaRecurringBulkSlots';
import {
  type AppointmentBillingLike,
  isPaidSessionAppointment,
  PAID_SESSION_LABEL,
  paidSessionChipSx,
} from '../utils/appointmentBilling';
import { buildClinicLetterheadBlockHtml, buildLetterheadFooterUnitsHtml, isClinicLetterheadEnabled, buildLetterheadTopSpacerHtml, clinicPrintAccentVarsStyle } from '../utils/clinicPrintLetterhead';
import { printClinicHtml } from '../utils/clinicPrintHtml';
import ClinicPatientTags from './clinic/ClinicPatientTags';
import ClinicalHtmlField from './ClinicalHtmlField';
import HealthcareEvolutionDialog from './HealthcareEvolutionDialog';
import { formatDiagnosisPair, formatPrimaryDiagnosisLabel } from '../utils/evolutionPrimaryDiagnosis';
import HealthcareMedicalReportDialog from './HealthcareMedicalReportDialog';
import PrescriptionFormDialog from './clinic/PrescriptionFormDialog';
import PrescriptionActionsBar from './clinic/PrescriptionActionsBar';
import { MemedPrescriptionFeedback } from './clinic/MemedPrescriptionButton';
import { useMemed } from '../hooks/useMemed';
import { subscribeMemedPrescriptionSaved } from '../utils/memedWorkspaceChannel';
import SignPrescriptionDialog from './clinic/SignPrescriptionDialog';
import { isSignedPrescription } from '../utils/prescriptionSignature';
import { openSignedPrescriptionPdf, type BatchSignResult } from '../services/documentSignatureService';
import SendDocumentDialog from './SendDocumentDialog';
import PrescriptionBatchPrintDialog, {
  buildPrescriptionPrintQueueFromIds,
  type PrescriptionPrintQueueItem,
} from './clinic/PrescriptionBatchPrintDialog';
import PatientDocumentThumbnail from './clinic/PatientDocumentThumbnail';
import { useClinicalImageAttachFlow } from './common/ClinicalImageAttach';
import PatientDocumentActions from './clinic/PatientDocumentActions';
import PatientPlanBadge from './clinic/PatientPlanBadge';
import PatientFamilyOverviewCard from './clinic/PatientFamilyOverviewCard';
import { normalizePatientFamilyRelatives } from '../utils/patientFamily';
import PatientActivityStatusChip from './clinic/PatientActivityStatusChip';
import LeadAdOriginCard from './LeadAdOriginCard';
import PatientPanelHeader, { type PatientPanelHeaderAction } from './PatientPanelHeader';
import PatientPanelMobileTabs from './PatientPanelMobileTabs';
import SellPlanDialog from './clinic/SellPlanDialog';
import { usePlansEnabled } from '../hooks/usePlansEnabled';
import { useIdocEnabled } from '../hooks/useIdocEnabled';
import PatientIdocTab from './idoc/PatientIdocTab';
import { idocService } from '../services/idocService';
import inboxService from '../services/inboxService';
import { formatPatientDocumentDate } from '../utils/patientDocumentPreview';
import { clinicalHtmlToPlainPreview } from '../utils/clinicalHtml';
import { isSignedMedicalReport } from '../utils/medicalReportSignature';
import { useAuth } from '../contexts/AuthContext';
import { canDeleteProtectedRecords } from '../config/permissions';
import { useClinicalEvolutionsAccess } from '../hooks/useClinicalEvolutionsAccess';
import {
  canDeleteEvolutionRecord,
  canEditEvolutionRecord,
  canInvalidateEvolutionRecord,
  evolutionDeleteTooltip,
  evolutionEditTooltip,
  evolutionInvalidateTooltip,
  isEvolutionInvalidated,
  isEvolutionSigned,
} from '../utils/evolutionRecordPolicy';
import EvolutionAdditionsSection from './evolution/EvolutionAdditionsSection';
import EvolutionAdditionsDialog from './evolution/EvolutionAdditionsDialog';
import EvolutionInvalidateDialog from './evolution/EvolutionInvalidateDialog';
import EvolutionTimelineScrollSentinel from './evolution/EvolutionTimelineScrollSentinel';
import EvolutionInvalidationBanner from './evolution/EvolutionInvalidationBanner';
import EvolutionAdditionsDisplay from './evolution/EvolutionAdditionsDisplay';
import { evolutionInvalidateDialogSubtitle } from './evolution/evolutionInvalidateSubtitle';
import InventoryPlanItemReservations from './InventoryPlanItemReservations';
import {
  getReceivablePaymentMethodLabel,
  isCardInstallmentPaymentMethod,
  isDebitCardPaymentMethod,
  CARD_INSTALLMENT_OPTIONS,
  parseCardInstallments,
  formatCardInstallmentOption,
} from '../constants/receivablePaymentMethods';
import { useEnabledCardBrands } from '../hooks/useEnabledCardBrands';
import { DEFAULT_CARD_BRAND } from '../utils/cardBrands';
import CardBrandSelect from './financial/CardBrandSelect';
import { useReceivablePaymentMethods } from '../hooks/useReceivablePaymentMethods';
import { resolveReceivablePaymentDate } from '../utils/receivablePaymentDate';
import { AWAITING_INSURANCE_SETTLEMENT_LABEL, isAwaitingInsuranceSettlement } from '../utils/insuranceSettlement';
import {
  evolutionViewerHasFollowUpSection,
  evolutionPrintableListLabel,
  formatEvolutionReturnWorkflowLines,
  getEvolutionIndicationAttachment,
  getEvolutionNonIndicationPrintables,
  getEvolutionReturnWorkflow,
  normalizeEvolutionProcedureLabels,
  parseEvolutionPlanExecutions,
  formatEvolutionPlanExecutionSummaryLine,
} from './evolution/evolutionViewHelpers';
import { dispatchBudgetUpdated, onBudgetUpdated } from '../utils/budgetUpdatedEvent';
import { printClinicReceiptFromTransaction } from '../utils/printClinicReceipt';
import { canEmitNfseForReceivable } from '../utils/nfseReceivable';
import ClinicNfseActions from './clinic/ClinicNfseActions';
import AsaasChargeDialog from './asaas/AsaasChargeDialog';
import { AsaasReceivableChargeButton, AsaasReceivableChargeChip } from './asaas/AsaasReceivableChargeButton';
import { receivableOpenBalance, useAsaasStatus } from '../services/asaasService';
import { useBudgetReceivableExpand } from '../hooks/useBudgetReceivableExpand';
import {
  getBudgetItemSessionCount,
  getMultiSessionBudgetItems,
  isBudgetTransaction,
  type BudgetItemRow,
} from '../utils/budgetReceivable';
import BudgetItemsInlineList from './financial/BudgetItemsInlineList';
import { MOBILE_FORM_SURFACE_CLASS } from '../utils/mobileFormLayout';
import PatientPaidSessionsCard from './PatientPaidSessionsCard';
import PatientPhysiotherapyAssessmentTab from './physiotherapy/PatientPhysiotherapyAssessmentTab';
import PatientOdontogramTab, { type PatientOdontogramSection } from './odontology/PatientOdontogramTab';
import PatientDentalTreatmentsTab from './odontology/PatientDentalTreatmentsTab';
import PatientContractsTab from './signature/PatientContractsTab';
import MedicalRecordDeliveryDialog from './signature/MedicalRecordDeliveryDialog';
import { useClinicMedicalSpecialty } from '../hooks/useClinicMedicalSpecialty';
import {
  APPOINTMENT_TYPE_LABELS,
  PATIENT_PANEL_THEME,
  getPatientPanelStatusConfig,
  patientPanelBadgeSx,
  patientPanelContentSx,
  patientPanelLoadingSkeletonSx,
} from './patientPanelTheme';

/* ═══════════════════════════════════════════════════════════════
   HELPERS (mesmos de Patients.tsx)
   ═══════════════════════════════════════════════════════════════ */

const getStatusConfig = getPatientPanelStatusConfig;

const getGenderLabel = (g: string) => {
  const m: Record<string, string> = {
    masculine: 'Masculino', feminine: 'Feminino', other: 'Outro', prefer_not_say: 'Prefiro não dizer',
  };
  return m[g] || g || '-';
};

const getDocTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    contract: 'Contrato', id_document: 'Doc. de Identidade', insurance_card: 'Carteirinha',
    exam: 'Exame', exam_result: 'Resultado de Exame', xray: 'Raio-X', photo: 'Foto',
    consent_form: 'Termo de Consentimento', other: 'Outro',
  };
  return labels[type] || type;
};

const getDocTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    contract: '#2563eb', id_document: '#7c3aed', insurance_card: '#059669',
    exam: '#0891b2', exam_result: '#d97706', xray: '#475569', photo: '#ec4899',
    consent_form: '#3b82f6', other: '#6b7280',
  };
  return colors[type] || '#6b7280';
};

type DocUploadItem = {
  key: string;
  file: File;
  title: string;
};

const docUploadFileKey = (file: File) => `${file.name}|${file.size}|${file.lastModified}`;

const defaultDocUploadTitle = (file: File) => file.name.replace(/\.[^/.]+$/, '') || file.name;

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  try { return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR'); } catch { return '-'; }
};

const todayLocalISODate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const escapeHtml = (raw: unknown) =>
  String(raw ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Para atributo HTML (ex.: src da logo) — não usar escapeHtml inteiro (&quot; quebraria o atributo). */
const escapeAttr = (raw: unknown) =>
  String(raw ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/* ── Constantes financeiras (mesmo conjunto usado em Financial/AccountsReceivable) ── */
const RECEIVABLE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'consulta_medica', label: 'Consulta Médica' },
  { value: 'procedimento', label: 'Procedimento' },
  { value: 'exame', label: 'Exame' },
  { value: 'cirurgia', label: 'Cirurgia' },
  { value: 'retorno', label: 'Retorno' },
  { value: 'avaliacao', label: 'Avaliação' },
  { value: 'tratamento', label: 'Tratamento Estético' },
  { value: 'convenio', label: 'Convênio / Plano' },
  { value: 'particular', label: 'Particular' },
  { value: 'venda_produtos', label: 'Venda de Produtos' },
  { value: 'outros', label: 'Outros' },
];

const getReceivableStatusInfo = (s: string) => {
  switch (s) {
    case 'paid': return { label: 'Recebido', bg: '#d1fae5', color: '#059669' };
    case 'card_authorized': return { label: 'Pago no cartão', bg: '#dbeafe', color: '#1d4ed8' };
    case 'partial': return { label: 'Parcial', bg: '#dbeafe', color: '#1d4ed8' };
    case 'overdue': return { label: 'Atrasado', bg: '#fee2e2', color: '#dc2626' };
    case 'cancelled': return { label: 'Cancelado', bg: '#f3f4f6', color: '#6b7280' };
    case 'pending':
    default:
      return { label: 'Pendente', bg: '#fef3c7', color: '#d97706' };
  }
};

const isReceivablePaidStatus = (status: string) =>
  status === 'paid' || status === 'card_authorized';

const getPaymentMethodLabel = getReceivablePaymentMethodLabel;

const calculateAge = (birthDate?: string) => {
  if (!birthDate) return '';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return `${age} anos`;
};

/* ═══════════════════════════════════════════════════════════════
   TIPOS
   ═══════════════════════════════════════════════════════════════ */

export type PatientPanelTabKey = 'profile' | 'history' | 'evolutions' | 'medical_reports' | 'assessment' | 'odontogram' | 'periodontal' | 'dental_treatments' | 'financial' | 'docs' | 'contracts' | 'images' | 'idoc' | 'procedures' | 'diagnoses';

/** Abas exclusivas de clínicas com especialidade Odontologia. */
const ODONTOLOGY_TAB_KEYS: PatientPanelTabKey[] = ['odontogram', 'periodontal', 'dental_treatments'];

function resolvePatientPanelTab(tab: PatientPanelTabKey): PatientPanelTabKey {
  if (tab === 'periodontal') return 'odontogram';
  if (tab === 'contracts') return 'docs';
  if (tab === 'idoc') return 'images';
  return tab;
}

function odontogramSectionFromInitialTab(tab: PatientPanelTabKey | undefined): PatientOdontogramSection {
  return tab === 'periodontal' ? 'periodontal' : 'chart';
}

export type PatientPanelVariant = 'full' | 'agenda-embedded' | 'inbox-embedded';

export type LinkedAppointmentContext = {
  id?: string | null;
  appointment_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  status?: string | null;
  appointment_type?: string | null;
  title?: string | null;
  created_by_name?: string | null;
  created_at?: string | null;
  procedure_ids?: string[];
  billing_procedure_id?: string | null;
  notes?: string | null;
  billing_mode?: string | null;
  billing_source?: string | null;
  patient_ticket_allocation_id?: string | null;
};

export type LinkedAppointmentProcedure = {
  id?: string;
  name?: string | null;
  code?: string | null;
  duration_minutes?: number | null;
};

export interface PatientDetailPanelProps {
  patientId: string;
  /** Dados iniciais já carregados (evita fetch extra). */
  initialPatient?: any;
  /** Layout completo (Pacientes) ou contextual na agenda (ações rápidas). */
  variant?: PatientPanelVariant;
  /** Aba aberta ao montar (ex.: inbox abrindo direto no histórico). */
  initialTab?: PatientPanelTabKey;
  /** Inbox: quando definido, docs e imagens ganham "Enviar no chat" da conversa aberta. */
  onSendMediaToChat?: (url: string, type: string, fileName?: string) => void;
  /** Inbox dinâmico: agendar / mensagem de saúde / desvincular (mesma barra da agenda). */
  onInboxSchedule?: () => void;
  onInboxHealthMessage?: () => void;
  onInboxUnlinkPatient?: () => void;
  /** Recarrega dados do paciente (ícone no cabeçalho do inbox). */
  onRefresh?: () => void;
  onClose?: () => void;
  /** Agenda / inbox: voltar ao formulário da consulta ou à grade. */
  onPanelBack?: () => void;
  panelBackLabel?: string;
  /** Recebe o paciente atual do painel (mesmo fluxo de Pacientes: lápis = editar ficha). */
  onEdit?: (patient: any) => void;
  /** Chamado quando o avatar é atualizado (para o pai sincronizar a lista). */
  onPatientUpdated?: (patient: any) => void;
  /**
   * Quando fornecido, o botão "Orçamento" chama esta função em vez de abrir nova aba.
   * Recebe o objeto do paciente para pré-preencher o formulário de orçamento.
   */
  onNewBudget?: (patient: any) => void;
  /** Incrementado pelo pai após salvar um orçamento (ex.: agenda) para recarregar KPIs/listas sem recarregar a página. */
  summaryReloadToken?: number;
  /** Consulta aberta na agenda — repasse de Consulta Médica vai para este profissional. */
  linkedAppointmentId?: string | null;
  linkedProfessionalId?: string | null;
  linkedProfessionalName?: string | null;
  /** Cobrança da consulta vinculada (ex.: sessão paga por ticket). */
  linkedAppointmentBilling?: AppointmentBillingLike | null;
  /** Observações da consulta aberta na agenda (exibidas no topo, fonte compacta). */
  linkedAppointmentNotes?: string | null;
  /** Perfil → Agenda (próximas ou recentes): abre edição (ex.: `openEditAppt` na agenda). */
  onEditUpcomingAppointment?: (appointment: any) => void;
  /** Após remarcar em lote (agenda): recarregar grade. */
  onPatientAppointmentsChanged?: () => void;
}

const BULK_WEEKDAY_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Segunda-feira' },
  { value: 2, label: 'Terça-feira' },
  { value: 3, label: 'Quarta-feira' },
  { value: 4, label: 'Quinta-feira' },
  { value: 5, label: 'Sexta-feira' },
  { value: 6, label: 'Sábado' },
];

/* ═══════════════════════════════════════════════════════════════
   COMPONENTE
   ═══════════════════════════════════════════════════════════════ */

const PatientDetailPanel: React.FC<PatientDetailPanelProps> = ({
  patientId,
  initialPatient,
  variant = 'full',
  initialTab = 'profile',
  onSendMediaToChat,
  onInboxSchedule,
  onInboxHealthMessage,
  onInboxUnlinkPatient,
  onRefresh,
  onClose,
  onPanelBack,
  panelBackLabel = 'Voltar',
  onEdit,
  onPatientUpdated,
  onNewBudget,
  summaryReloadToken = 0,
  linkedAppointmentId,
  linkedProfessionalId,
  linkedProfessionalName,
  linkedAppointmentBilling,
  linkedAppointmentNotes,
  onEditUpcomingAppointment,
  onPatientAppointmentsChanged,
}) => {
  const panelPatientId = useMemo(
    () => String(patientId || initialPatient?.id || '').trim(),
    [patientId, initialPatient?.id],
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const isProfessionalUser = user?.role === 'professional' && !!user?.professional_id;
  const { canAccess: showClinicalEvolutions } = useClinicalEvolutionsAccess();
  const evolutionViewer = { role: user?.role, professional_id: user?.professional_id };
  const canDeleteFinancial = canDeleteProtectedRecords(user?.role, user?.current_unit);
  const navigate = useNavigate();
  const { handleCashRegisterRequired, cashRegisterPromptUi } = useCashRegisterPrompt();
  const { promptAndEmit, nfseServiceCodeDialog } = useNfseServiceCodePrompt();
  const isInboxEmbedded = variant === 'inbox-embedded';
  const isAgendaEmbedded = variant === 'agenda-embedded' || isInboxEmbedded;
  const { plansEnabled } = usePlansEnabled();
  const { idocEnabled, loading: idocEnabledLoading } = useIdocEnabled();
  const [idocUnseen, setIdocUnseen] = useState(0);
  const { isFisioterapia, isOdontologia } = useClinicMedicalSpecialty();
  const showPaidSessionBadge = isAgendaEmbedded
    && isPaidSessionAppointment(linkedAppointmentBilling)
    && !isProfessionalUser;
  // ── Estado ──
  const [patient, setPatient] = useState<any>(initialPatient || null);
  const { connected: asaasConnected } = useAsaasStatus();
  const [asaasChargeReceivable, setAsaasChargeReceivable] = useState<any | null>(null);
  const [asaasPickOpen, setAsaasPickOpen] = useState(false);
  const [healthcareSummary, setHealthcareSummary] = useState<any>(null);
  const [patientEvolutions, setPatientEvolutions] = useState<any[] | null>(null);
  const [evolutionsLoading, setEvolutionsLoading] = useState(false);
  const [patientPrescriptions, setPatientPrescriptions] = useState<any[] | null>(null);
  const [prescriptionsLoading, setPrescriptionsLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(() => !initialPatient?.id);
  const [openingWhatsAppInbox, setOpeningWhatsAppInbox] = useState(false);
  const [fullSummaryLoading, setFullSummaryLoading] = useState(false);
  const summaryScopeRef = useRef<'none' | 'profile' | 'full'>('none');
  const [profileAppointmentHistoryOpen, setProfileAppointmentHistoryOpen] = useState(false);
  const [appointmentHistoryReloadKey, setAppointmentHistoryReloadKey] = useState(0);
  const [activeTab, setActiveTab] = useState<PatientPanelTabKey>(() => resolvePatientPanelTab(initialTab));
  const [odontogramSection, setOdontogramSection] = useState<PatientOdontogramSection>(() =>
    odontogramSectionFromInitialTab(initialTab),
  );
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  /**
   * Recolhe o cabeçalho ao rolar o conteúdo. Só recolhe quando sobra rolagem suficiente:
   * senão o ganho de altura zera o scroll, o cabeçalho expande de novo e a tela fica piscando.
   */
  const handleContentScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    const el = event.currentTarget;
    const top = el.scrollTop;
    const room = el.scrollHeight - el.clientHeight;
    setHeaderCollapsed((prev) => (prev ? top > 8 : top > 56 && room > 220));
  }, []);
  useEffect(() => {
    setHeaderCollapsed(false);
  }, [activeTab]);
  const [expandedHistoryRow, setExpandedHistoryRow] = useState<string | null>(null);
  const [expandedEvolutionIds, setExpandedEvolutionIds] = useState<string[]>([]);
  const expandEvolutionById = useCallback((id: string) => {
    const key = String(id);
    setExpandedEvolutionIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);
  const toggleEvolutionExpanded = useCallback((id: string) => {
    const key = String(id);
    setExpandedEvolutionIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  }, []);
  const [expandedMedicalReportId, setExpandedMedicalReportId] = useState<string | null>(null);
  const [signingEvolutionId, setSigningEvolutionId] = useState<string | null>(null);
  const [deletingEvolutionId, setDeletingEvolutionId] = useState<string | null>(null);
  const [printingEvolutionId, setPrintingEvolutionId] = useState<string | null>(null);
  const [exportingEvolutions, setExportingEvolutions] = useState(false);
  const [recordDeliveryOpen, setRecordDeliveryOpen] = useState(false);
  const [evolutionAdditionsTarget, setEvolutionAdditionsTarget] = useState<any>(null);
  const [invalidateTarget, setInvalidateTarget] = useState<any>(null);
  const [invalidatingEvolution, setInvalidatingEvolution] = useState(false);
  const [invalidateError, setInvalidateError] = useState<string | null>(null);
  const [snackMsg, setSnackMsg] = useState('');
  const [openingUpcomingId, setOpeningUpcomingId] = useState<string | null>(null);
  const [bulkRescheduleOpen, setBulkRescheduleOpen] = useState(false);
  const [bulkRescheduleTime, setBulkRescheduleTime] = useState('08:00');
  const [bulkRescheduleChangeTime, setBulkRescheduleChangeTime] = useState(true);
  const [bulkRescheduleChangeWeekday, setBulkRescheduleChangeWeekday] = useState(false);
  const [bulkRescheduleChangeProfessional, setBulkRescheduleChangeProfessional] = useState(false);
  const [bulkRescheduleWeekday, setBulkRescheduleWeekday] = useState(1);
  const [bulkRescheduleProfessionalId, setBulkRescheduleProfessionalId] = useState('');
  const [bulkRescheduleSaving, setBulkRescheduleSaving] = useState(false);

  const BULK_RESCHEDULE_TOOLTIP =
    'Aplica as mesmas alterações em todos os agendamentos futuros deste paciente: horário de início, dia da semana (cada consulta vai para esse dia na mesma semana) e/ou profissional.';

  // Documentos
  const [documents, setDocuments] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [docUploadForm, setDocUploadForm] = useState({ document_type: 'contract', description: '' });
  const [docUploadItems, setDocUploadItems] = useState<DocUploadItem[]>([]);
  const [docUploading, setDocUploading] = useState(false);
  const docFileInputRef = useRef<HTMLInputElement>(null);

  const resetDocUpload = () => {
    setShowDocUpload(false);
    setDocUploadItems([]);
    setDocUploadForm({ document_type: 'contract', description: '' });
    if (docFileInputRef.current) docFileInputRef.current.value = '';
  };

  const addDocFiles = (picked: File[]) => {
    if (!picked.length) return;
    setDocUploadItems((prev) => {
      const merged = [...prev];
      for (const file of picked) {
        const key = docUploadFileKey(file);
        if (!merged.some((item) => item.key === key)) {
          merged.push({ key, file, title: defaultDocUploadTitle(file) });
        }
      }
      return merged;
    });
  };

  const updateDocUploadTitle = (key: string, title: string) => {
    setDocUploadItems((prev) => prev.map((item) => (item.key === key ? { ...item, title } : item)));
  };

  const removeDocUploadItem = (key: string) => {
    setDocUploadItems((prev) => prev.filter((item) => item.key !== key));
  };

  // Imagens
  const [patientImages, setPatientImages] = useState<any[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [imageUploadForm, setImageUploadForm] = useState({ title: '', image_type: 'clinical_photo', description: '' });
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUploading, setImageUploading] = useState(false);

  const resetImageUpload = () => {
    setShowImageUpload(false);
    setImageFiles([]);
    setImageUploadForm({ title: '', image_type: 'clinical_photo', description: '' });
  };

  const addImageFiles = (picked: File[]) => {
    const imagesOnly = picked.filter((file) => file.type.startsWith('image/'));
    if (!imagesOnly.length) return;
    setImageFiles((prev) => {
      const merged = [...prev];
      for (const file of imagesOnly) {
        const exists = merged.some(
          (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified,
        );
        if (!exists) merged.push(file);
      }
      return merged;
    });
    if (imagesOnly.length === 1 && !imageUploadForm.title) {
      setImageUploadForm((p) => ({
        ...p,
        title: imagesOnly[0].name.replace(/\.[^/.]+$/, '') || imagesOnly[0].name,
      }));
    }
  };

  const addImageFilesRef = useRef(addImageFiles);
  addImageFilesRef.current = addImageFiles;
  const { openImageSourceChooser, imageSourcePickerNodes } = useClinicalImageAttachFlow({
    onFiles: (files) => {
      if (!files.length) return;
      addImageFilesRef.current(files);
      setShowImageUpload(true);
    },
  });

  // Healthcare Panel / Evolution
  const [evolutionDialogOpen, setEvolutionDialogOpen] = useState(false);
  const [evolutionExistingId, setEvolutionExistingId] = useState<string | null>(null);
  const [continueTodayEvolution, setContinueTodayEvolution] = useState<any | null>(null);
  const [medicalReportDialogOpen, setMedicalReportDialogOpen] = useState(false);
  const [medicalReportExistingId, setMedicalReportExistingId] = useState<string | null>(null);

  // Procedimentos
  const [patientProcedures, setPatientProcedures] = useState<{ pending: any[]; approved: any[]; completed: any[] }>({ pending: [], approved: [], completed: [] });
  const [patientDiagnoses, setPatientDiagnoses] = useState<{
    entries: Array<{
      evolution_id: string;
      evolution_date: string | null;
      evolution_time: string | null;
      diagnosis: string;
      diagnosis_code: string | null;
      professional_name: string | null;
      is_primary: boolean;
    }>;
    needs_primary_selection: boolean;
    primary_evolution_id: string | null;
  } | null>(null);
  const [diagnosesLoading, setDiagnosesLoading] = useState(false);
  const [settingPrimaryEvolutionId, setSettingPrimaryEvolutionId] = useState<string | null>(null);
  const [proceduresLoading, setProceduresLoading] = useState(false);

  // Financeiro — contas a receber do paciente
  const [receivables, setReceivables] = useState<any[]>([]);
  const [receivablesLoading, setReceivablesLoading] = useState(false);
  const [paidSessionsReloadToken, setPaidSessionsReloadToken] = useState(0);
  const [pendingApprovalBudgets, setPendingApprovalBudgets] = useState<any[]>([]);
  const [budgetDecisionId, setBudgetDecisionId] = useState<string | null>(null);
  const {
    budgetDetailsByAccountId,
    budgetLoadingAccountId,
    preloadBudgetDetailsForAccounts,
    loadBudgetDetailForAccount,
    isBudgetTransaction,
  } = useBudgetReceivableExpand();

  const todayStr = () => todayYmdBr();

  // Dialog: registrar recebimento de uma conta existente
  const { options: receivablePaymentOptions } = useReceivablePaymentMethods(true);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receivingReceivable, setReceivingReceivable] = useState(false);
  const [activeReceivable, setActiveReceivable] = useState<any | null>(null);
  const [receiveForm, setReceiveForm] = useState({
    amount: '',
    payment_method: 'dinheiro',
    card_installments: '1',
    payment_date: todayStr(),
    notes: '',
    execute_session_today: false,
    session_professional_id: '',
    session_budget_item_ids: [] as string[],
    card_brand: '',
  });
  const [receiveMultiSessionItems, setReceiveMultiSessionItems] = useState<BudgetItemRow[]>([]);
  const [receiveBudgetId, setReceiveBudgetId] = useState<string | null>(null);
  const receiveIsCard = isCardInstallmentPaymentMethod(receiveForm.payment_method);
  const receiveEnabledCardBrands = useEnabledCardBrands(receiveDialogOpen);
  const receiveNeedsBrand =
    (receiveIsCard || isDebitCardPaymentMethod(receiveForm.payment_method))
    && receiveEnabledCardBrands.length > 0;
  const receiveRemainingDue = activeReceivable
    ? Math.max(
      0,
      (Number(activeReceivable.amount) || 0) - (Number(activeReceivable.amount_paid || activeReceivable.paid_amount) || 0),
    )
    : 0;
  const receiveWillCompleteBudget =
    receiveRemainingDue > 0
    && (parseFloat(receiveForm.amount) || 0) >= receiveRemainingDue - 0.009;
  const [printingReceiptId, setPrintingReceiptId] = useState<string | null>(null);
  const [nfseEmittingId, setNfseEmittingId] = useState<string | null>(null);
  const [nfseByReceivableId, setNfseByReceivableId] = useState<Record<string, any>>({});

  // Executar procedimento
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [executingItem, setExecutingItem] = useState<any>(null);
  const [executeForm, setExecuteForm] = useState({
    professional_id: '',
    referring_professional_id: '',
    notes: '',
    date_mode: 'today' as 'today' | 'custom',
    executed_date: todayLocalISODate(),
  });
  const [executeSaving, setExecuteSaving] = useState(false);
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [editingPrescriptionId, setEditingPrescriptionId] = useState<string | null>(null);
  const [rxPrintDialogOpen, setRxPrintDialogOpen] = useState(false);
  const [rxPrintQueue, setRxPrintQueue] = useState<PrescriptionPrintQueueItem[]>([]);
  const [rxPrintFirstViaOnly, setRxPrintFirstViaOnly] = useState(false);
  const [signPrescriptionOpen, setSignPrescriptionOpen] = useState(false);
  const [signPrescriptionTarget, setSignPrescriptionTarget] = useState<any>(null);
  const [signPrescriptionBatch, setSignPrescriptionBatch] = useState<any[] | null>(null);
  const [selectedRxRows, setSelectedRxRows] = useState<Record<string, any>>({});
  const [rxBundleSendIds, setRxBundleSendIds] = useState<string[] | null>(null);
  const [sellPlanDialogOpen, setSellPlanDialogOpen] = useState(false);

  const refreshActivePlans = useCallback(() => {
    if (!patientId || !plansEnabled) return;
    axios
      .get(getApiUrl(`/clinic/patients/${patientId}/active-plans`))
      .then((res) => {
        setPatient((prev: any) => ({ ...(prev || {}), active_plans: res.data?.data || [] }));
      })
      .catch(() => { /* sem planos: a tarja simplesmente não aparece */ });
  }, [patientId, plansEnabled]);

  // Planos vigentes: o paciente pode chegar sem essa informação (ex.: painel aberto
  // a partir de um contexto que não enriquece), então buscamos direto da API.
  useEffect(() => {
    refreshActivePlans();
  }, [refreshActivePlans]);

  const loadPatientDiagnoses = useCallback(async (opts?: { silent?: boolean }) => {
    if (!patientId) return;
    if (!opts?.silent) setDiagnosesLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/clinic/healthcare-panel/patient/${patientId}/diagnoses`));
      if (res.data?.success) setPatientDiagnoses(res.data.data);
    } catch {
      if (!opts?.silent) setPatientDiagnoses(null);
    } finally {
      if (!opts?.silent) setDiagnosesLoading(false);
    }
  }, [patientId]);

  const loadPatientEvolutions = useCallback(async (opts?: { silent?: boolean }) => {
    if (!patientId || !showClinicalEvolutions) return;
    const silent = Boolean(opts?.silent);
    if (!silent) setEvolutionsLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/clinic/healthcare-panel/patient/${patientId}/evolutions`));
      if (res.data?.success) {
        setPatientEvolutions(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (err) {
      console.error('Error fetching patient evolutions:', err);
      if (!silent) setPatientEvolutions([]);
    } finally {
      if (!silent) setEvolutionsLoading(false);
    }
  }, [patientId, showClinicalEvolutions]);

  const timelineEvolutions = useMemo(
    () => patientEvolutions ?? healthcareSummary?.recent_evolutions ?? [],
    [patientEvolutions, healthcareSummary?.recent_evolutions],
  );

  const loadPatientPrescriptions = useCallback(async (opts?: { silent?: boolean }) => {
    if (!patientId || isFisioterapia) return;
    const silent = Boolean(opts?.silent);
    if (!silent) setPrescriptionsLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/clinic/healthcare-panel/patient/${patientId}/prescriptions`));
      if (res.data?.success) {
        setPatientPrescriptions(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (err) {
      console.error('Error fetching patient prescriptions:', err);
      if (!silent) setPatientPrescriptions([]);
    } finally {
      if (!silent) setPrescriptionsLoading(false);
    }
  }, [patientId, isFisioterapia]);

  const historyPrescriptions = useMemo(
    () => patientPrescriptions ?? healthcareSummary?.recent_prescriptions ?? [],
    [patientPrescriptions, healthcareSummary?.recent_prescriptions],
  );

  const isSelectableRx = (p: any) => p?.status === 'active' && p?.origin !== 'memed';
  const selectedRxList = useMemo(
    () => historyPrescriptions.filter((p: any) => selectedRxRows[p.id]),
    [historyPrescriptions, selectedRxRows],
  );
  const selectedRxPendingSignature = selectedRxList.filter((p: any) => !isSignedPrescription(p));

  const toggleRxSelection = (p: any) => {
    setSelectedRxRows((prev) => {
      const next = { ...prev };
      if (next[p.id]) delete next[p.id];
      else next[p.id] = true;
      return next;
    });
  };

  /** Uma autorização assina todas; o certificado precisa ser do prescritor de cada uma. */
  const openRxBatchSign = (rows: any[]) => {
    const pending = rows.filter((p) => p?.id && isSelectableRx(p) && !isSignedPrescription(p));
    if (pending.length === 0) {
      setSnackMsg('Nenhuma das receitas selecionadas está pendente de assinatura.');
      return;
    }
    if (pending.some((p) => !p.professional_id)) {
      setSnackMsg('Defina o profissional prescritor em todas as receitas antes de assinar.');
      return;
    }
    if (new Set(pending.map((p) => p.professional_id)).size > 1) {
      setSnackMsg('Assine juntas apenas receitas do mesmo prescritor.');
      return;
    }
    if (pending.length === 1) {
      setSignPrescriptionBatch(null);
      setSignPrescriptionTarget(pending[0]);
    } else {
      setSignPrescriptionTarget(null);
      setSignPrescriptionBatch(pending);
    }
    setSignPrescriptionOpen(true);
  };

  const closeRxSignDialog = () => {
    setSignPrescriptionOpen(false);
    setSignPrescriptionTarget(null);
    setSignPrescriptionBatch(null);
  };

  const fetchSummary = useCallback(async (opts?: { silent?: boolean; scope?: 'profile' | 'full' }) => {
    const id = panelPatientId;
    if (!id) return;
    const silent = Boolean(opts?.silent);
    const scope = opts?.scope ?? (summaryScopeRef.current === 'full' ? 'full' : 'profile');
    if (!silent && !healthcareSummary && !patient) setSummaryLoading(true);
    if (scope === 'full') setFullSummaryLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/clinic/healthcare-panel/patient/${id}/summary`), {
        params: { scope },
      });
      if (res.data.success) {
        setHealthcareSummary(res.data.data);
        setAppointmentHistoryReloadKey((k) => k + 1);
        summaryScopeRef.current =
          res.data.data?.summary_scope === 'full' ? 'full' : scope === 'full' ? 'full' : 'profile';
        if (res.data.data?.patient) {
          setPatient((prev: any) => ({ ...prev, ...res.data.data.patient }));
        }
      }
    } catch (err) {
      console.error('Error fetching patient summary:', err);
    } finally {
      if (!silent) setSummaryLoading(false);
      if (scope === 'full') setFullSummaryLoading(false);
    }
  }, [panelPatientId]);

  const openUpcomingAppointment = useCallback(async (apt: { id?: string }) => {
    if (!apt?.id) return;
    setOpeningUpcomingId(apt.id);
    try {
      let full: any = apt;
      try {
        const res = await axios.get(getApiUrl(`/clinic/appointments/${apt.id}`));
        full = res.data?.data ?? res.data ?? apt;
      } catch {
        full = apt;
      }
      const enriched = {
        ...full,
        patient_id: full.patient_id || patientId,
        clinic_patients: full.clinic_patients ?? patient,
        clinic_professionals: full.clinic_professionals ?? (apt as any).clinic_professionals,
      };
      if (onEditUpcomingAppointment) {
        onEditUpcomingAppointment(enriched);
        return;
      }
      navigate('/agenda', { state: { editAppointmentId: apt.id } });
    } finally {
      setOpeningUpcomingId(null);
    }
  }, [navigate, onEditUpcomingAppointment, patient, patientId]);

  const upcomingForBulkReschedule = (healthcareSummary?.upcoming_appointments || []).filter(
    (a: any) => String(a?.appointment_type || '').toLowerCase() !== 'commitment',
  );

  const bulkRescheduleWeekendHits = useMemo(() => {
    if (!bulkRescheduleOpen || !bulkRescheduleChangeWeekday) return [];
    const dates = upcomingForBulkReschedule.map((a: any) =>
      shiftDateToWeekday(String(a.appointment_date), bulkRescheduleWeekday),
    );
    return detectWeekendSessionHits(dates);
  }, [bulkRescheduleOpen, bulkRescheduleChangeWeekday, bulkRescheduleWeekday, upcomingForBulkReschedule]);

  const bulkRescheduleCanSubmit =
    (bulkRescheduleChangeTime && bulkRescheduleTime) ||
    bulkRescheduleChangeWeekday ||
    (bulkRescheduleChangeProfessional && bulkRescheduleProfessionalId);

  const buildBulkRescheduleBody = (overrideAvailability: boolean) => {
    const body: Record<string, unknown> = { override_availability: overrideAvailability };
    if (bulkRescheduleChangeTime && bulkRescheduleTime) body.start_time = bulkRescheduleTime;
    if (bulkRescheduleChangeWeekday) body.target_weekday = bulkRescheduleWeekday;
    if (bulkRescheduleChangeProfessional) body.professional_id = bulkRescheduleProfessionalId || null;
    return body;
  };

  const openBulkRescheduleDialog = () => {
    const first = upcomingForBulkReschedule[0];
    const t = first?.start_time ? String(first.start_time).substring(0, 5) : '08:00';
    setBulkRescheduleTime(t);
    const dow = first?.appointment_date
      ? ymdToLocalDate(String(first.appointment_date)).getDay()
      : 1;
    setBulkRescheduleWeekday(dow);
    const profId = first?.professional_id || first?.clinic_professionals?.id || '';
    setBulkRescheduleProfessionalId(String(profId || ''));
    setBulkRescheduleChangeTime(true);
    setBulkRescheduleChangeWeekday(false);
    setBulkRescheduleChangeProfessional(false);
    if (professionals.length === 0) void loadProfessionals();
    setBulkRescheduleOpen(true);
  };

  const applyBulkRescheduleResult = async (res: any) => {
    const stats = res?.data || {};
    const updated = Number(stats.updated) || 0;
    const skipped = Number(stats.skipped) || 0;
    const failed = Number(stats.failed) || 0;
    const parts: string[] = [];
    if (bulkRescheduleChangeTime && bulkRescheduleTime) parts.push(`horário ${bulkRescheduleTime}`);
    if (bulkRescheduleChangeWeekday) {
      const wd = BULK_WEEKDAY_OPTIONS.find((w) => w.value === bulkRescheduleWeekday)?.label;
      parts.push(`dia ${wd || bulkRescheduleWeekday}`);
    }
    if (bulkRescheduleChangeProfessional) {
      const prof = professionals.find((p: any) => p.id === bulkRescheduleProfessionalId);
      parts.push(prof?.full_name ? `profissional ${prof.full_name}` : 'profissional');
    }
    const detail = parts.length ? ` (${parts.join(', ')})` : '';
    let msg = `${updated} consulta(s) atualizada(s)${detail}.`;
    if (skipped > 0) msg += ` ${skipped} já estavam como definido.`;
    if (failed > 0) msg += ` ${failed} não puderam ser alteradas.`;
    setSnackMsg(msg);
    setBulkRescheduleOpen(false);
    await fetchSummary({ silent: true });
    onPatientAppointmentsChanged?.();
  };

  const runBulkReschedule = async (overrideAvailability: boolean) => {
    const id = panelPatientId || String(patient?.id || '').trim();
    if (!id) {
      setSnackMsg('Não foi possível identificar o paciente desta consulta. Volte ao agendamento e selecione o paciente.');
      return;
    }
    if (!bulkRescheduleCanSubmit) return;
    setBulkRescheduleSaving(true);
    const body = buildBulkRescheduleBody(overrideAvailability);
    try {
      const { data: res } = await axios.post(
        getApiUrl(`/clinic/appointments/patient/${id}/reschedule-future-time`),
        body,
      );
      await applyBulkRescheduleResult(res);
    } catch (err: any) {
      if (err?.response?.status === 422 && err?.response?.data?.code === 'UNAVAILABLE') {
        const msg = err.response.data.error || 'Horário fora da grade do profissional.';
        if (window.confirm(`${msg}\n\nDeseja remarcar mesmo assim (encaixe)?`)) {
          try {
            const { data: res2 } = await axios.post(
              getApiUrl(`/clinic/appointments/patient/${id}/reschedule-future-time`),
              buildBulkRescheduleBody(true),
            );
            await applyBulkRescheduleResult(res2);
          } catch (e2: any) {
            setSnackMsg(e2?.response?.data?.error || 'Não foi possível remarcar em lote.');
          }
        }
      } else {
        setSnackMsg(err?.response?.data?.error || 'Não foi possível remarcar em lote.');
      }
    } finally {
      setBulkRescheduleSaving(false);
    }
  };

  const handleSetPrimaryDiagnosis = async (evolutionId: string) => {
    if (!patientId) return;
    setSettingPrimaryEvolutionId(evolutionId);
    try {
      const res = await axios.put(
        getApiUrl(`/clinic/healthcare-panel/patient/${patientId}/primary-diagnosis`),
        { evolution_id: evolutionId },
      );
      if (res.data?.success && res.data.data) {
        setPatient((prev: any) => ({ ...prev, ...res.data.data }));
      }
      await loadPatientDiagnoses();
      setSnackMsg('Diagnóstico principal atualizado.');
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Não foi possível definir o diagnóstico principal.');
    } finally {
      setSettingPrimaryEvolutionId(null);
    }
  };

  const handleSignEvolution = async (evoId: string) => {
    if (!window.confirm('Deseja assinar esta evolução? Administradores poderão excluí-la por até 24 horas após a assinatura.')) return;
    try {
      setSigningEvolutionId(evoId);
      await axios.patch(getApiUrl(`/clinic/evolutions/${evoId}/sign`));
      const signedAt = new Date().toISOString();
      setHealthcareSummary((prev: any) => {
        if (!prev?.recent_evolutions) return prev;
        return {
          ...prev,
          recent_evolutions: prev.recent_evolutions.map((e: any) =>
            String(e.id) === evoId ? { ...e, is_signed: true, status: 'signed', signed_at: signedAt } : e,
          ),
        };
      });
      setSnackMsg('Evolução assinada com sucesso!');
      void fetchSummary();
      void loadPatientEvolutions({ silent: true });
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Erro ao assinar evolução');
    } finally {
      setSigningEvolutionId(null);
    }
  };

  const handlePrintEvolutionText = async (evo: any) => {
    const evoId = String(evo?.id || '');
    if (!evoId) return;
    setPrintingEvolutionId(evoId);
    try {
      const { data: res } = await axios.get(getApiUrl(`/clinic/evolutions/${evoId}`));
      const loaded = res?.data ?? res;
      const patientCtx = healthcareSummary?.patient || patient;
      await printClinicalEvolutionTextOnlyDocument({
        ...evo,
        ...loaded,
        patient: loaded?.patient || patientCtx,
      });
    } catch (err: any) {
      setSnackMsg(err?.response?.data?.error || 'Não foi possível imprimir a evolução.');
    } finally {
      setPrintingEvolutionId(null);
    }
  };

  const handleExportPatientEvolutions = async () => {
    if (!timelineEvolutions.length) return;
    setExportingEvolutions(true);
    try {
      const patientCtx = healthcareSummary?.patient || patient;
      const loaded = await Promise.all(
        timelineEvolutions.map(async (evo: any) => {
          const evoId = String(evo?.id || '');
          if (!evoId) return { ...evo, patient: patientCtx };
          try {
            const { data: res } = await axios.get(getApiUrl(`/clinic/evolutions/${evoId}`));
            const detail = res?.data ?? res;
            return { ...evo, ...detail, patient: detail?.patient || patientCtx };
          } catch {
            return { ...evo, patient: patientCtx };
          }
        }),
      );
      await printPatientClinicalEvolutionsExport(patientCtx, loaded);
    } catch (err: any) {
      setSnackMsg(err?.response?.data?.error || 'Não foi possível exportar as evoluções.');
    } finally {
      setExportingEvolutions(false);
    }
  };

  const handleDeleteEvolution = async (evoId: string) => {
    if (!window.confirm('Deseja excluir esta evolução? Esta ação não pode ser desfeita.')) return;
    try {
      setDeletingEvolutionId(evoId);
      await axios.delete(getApiUrl(`/clinic/evolutions/${evoId}`));
      setHealthcareSummary((prev: any) => {
        if (!prev?.recent_evolutions) return prev;
        return {
          ...prev,
          recent_evolutions: prev.recent_evolutions.filter((e: any) => String(e.id) !== evoId),
        };
      });
      setExpandedEvolutionIds((prev) => prev.filter((id) => id !== evoId));
      setPatientEvolutions((prev) => (prev ? prev.filter((e: any) => String(e.id) !== evoId) : prev));
      setSnackMsg('Evolução excluída.');
      void fetchSummary();
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Erro ao excluir evolução');
    } finally {
      setDeletingEvolutionId(null);
    }
  };

  const handleConfirmInvalidateEvolution = async (reason: string) => {
    const id = invalidateTarget?.id;
    if (!id) return;
    try {
      setInvalidatingEvolution(true);
      setInvalidateError(null);
      const { data: res } = await axios.patch(getApiUrl(`/clinic/evolutions/${id}/invalidate`), {
        invalidation_reason: reason,
      });
      const updated = res?.data ?? res;
      setHealthcareSummary((prev: any) => {
        if (!prev?.recent_evolutions) return prev;
        return {
          ...prev,
          recent_evolutions: prev.recent_evolutions.map((e: any) =>
            String(e.id) === String(id) ? { ...e, ...updated, status: 'cancelled' } : e,
          ),
        };
      });
      setInvalidateTarget(null);
      setSnackMsg('Evolução invalidada.');
      void fetchSummary();
      void loadPatientEvolutions({ silent: true });
    } catch (err: any) {
      setInvalidateError(err.response?.data?.error || 'Erro ao invalidar evolução');
    } finally {
      setInvalidatingEvolution(false);
    }
  };

  const loadDocuments = useCallback(async () => {
    if (!patientId) return;
    setDocsLoading(true);
    try {
      const { data: res } = await axios.get(getApiUrl(`/clinic/patients/${patientId}/documents`));
      if (res.success) setDocuments(res.data || []);
    } catch (err) { console.error('Error loading documents:', err); }
    finally { setDocsLoading(false); }
  }, [patientId]);

  const loadPatientImages = useCallback(async () => {
    if (!patientId) return;
    setImagesLoading(true);
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/patient-images'), { params: { patient_id: patientId } });
      if (res.success) setPatientImages(res.data || []);
    } catch (err) { console.error('Error loading images:', err); }
    finally { setImagesLoading(false); }
  }, [patientId]);

  const loadPatientProcedures = useCallback(async () => {
    if (!patientId) return;
    setProceduresLoading(true);
    try {
      const { data: body } = await axios.get(getApiUrl(`/clinic/patients/${patientId}/procedures`));
      const payload = body?.data ?? body;
      if (payload && typeof payload === 'object' && Array.isArray((payload as any).pending)) {
        setPatientProcedures({
          pending: (payload as any).pending || [],
          approved: (payload as any).approved || [],
          completed: (payload as any).completed || [],
        });
      } else {
        setPatientProcedures({ pending: [], approved: [], completed: [] });
      }
    } catch { setPatientProcedures({ pending: [], approved: [], completed: [] }); }
    finally { setProceduresLoading(false); }
  }, [patientId]);

  const loadProfessionals = useCallback(async () => {
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/professionals'), { params: { is_active: true, limit: 200 } });
      setProfessionals(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const syncNfseForReceivables = useCallback(async (items: any[]) => {
    const paid = items.filter((r) => isReceivablePaidStatus(r.status));
    const map: Record<string, any> = {};
    await Promise.all(
      paid.map(async (r) => {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/nfse/by-transaction/${r.id}`));
          if (data?.data?.id) map[r.id] = data.data;
        } catch {
          /* sem NFSe vinculada */
        }
      }),
    );
    setNfseByReceivableId(map);
  }, []);

  const refreshNfseForReceivable = useCallback(async (receivableId: string) => {
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/nfse/by-transaction/${receivableId}`));
      if (data?.data?.id) {
        setNfseByReceivableId((prev) => ({ ...prev, [receivableId]: data.data }));
      } else {
        setNfseByReceivableId((prev) => {
          const next = { ...prev };
          delete next[receivableId];
          return next;
        });
      }
    } catch {
      setNfseByReceivableId((prev) => {
        const next = { ...prev };
        delete next[receivableId];
        return next;
      });
    }
  }, []);

  /* ── Contas a receber do paciente ── */
  const loadReceivables = useCallback(async () => {
    if (!patientId) return;
    setReceivablesLoading(true);
    void axios
      .get(getApiUrl('/clinic/budgets'), {
        params: { patient_id: patientId, status_group: 'pending', limit: 100 },
      })
      .then(({ data }) => setPendingApprovalBudgets(Array.isArray(data?.data) ? data.data : []))
      .catch(() => setPendingApprovalBudgets([]));
    try {
      const { data: res } = await axios.get(
        getApiUrl('/financial-module/accounts-receivable'),
        { params: { patient_id: patientId, limit: 100 } },
      );
      const items = res?.data || [];
      setReceivables(items);
      void syncNfseForReceivables(items);
    } catch (err) {
      console.error('Error loading receivables:', err);
      setReceivables([]);
      setNfseByReceivableId({});
    } finally {
      setReceivablesLoading(false);
    }
  }, [patientId, syncNfseForReceivables]);

  useEffect(() => {
    if (receivables.length > 0) {
      void preloadBudgetDetailsForAccounts(receivables);
    }
  }, [receivables, preloadBudgetDetailsForAccounts]);

  const decideBudget = async (budget: any, decision: 'approved' | 'rejected', notes?: string) => {
    if (!budget?.id) return;
    setBudgetDecisionId(budget.id);
    try {
      const res = await axios.patch(getApiUrl(`/clinic/budgets/${budget.id}/status`), { status: decision, notes });
      if (decision === 'approved') {
        const created = Number(res.data?.data?._auto?.receivables_created) || 0;
        setSnackMsg(
          created > 0
            ? `Orçamento ${budget.budget_number || ''} aprovado — ${created} conta(s) a receber gerada(s).`
            : `Orçamento ${budget.budget_number || ''} aprovado.`,
        );
      } else {
        setSnackMsg(`Orçamento ${budget.budget_number || ''} rejeitado.`);
      }
      dispatchBudgetUpdated({ budgetId: budget.id, patientId: patientId || null });
    } catch (err: any) {
      if (handleCashRegisterRequired(err, () => { void decideBudget(budget, decision, notes); })) return;
      setSnackMsg(err?.response?.data?.error || 'Não foi possível atualizar o orçamento.');
    } finally {
      setBudgetDecisionId(null);
    }
  };

  const openNewBudget = () => {
    const target = patient ?? { id: patientId };
    if (onNewBudget) {
      onNewBudget(target);
      return;
    }
    window.open(`/clinic/budgets?patientId=${encodeURIComponent(patientId)}`, '_blank');
  };

  const openReceiveDialog = async (rec: any) => {
    const remaining = Math.max(0, (Number(rec.amount) || 0) - (Number(rec.amount_paid || rec.paid_amount) || 0));
    let multiSession: BudgetItemRow[] = [];
    let resolvedBudgetId: string | null = rec.budget_id || null;
    if (isBudgetTransaction(rec)) {
      const budgetDetail = await loadBudgetDetailForAccount(rec);
      resolvedBudgetId = budgetDetail?.id || rec.budget_id || null;
      multiSession = getMultiSessionBudgetItems(budgetDetail);
    }
    setReceiveBudgetId(resolvedBudgetId);
    const multiIds = multiSession.map((item) => String(item.id || '')).filter(Boolean);
    const defaultProfessional = user?.professional_id || linkedProfessionalId || '';
    if (multiSession.length > 0 && professionals.length === 0) {
      void loadProfessionals();
    }
    setReceiveMultiSessionItems(multiSession);
    setActiveReceivable(rec);
    setReceiveForm({
      amount: (remaining || rec.amount || 0).toString(),
      payment_method: rec.payment_method || 'dinheiro',
      card_installments: String(parseCardInstallments(rec.installments)),
      payment_date: todayStr(),
      notes: '',
      execute_session_today: false,
      session_professional_id: defaultProfessional,
      session_budget_item_ids: multiIds,
      card_brand: '',
    });
    setReceiveDialogOpen(true);
  };

  const submitReceive = async () => {
    if (!activeReceivable) return;
    const amount = parseFloat(receiveForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) { setSnackMsg('Valor inválido'); return; }
    if (receiveNeedsBrand && !receiveForm.card_brand) { setSnackMsg('Selecione a bandeira do cartão'); return; }
    const totalDue = Number(activeReceivable.amount) || 0;
    const alreadyPaid = Number(activeReceivable.amount_paid || activeReceivable.paid_amount) || 0;
    const remaining = Math.max(0, totalDue - alreadyPaid);
    const willCompleteBudget = amount >= remaining - 0.009;
    if (
      receiveForm.execute_session_today
      && willCompleteBudget
      && receiveMultiSessionItems.length > 0
      && !receiveForm.session_professional_id
    ) {
      setSnackMsg('Selecione o profissional que realizou a sessão hoje');
      return;
    }
    if (
      receiveForm.execute_session_today
      && willCompleteBudget
      && receiveMultiSessionItems.length > 1
      && receiveForm.session_budget_item_ids.length === 0
    ) {
      setSnackMsg('Selecione ao menos um procedimento para registrar a sessão de hoje');
      return;
    }
    setReceivingReceivable(true);
    try {
      const newTotalPaid = alreadyPaid + amount;
      const newStatus = newTotalPaid >= totalDue ? 'paid' : 'partial';
      const shouldConsumeSession =
        receiveForm.execute_session_today
        && willCompleteBudget
        && receiveMultiSessionItems.length > 0;
      const { data: res } = await axios.put(
        getApiUrl(`/financial-module/accounts-receivable/${activeReceivable.id}`),
        {
          status: newStatus,
          amount_paid: newTotalPaid,
          payment_date: receiveForm.payment_date,
          payment_method: receiveForm.payment_method || undefined,
          installments: receiveIsCard ? parseCardInstallments(receiveForm.card_installments) : 1,
          ...(receiveNeedsBrand ? { _card_brand: receiveForm.card_brand || DEFAULT_CARD_BRAND } : {}),
          payment_notes: receiveForm.notes || undefined,
          consume_walk_in_sessions_today: shouldConsumeSession,
          walk_in_budget_id: shouldConsumeSession ? (receiveBudgetId || activeReceivable.budget_id || undefined) : undefined,
          walk_in_session_professional_id: shouldConsumeSession ? receiveForm.session_professional_id : undefined,
          walk_in_session_budget_item_ids: shouldConsumeSession ? receiveForm.session_budget_item_ids : undefined,
          _user_id: user?.id,
          _user_name: user?.full_name,
        },
      );
      let msg = newStatus === 'paid'
        ? 'Recebimento total registrado — lançado no seu caixa'
        : `Recebimento parcial (${formatCurrency(amount)}) registrado — saldo pendente: ${formatCurrency(Math.max(0, totalDue - newTotalPaid))}`;
      const walkIn = res?.walk_in_sessions as Array<{ procedure_name?: string; error?: string }> | null;
      if (shouldConsumeSession && walkIn?.length) {
        const ok = walkIn.filter((row) => row.procedure_name && !row.error);
        const failed = walkIn.filter((row) => row.error);
        if (ok.length > 0) {
          const names = ok.map((row) => row.procedure_name).join(', ');
          msg += ` · 1ª sessão registrada hoje (${names}) — próximo agendamento será da 2ª sessão`;
        }
        if (failed.length > 0) {
          msg += ` · Aviso: ${failed[0].error}`;
        }
      }
      setSnackMsg(msg);
      setReceiveDialogOpen(false);
      setActiveReceivable(null);
      setReceiveMultiSessionItems([]);
      setReceiveBudgetId(null);
      void loadReceivables();
      void fetchSummary();
      if (shouldConsumeSession) {
        void loadPatientProcedures();
      }
    } catch (err: any) {
      if (handleCashRegisterRequired(err, submitReceive)) {
        setReceivingReceivable(false);
        return;
      }
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao registrar recebimento';
      setSnackMsg(msg);
    } finally {
      setReceivingReceivable(false);
    }
  };

  const deleteReceivable = async (rec: any) => {
    if (!rec?.id) return;
    if (!window.confirm('Excluir esta conta a receber?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/accounts-receivable/${rec.id}`));
      setSnackMsg('Conta a receber excluída');
      void loadReceivables();
      void fetchSummary();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao excluir';
      setSnackMsg(msg);
    }
  };

  const printReceivableReceipt = async (rec: any) => {
    if (!rec?.id) return;
    setPrintingReceiptId(rec.id);
    try {
      await printClinicReceiptFromTransaction(rec.id);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Erro ao gerar recibo';
      setSnackMsg(msg);
    } finally {
      setPrintingReceiptId(null);
    }
  };

  const emitReceivableNfse = async (rec: any) => {
    if (!rec?.id) return;
    if (!canEmitNfseForReceivable(rec)) {
      setSnackMsg('Registre o recebimento antes de emitir a nota fiscal.');
      return;
    }
    setNfseEmittingId(rec.id);
    try {
      await promptAndEmit({ transactionId: rec.id }, async (choice) => {
        try {
          const { data } = await axios.post(getApiUrl(`/clinic/nfse/from-transaction/${rec.id}`), choice);
          if (data?.data?.id) {
            setNfseByReceivableId((prev) => ({ ...prev, [rec.id]: data.data }));
          } else {
            await refreshNfseForReceivable(rec.id);
          }
          setSnackMsg('NFSe enviada para a Focus. Use o menu ⋯ para detalhes, PDF e envio ao paciente.');
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.message || 'Erro ao emitir NFSe';
          setSnackMsg(msg);
        }
      });
    } finally {
      setNfseEmittingId(null);
    }
  };

  // ── Effects ──

  const loadedPanelPatientRef = useRef<string | null>(null);

  /** Só zera o painel ao trocar de paciente — não quando a agenda atualiza o mesmo paciente. */
  useEffect(() => {
    const id = panelPatientId;
    if (!id) return;
    const patientChanged = loadedPanelPatientRef.current !== id;
    loadedPanelPatientRef.current = id;
    if (!patientChanged) return;

    setPatient(initialPatient || null);
    setHealthcareSummary(null);
    summaryScopeRef.current = 'none';
    setProfileAppointmentHistoryOpen(false);
    setAppointmentHistoryReloadKey((k) => k + 1);
    setPatientEvolutions(null);
    setPatientPrescriptions(null);
    setActiveTab(resolvePatientPanelTab(initialTab));
    setOdontogramSection(odontogramSectionFromInitialTab(initialTab));
    setExpandedHistoryRow(null);
    setExpandedEvolutionIds([]);
    setExpandedMedicalReportId(null);
    setSummaryLoading(!initialPatient?.id);
    void fetchSummary({ scope: 'profile' });
  }, [panelPatientId, initialTab, fetchSummary, initialPatient]);

  useEffect(() => {
    if (!panelPatientId || !initialPatient?.id || initialPatient.id !== panelPatientId) return;
    setPatient((prev: any) => {
      if (!prev || prev.id !== initialPatient.id) return initialPatient;
      return { ...prev, ...initialPatient };
    });
  }, [panelPatientId, initialPatient]);

  // O inbox reabre o painel já numa aba (ex.: "Ver agenda") sem trocar de paciente.
  useEffect(() => {
    setActiveTab(resolvePatientPanelTab(initialTab));
    setOdontogramSection(odontogramSectionFromInitialTab(initialTab));
  }, [initialTab]);

  useEffect(() => {
    if (!patientId || summaryReloadToken < 1) return;
    void fetchSummary({
      silent: true,
      scope: summaryScopeRef.current === 'full' ? 'full' : 'profile',
    });
    void loadReceivables();
  }, [summaryReloadToken, patientId, fetchSummary, loadReceivables]);

  useEffect(() => {
    if (!patientId) return;
    return onBudgetUpdated((detail) => {
      if (detail.patientId && detail.patientId !== patientId) return;
      void fetchSummary();
      void loadReceivables();
    });
  }, [patientId, fetchSummary, loadReceivables]);

  const tabsNeedingFullSummary: PatientPanelTabKey[] = useMemo(
    () => ['history', 'financial', 'evolutions', 'medical_reports', 'docs', 'procedures', 'images'],
    [],
  );

  useEffect(() => {
    if (!panelPatientId) return;
    if (summaryScopeRef.current === 'full') return;
    if (!tabsNeedingFullSummary.includes(activeTab)) return;
    void fetchSummary({ silent: true, scope: 'full' });
  }, [activeTab, panelPatientId, fetchSummary, tabsNeedingFullSummary]);

  useEffect(() => {
    if (activeTab === 'financial') loadReceivables();
    if (activeTab === 'docs') loadDocuments();
    if (activeTab === 'images') loadPatientImages();
    if (activeTab === 'procedures') loadPatientProcedures();
    if (activeTab === 'diagnoses' && showClinicalEvolutions) loadPatientDiagnoses();
    if (activeTab === 'evolutions' && showClinicalEvolutions) void loadPatientEvolutions();
    if (activeTab === 'history' && !isFisioterapia) void loadPatientPrescriptions();
  }, [activeTab, showClinicalEvolutions, isFisioterapia, loadReceivables, loadDocuments, loadPatientImages, loadPatientProcedures, loadPatientDiagnoses, loadPatientEvolutions, loadPatientPrescriptions]);

  const refreshAfterMemedPrescription = useCallback(() => {
    void loadPatientPrescriptions({ silent: true });
    void fetchSummary({ silent: true });
    setSnackMsg('Receita Memed salva no prontuário.');
  }, [loadPatientPrescriptions, fetchSummary]);

  const memedPrescription = useMemed({
    professionalId: linkedProfessionalId || user?.professional_id,
    onPrescriptionSaved: () => refreshAfterMemedPrescription(),
  });

  useEffect(() => {
    const profId = linkedProfessionalId || user?.professional_id;
    if (!memedPrescription.available || !profId) return;
    void memedPrescription.warmUp(profId);
  }, [memedPrescription.available, memedPrescription.warmUp, linkedProfessionalId, user?.professional_id]);

  useEffect(() => {
    if (!panelPatientId) return;
    return subscribeMemedPrescriptionSaved((ev) => {
      if (ev.patientId !== panelPatientId) return;
      refreshAfterMemedPrescription();
    });
  }, [panelPatientId, refreshAfterMemedPrescription]);

  useEffect(() => {
    if (activeTab !== 'evolutions') setExpandedEvolutionIds([]);
    if (!showClinicalEvolutions && activeTab === 'evolutions') setActiveTab('profile');
    if (!showClinicalEvolutions && activeTab === 'diagnoses') setActiveTab('profile');
    if (activeTab !== 'medical_reports') setExpandedMedicalReportId(null);
  }, [activeTab, showClinicalEvolutions]);

  useEffect(() => {
    if (isFisioterapia && activeTab === 'medical_reports') {
      setActiveTab('profile');
    }
  }, [isFisioterapia, activeTab]);

  useEffect(() => {
    if (!isOdontologia && ODONTOLOGY_TAB_KEYS.includes(activeTab)) {
      setActiveTab('profile');
    }
  }, [isOdontologia, activeTab]);

  useEffect(() => {
    if (isOdontologia && activeTab === 'periodontal') {
      setActiveTab('odontogram');
      setOdontogramSection('periodontal');
    }
  }, [isOdontologia, activeTab]);

  useEffect(() => {
    if (idocEnabledLoading || activeTab !== 'idoc') return;
    setActiveTab(idocEnabled ? 'images' : 'profile');
  }, [idocEnabled, idocEnabledLoading, activeTab]);

  useEffect(() => {
    setIdocUnseen(0);
    if (!idocEnabled || !panelPatientId) return;
    let cancelled = false;
    idocService
      .getPatientUnseenCount(panelPatientId)
      .then(({ count }) => { if (!cancelled) setIdocUnseen(count); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [idocEnabled, panelPatientId]);

  // ── Handlers ──

  const handleOpenWhatsAppConversation = async () => {
    const contactId = patient?.contact_id ? String(patient.contact_id) : '';
    if (!contactId) {
      setSnackMsg('Paciente sem conversa vinculada. Vincule o contato no inbox dinâmico primeiro.');
      return;
    }
    setOpeningWhatsAppInbox(true);
    try {
      const resolved = await inboxService.resolveInboxForContact(contactId);
      const path = resolved?.path || '/inbox/whatsapp';
      navigate(`${path}?contactId=${contactId}`);
    } catch {
      setSnackMsg('Não foi possível abrir o inbox; tentando WhatsApp padrão.');
      navigate(`/inbox/whatsapp?contactId=${contactId}`);
    } finally {
      setOpeningWhatsAppInbox(false);
    }
  };

  const handleProcedureItemStatusChange = async (itemId: string, status: string) => {
    try {
      const { data: res } = await axios.patch(getApiUrl(`/clinic/treatment-plan-items/${itemId}/status`), { status });
      const warnings: string[] = res.data?.reservation_warnings || res.data?.data?.reservation_warnings || [];
      loadPatientProcedures();
      if (warnings.length > 0) {
        setSnackMsg(`Status atualizado. Avisos de reserva: ${warnings.join('; ')}`);
      }
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const handleExecuteProcedure = (item: any) => {
    setExecutingItem(item);
    setExecuteForm({
      professional_id: user?.professional_id || '',
      referring_professional_id: '',
      notes: '',
      date_mode: 'today',
      executed_date: todayLocalISODate(),
    });
    if (professionals.length === 0) loadProfessionals();
    setExecuteDialogOpen(true);
  };

  const confirmExecute = async () => {
    if (!executingItem || !executeForm.professional_id) return;
    if (executeForm.date_mode === 'custom' && !executeForm.executed_date) {
      setSnackMsg('Selecione a data de execução');
      return;
    }
    setExecuteSaving(true);
    try {
      const payload = {
        professional_id: executeForm.professional_id,
        referring_professional_id: executeForm.referring_professional_id || undefined,
        notes: executeForm.notes || undefined,
        executed_at: executeForm.date_mode === 'custom' ? executeForm.executed_date : undefined,
      };
      const { data: res } = await axios.post(getApiUrl(`/clinic/treatment-plan-items/${executingItem.id}/execute`), payload);
      const warnings = res.data?.stock_warnings || res.data?.data?.stock_warnings || [];
      const treatmentCrm = res.data?.treatment_crm || res.data?.data?.treatment_crm;
      let successMsg = warnings.length > 0
        ? `Executado! Alertas de estoque: ${warnings.join('; ')}`
        : 'Procedimento executado com sucesso!';
      if (treatmentCrm?.pending_items > 0) {
        successMsg += ` · ${treatmentCrm.pending_items} procedimento(s) pendente(s) — card CRM atualizado.`;
      } else if (treatmentCrm && treatmentCrm.pending_items === 0) {
        successMsg += ' · Tratamento concluído no CRM.';
      }
      setSnackMsg(successMsg);
      setExecuteDialogOpen(false);
      loadPatientProcedures();
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Erro ao executar procedimento');
    } finally { setExecuteSaving(false); }
  };

  const handleUndoExecution = async (itemId: string) => {
    if (!window.confirm('Desfazer a execução deste procedimento? Os materiais consumidos serão devolvidos ao estoque.')) return;
    try {
      await axios.post(getApiUrl(`/clinic/treatment-plan-items/${itemId}/undo-execution`));
      setSnackMsg('Execução desfeita com sucesso');
      loadPatientProcedures();
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Erro ao desfazer');
    }
  };

  const handleDocUpload = async () => {
    if (!patient || docUploadItems.length === 0) return;
    setDocUploading(true);
    const uploaded: any[] = [];
    const failedKeys: string[] = [];
    try {
      for (const item of docUploadItems) {
        const fd = new FormData();
        fd.append('file', item.file);
        const title = item.title.trim() || defaultDocUploadTitle(item.file);
        fd.append('title', title);
        fd.append('document_type', docUploadForm.document_type);
        if (docUploadForm.description) fd.append('description', docUploadForm.description);
        try {
          const { data: res } = await axios.post(
            getApiUrl(`/clinic/patients/${patientId}/documents/upload`), fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          if (res.success) uploaded.push(res.data);
          else failedKeys.push(item.key);
        } catch {
          failedKeys.push(item.key);
        }
      }

      if (uploaded.length) setDocuments((prev) => [...uploaded, ...prev]);

      if (failedKeys.length === 0) {
        resetDocUpload();
        setSnackMsg(uploaded.length === 1 ? 'Documento anexado com sucesso!' : `${uploaded.length} documentos anexados com sucesso!`);
      } else if (uploaded.length > 0) {
        setDocUploadItems((prev) => prev.filter((item) => failedKeys.includes(item.key)));
        setSnackMsg(`${uploaded.length} anexado(s), ${failedKeys.length} falhou(ram).`);
      } else {
        setSnackMsg('Erro ao fazer upload do documento');
      }
    } finally { setDocUploading(false); }
  };

  const handleImageUpload = async () => {
    if (!patientId || imageFiles.length === 0) return;
    setImageUploading(true);
    const uploaded: any[] = [];
    const failedNames: string[] = [];
    try {
      for (const file of imageFiles) {
        const title = imageFiles.length === 1 && imageUploadForm.title.trim()
          ? imageUploadForm.title.trim()
          : (file.name.replace(/\.[^/.]+$/, '') || file.name);
        try {
          const fd = new FormData();
          fd.append('file', file);
          const { data: uploadRes } = await axios.post(
            getApiUrl('/clinic/patient-images/upload'),
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          if (!uploadRes.success || !uploadRes.data?.file_url) {
            failedNames.push(file.name);
            continue;
          }
          const { data: createRes } = await axios.post(getApiUrl('/clinic/patient-images'), {
            patient_id: patientId,
            title,
            description: imageUploadForm.description || null,
            image_type: imageUploadForm.image_type,
            file_url: uploadRes.data.file_url,
            file_name: uploadRes.data.file_name || file.name,
            file_type: uploadRes.data.file_type || file.type,
            file_size: uploadRes.data.file_size || file.size,
            capture_date: new Date().toISOString().split('T')[0],
            metadata: { source: 'patient_panel' },
          });
          if (createRes.success) uploaded.push(createRes.data);
          else failedNames.push(file.name);
        } catch {
          failedNames.push(file.name);
        }
      }

      if (uploaded.length) setPatientImages((prev) => [...uploaded, ...prev]);

      if (failedNames.length === 0) {
        resetImageUpload();
        setSnackMsg(uploaded.length === 1 ? 'Imagem anexada com sucesso!' : `${uploaded.length} imagens anexadas com sucesso!`);
      } else if (uploaded.length > 0) {
        setImageFiles((prev) => prev.filter((f) => failedNames.includes(f.name)));
        setSnackMsg(`${uploaded.length} anexada(s), ${failedNames.length} falhou(ram).`);
      } else {
        setSnackMsg('Erro ao anexar imagem');
      }
    } finally {
      setImageUploading(false);
    }
  };

  const downloadPatientImage = (img: any) => {
    if (!img?.file_url) return;
    const fileName = img.file_name || img.title || `imagem_${img.id || Date.now()}.jpg`;
    const link = document.createElement('a');
    link.href = img.file_url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImageDelete = async (img: any) => {
    if (!window.confirm(`Excluir a imagem "${img.title || 'imagem'}"? Esta ação não pode ser desfeita.`)) return;
    try {
      const { data: res } = await axios.delete(getApiUrl(`/clinic/patient-images/${img.id}`));
      if (res.success) {
        setPatientImages((prev) => prev.filter((i) => i.id !== img.id));
        if (expandedImage === img.file_url) setExpandedImage(null);
        setSnackMsg('Imagem excluída com sucesso!');
      } else {
        setSnackMsg(res.error || 'Erro ao excluir imagem');
      }
    } catch (err: any) {
      setSnackMsg(err?.response?.data?.error || 'Erro ao excluir imagem');
    }
  };

  // ── Print ──

  const printPatient = async () => {
    if (!patient) return;
    const p = patient;
    const s = healthcareSummary;
    let clinic: Record<string, unknown> = {};
    try {
      const { data: body } = await axios.get<{ data?: Record<string, unknown> }>(getApiUrl('/clinic/settings'));
      if (body?.data && typeof body.data === 'object') clinic = body.data;
    } catch {
      /* imprime sem cabeçalho da clínica */
    }

    const allergies = (s?.patient?.allergies || p.allergies || []) as string[];
    const conditions = (s?.patient?.chronic_conditions || p.chronic_conditions || []) as string[];
    const meds = (s?.patient?.medications || p.medications || []) as string[];

    const opMeta = (p.metadata && typeof p.metadata === 'object' ? p.metadata.ophthalmology_preconsult : null) || {};
    const isOphthalmologyClinic = clinic.clinic_medical_specialty === 'oftalmologia';
    const hasVitals = Boolean(p.vital_blood_pressure || p.vital_heart_rate || p.vital_glucose);
    const ynPrint = (v: unknown) => (v ? 'Sim' : '');
    const textPrint = (v: unknown) => (v ? escapeHtml(String(v)) : '');
    const facoParts: string[] = [];
    if (opMeta.faco_od) facoParts.push('OD');
    if (opMeta.faco_oe) facoParts.push('OE');
    const avParts: string[] = [];
    if (opMeta.av_cc) avParts.push('CC');
    if (opMeta.av_sc) avParts.push('SC');
    const facoPrint = facoParts.length ? escapeHtml(facoParts.join(', ')) : '';
    const avPrint = avParts.length ? escapeHtml(avParts.join(', ')) : '';

    const sheetVisitCount =
      s != null ? Number(s.stats?.completed_appointments) || 0 : Number(p.total_visits) || 0;
    const sheetTotalInvested =
      s != null ? Number(s.financial?.total_paid) || 0 : Number(p.total_spent) || 0;

    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Ficha — ${escapeHtml(p.full_name)}</title>
<style>
*{box-sizing:border-box}
:root{--lh-accent:#2563eb;--lh-accent-rgb:37,99,235}
body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;padding:28px 36px;color:#111;font-size:13px;background:#fff;margin:0}
.clinic-sheet-letterhead{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;border-bottom:1px solid rgba(var(--lh-accent-rgb),0.30);padding-bottom:18px;margin-bottom:26px;position:relative}
.clinic-sheet-letterhead::after{content:'';position:absolute;left:0;bottom:-2px;width:96px;height:3px;background:var(--lh-accent);border-radius:2px}
.doc-head-logo{flex-shrink:0;min-width:120px}
.doc-head-logo img{display:block;max-width:170px;max-height:70px;width:auto;height:auto;object-fit:contain;object-position:left top}
.doc-head-logo--empty{min-height:48px}
.doc-head-center{flex:1;min-width:0;text-align:center;align-self:center}
.doc-head-meta{flex-shrink:0;max-width:44%;font-size:9.5px;color:#5b6b80;text-align:right;line-height:1.75;letter-spacing:0.015em;padding-left:18px;border-left:1px solid #e8edf3;align-self:stretch}
.clinic-name{font-family:Georgia,'Times New Roman',serif;font-size:21px;font-weight:700;color:var(--lh-accent);margin:0;letter-spacing:0.015em}
.clinic-subtitle{font-size:9.5px;color:#64748b;margin-top:5px;text-transform:uppercase;letter-spacing:0.18em;font-weight:600}
.lh-professionals{display:flex;flex-wrap:wrap;gap:8px 0;margin:0;padding:0;border:none;justify-content:flex-start;text-align:left}
.lh-prof{font-size:10px;line-height:1.45;color:#475569;padding-right:22px}
.lh-prof + .lh-prof{padding-left:22px;border-left:1px solid #e8edf3}
.lh-prof-name{font-weight:700;color:#0f172a;font-size:11.5px;letter-spacing:0.02em}
.lh-prof-sub{color:var(--lh-accent);font-size:8.5px;font-weight:600;text-transform:uppercase;letter-spacing:0.12em;margin-top:2px}
.lh-prof-reg{color:#8492a6;font-size:9px;letter-spacing:0.05em;margin-top:1px}
.lh-footer-units{margin-top:20px;padding-top:12px;border-top:1px solid #e8edf3;display:flex;flex-wrap:wrap;gap:10px 0;justify-content:flex-start;position:relative;page-break-before:avoid;break-before:avoid-page;page-break-inside:avoid;break-inside:avoid}
.lh-footer-units::before{content:'';position:absolute;top:-2px;left:0;width:64px;height:3px;background:var(--lh-accent);border-radius:2px}
.lh-unit{font-size:9px;line-height:1.6;color:#5b6b80;text-align:left;flex:1 1 0;min-width:150px;padding:0 18px;border-left:1px solid #e8edf3}
.lh-unit:first-child{padding-left:0;border-left:none}
.lh-unit-name{font-weight:700;color:#0f172a;letter-spacing:0.12em;text-transform:uppercase;font-size:8.5px;margin-bottom:4px}
.lh-unit-line{display:flex;align-items:flex-start;gap:5px;margin-top:2px}
.lh-unit-ico{flex-shrink:0;display:inline-flex;align-items:center;margin-top:1px;color:var(--lh-accent)}
.patient-sheet-title{font-size:22px;margin:4px 0 2px;color:#2563eb;font-weight:700}
.sub{color:#64748b;font-size:13px;margin-bottom:4px}
h2{font-size:13px;font-weight:700;border-bottom:2px solid #3b82f6;padding-bottom:3px;margin:18px 0 8px;color:#2563eb;text-transform:uppercase;letter-spacing:.5px}
.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:4px 20px;margin-bottom:8px}
.field{margin-bottom:4px}
.label{color:#6b7280;font-size:11px;display:block;font-weight:600}
.alert{background:#fef3c7;border:1px solid #fde68a;border-radius:4px;padding:6px 10px;margin:6px 0;font-size:12px}
.alert.red{background:#fee2e2;border-color:#fecaca}
.alert.blue{background:#dbeafe;border-color:#bfdbfe}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:4px}
th,td{border:1px solid #e5e7eb;padding:5px 8px;text-align:left}
th{background:#f9fafb;font-weight:600}
.chip{display:inline-block;padding:1px 7px;border-radius:10px;font-size:11px;font-weight:600;background:#d1fae5;color:#059669;margin:2px}
.chip.red{background:#fee2e2;color:#dc2626}
.chip.yellow{background:#fef3c7;color:#d97706}
.chip.blue{background:#dbeafe;color:#1d4ed8}
@media print{
  @page{size:A4;margin:10mm 12mm}
  body{background:#fff;padding:0;margin:0}
  .clinic-sheet-letterhead{padding-bottom:10px;margin-bottom:12px;gap:16px}
  .doc-head-logo img{max-height:56px;max-width:140px}
  .lh-footer-units{margin-top:6px!important;padding-top:6px!important;gap:6px 0}
  h2{margin:12px 0 6px}
}
</style></head><body style="${clinicPrintAccentVarsStyle(clinic)}">
${isClinicLetterheadEnabled(clinic) ? `<div class="clinic-sheet-letterhead">${buildClinicLetterheadBlockHtml(clinic)}</div>` : buildLetterheadTopSpacerHtml(clinic)}
<div class="patient-sheet-title">${escapeHtml(p.full_name)}</div>
${p.social_name ? `<div class="sub">Nome social: ${escapeHtml(p.social_name)}</div>` : ''}
<div class="sub">Ficha gerada em ${new Date().toLocaleString('pt-BR')} &nbsp;|&nbsp; Status: ${p.status === 'active' ? 'Ativo' : 'Inativo'}</div>

<h2>Dados Pessoais</h2>
<div class="grid">
${p.cpf ? `<div class="field"><span class="label">CPF</span>${escapeHtml(p.cpf)}</div>` : ''}
${p.rg ? `<div class="field"><span class="label">RG</span>${escapeHtml(p.rg)}</div>` : ''}
${p.birth_date ? `<div class="field"><span class="label">Data de Nascimento</span>${formatDate(p.birth_date)} (${calculateAge(p.birth_date)})</div>` : ''}
${p.gender ? `<div class="field"><span class="label">Gênero</span>${getGenderLabel(p.gender)}</div>` : ''}
${p.marital_status ? `<div class="field"><span class="label">Estado Civil</span>${escapeHtml(p.marital_status)}</div>` : ''}
${p.profession ? `<div class="field"><span class="label">Profissão</span>${escapeHtml(p.profession)}</div>` : ''}
${p.mother_name ? `<div class="field"><span class="label">Nome da Mãe</span>${escapeHtml(p.mother_name)}</div>` : ''}
${p.father_name ? `<div class="field"><span class="label">Nome do Pai</span>${escapeHtml(p.father_name)}</div>` : ''}
${p.blood_type ? `<div class="field"><span class="label">Tipo Sanguíneo</span>${escapeHtml(p.blood_type)}</div>` : ''}
</div>

<h2>Rastreio</h2>
<div class="grid">
${p.whatsapp ? `<div class="field"><span class="label">WhatsApp</span>${escapeHtml(p.whatsapp)}</div>` : ''}
${p.email ? `<div class="field"><span class="label">E-mail</span>${escapeHtml(p.email)}</div>` : ''}
${(p.patient_source_ref?.name || p.patient_source) ? `<div class="field"><span class="label">Origem do Lead</span>${escapeHtml(p.patient_source_ref?.name || p.patient_source)}</div>` : ''}
</div>

${(p.companion_name || p.companion_relation) ? `
<h2>Acompanhante</h2>
<div class="grid">
${p.companion_name ? `<div class="field"><span class="label">Nome</span>${escapeHtml(p.companion_name)}</div>` : ''}
${p.companion_relation ? `<div class="field"><span class="label">Relação</span>${escapeHtml(p.companion_relation)}</div>` : ''}
</div>` : ''}

${(() => {
  const rels = normalizePatientFamilyRelatives(p.family_relatives);
  if (!rels.length) return '';
  return `<h2>Outros Familiares</h2><div class="grid">${rels.map((r) => `<div class="field"><span class="label">${escapeHtml(r.relation || 'Familiar')}</span>${escapeHtml(r.name)}${r.profession ? ` — ${escapeHtml(r.profession)}` : ''}</div>`).join('')}</div>`;
})()}

<h2>Contatos</h2>
<div class="grid">
${p.phone ? `<div class="field"><span class="label">Telefone</span>${escapeHtml(p.phone)}</div>` : ''}
${p.phone_secondary ? `<div class="field"><span class="label">Tel. adicional (informação)</span>${escapeHtml(p.phone_secondary)}</div>` : ''}
</div>

${(p.address_street || p.address_city) ? `
<h2>Endereço</h2>
<div>${escapeHtml([p.address_street, p.address_number, p.address_complement].filter(Boolean).join(', '))}</div>
<div>${escapeHtml([p.address_neighborhood, p.address_city, p.address_state].filter(Boolean).join(' - '))}${p.address_zip ? ` • CEP: ${escapeHtml(p.address_zip)}` : ''}</div>
` : ''}

${(isOphthalmologyClinic || hasVitals) ? `
<h2>Sinais Vitais</h2>
<div class="grid">
<div class="field"><span class="label">PA (Pressão Arterial)</span>${p.vital_blood_pressure ? escapeHtml(p.vital_blood_pressure) : ''}</div>
<div class="field"><span class="label">FC (Frequência Cardíaca)</span>${p.vital_heart_rate ? escapeHtml(p.vital_heart_rate) : ''}</div>
<div class="field"><span class="label">Glicemia</span>${p.vital_glucose ? escapeHtml(p.vital_glucose) : ''}</div>
</div>` : ''}

<h2>Informações de Saúde</h2>
${p.medical_notes || s?.patient?.medical_notes ? `<div class="field"><span class="label">Obs. Médicas</span>${escapeHtml(p.medical_notes || s?.patient?.medical_notes)}</div>` : ''}
${allergies.length ? `<div class="alert red"><strong>Alergias:</strong> ${escapeHtml(allergies.join(', '))}</div>` : ''}
${conditions.length ? `<div class="alert yellow"><strong>Condições Crônicas:</strong> ${escapeHtml(conditions.join(', '))}</div>` : ''}
${meds.length ? `<div class="alert blue"><strong>Medicamentos em Uso:</strong> ${escapeHtml(meds.join(', '))}</div>` : ''}
${!allergies.length && !conditions.length && !meds.length && !p.medical_notes ? '<div style="color:#9ca3af">Sem alertas de saúde registrados.</div>' : ''}

${(p.clinic_insurances?.name || p.insurance_plan) ? `
<h2>Convênio / Plano de Saúde</h2>
<div class="grid">
<div class="field"><span class="label">Plano</span>${escapeHtml(p.clinic_insurances?.name || p.insurance_plan)}</div>
${p.insurance_number ? `<div class="field"><span class="label">Nº do Cartão</span>${escapeHtml(p.insurance_number)}</div>` : ''}
${p.insurance_validity ? `<div class="field"><span class="label">Validade</span>${formatDate(p.insurance_validity)}</div>` : ''}
</div>` : ''}

${isOphthalmologyClinic ? `
<h2>Pré-Consulta (Oftalmologia)</h2>
<div class="grid">
<div class="field" style="grid-column:1/-1"><span class="label">Motivo da consulta</span>${textPrint(opMeta.reason)}</div>
<div class="field"><span class="label">HAS</span>${ynPrint(opMeta.has)}</div>
<div class="field"><span class="label">DM</span>${ynPrint(opMeta.dm)}</div>
<div class="field"><span class="label">Glaucoma</span>${ynPrint(opMeta.glaucoma)}</div>
<div class="field"><span class="label">FACO</span>${facoPrint}</div>
<div class="field" style="grid-column:1/-1"><span class="label">HF (História Familiar)</span>${textPrint(opMeta.hf)}</div>
<div class="field"><span class="label">AV (Acuidade Visual)</span>${avPrint}</div>
</div>` : ''}

${(p.guardian_name || p.guardian_cpf || p.guardian_phone || p.guardian_relation) ? `
<h2>Responsável Legal</h2>
<div class="grid">
${p.guardian_name ? `<div class="field"><span class="label">Nome</span>${escapeHtml(p.guardian_name)}</div>` : ''}
${p.guardian_cpf ? `<div class="field"><span class="label">CPF</span>${escapeHtml(p.guardian_cpf)}</div>` : ''}
${p.guardian_phone ? `<div class="field"><span class="label">Telefone</span>${escapeHtml(p.guardian_phone)}</div>` : ''}
${p.guardian_relation ? `<div class="field"><span class="label">Parentesco</span>${escapeHtml(p.guardian_relation)}</div>` : ''}
</div>` : ''}

${p.emergency_contact_name ? `
<h2>Contato de Emergência</h2>
<div>${escapeHtml(p.emergency_contact_name)}${p.emergency_contact_relation ? ` (${escapeHtml(p.emergency_contact_relation)})` : ''}${p.emergency_contact_phone ? ` — ${escapeHtml(p.emergency_contact_phone)}` : ''}</div>
` : ''}

<h2>Resumo Clínico</h2>
<div class="grid">
<div class="field"><span class="label">Total de Consultas</span>${sheetVisitCount}</div>
<div class="field"><span class="label">Total Investido</span>${formatCurrency(sheetTotalInvested)}</div>
${p.first_visit_date ? `<div class="field"><span class="label">Primeira Consulta</span>${formatDate(p.first_visit_date)}</div>` : ''}
${p.last_visit_date ? `<div class="field"><span class="label">Última Consulta</span>${formatDate(p.last_visit_date)}</div>` : ''}
</div>

${s?.upcoming_appointments?.length ? `
<h2>Próximas Consultas</h2>
<table>
<thead><tr><th>Data</th><th>Hora</th><th>Tipo</th><th>Profissional</th><th>Status</th></tr></thead>
<tbody>
${(s.upcoming_appointments as any[]).map((a: any) => `<tr>
<td>${formatDate(a.appointment_date)}</td>
<td>${escapeHtml(a.start_time?.substring(0, 5) || '-')}</td>
<td>${escapeHtml(a.appointment_type || '-')}</td>
<td>${a.clinic_professionals?.full_name ? `Dr(a). ${escapeHtml(a.clinic_professionals.full_name)}` : '-'}</td>
<td>${escapeHtml(getStatusConfig(a.status).label)}</td>
</tr>`).join('')}
</tbody></table>` : ''}

${s?.recent_appointments?.length ? `
<h2>Consultas Recentes</h2>
<table>
<thead><tr><th>Data</th><th>Hora</th><th>Tipo</th><th>Profissional</th><th>Status</th></tr></thead>
<tbody>
${(s.recent_appointments as any[]).map((a: any) => `<tr>
<td>${formatDate(a.appointment_date)}</td>
<td>${escapeHtml(a.start_time?.substring(0, 5) || '-')}</td>
<td>${escapeHtml(a.title || a.appointment_type || '-')}</td>
<td>${a.clinic_professionals?.full_name ? `Dr(a). ${escapeHtml(a.clinic_professionals.full_name)}` : '-'}</td>
<td>${escapeHtml(getStatusConfig(a.status).label)}</td>
</tr>`).join('')}
</tbody></table>` : ''}

${buildLetterheadFooterUnitsHtml(clinic)}
</body></html>`;

    printClinicHtml(html);
  };

  const openDocsUpload = () => {
    setActiveTab('docs');
    setShowDocUpload(true);
    void loadDocuments();
  };

  const findTodayEditableEvolution = () => {
    if (!user?.professional_id) return null;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return (timelineEvolutions as any[]).find((evo) =>
      String(evo?.evolution_date || '').slice(0, 10) === today
      && (evo?.professional_id || evo?.professional?.id) === user.professional_id
      && canEditEvolutionRecord(evolutionViewer, evo),
    ) || null;
  };

  const openEvolutionDialog = (existingId: string | null) => {
    setContinueTodayEvolution(null);
    setEvolutionExistingId(existingId);
    setEvolutionDialogOpen(true);
    setActiveTab('evolutions');
  };

  const openNewEvolution = () => {
    if (!showClinicalEvolutions) return;
    // Evita várias evoluções picadas no mesmo atendimento: oferece retomar a de hoje.
    const todayEvolution = findTodayEditableEvolution();
    if (todayEvolution) {
      setContinueTodayEvolution(todayEvolution);
      return;
    }
    openEvolutionDialog(null);
  };

  // ── Guard ──
  if (!patient && summaryLoading) {
    return (
      <Box sx={patientPanelLoadingSkeletonSx}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Skeleton variant="circular" width={56} height={56} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" height={28} />
              <Skeleton variant="text" width="45%" height={20} />
            </Box>
          </Box>
          <Skeleton variant="rounded" height={52} />
          <Skeleton variant="rounded" height={36} />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      </Box>
    );
  }

  if (!patient) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>Paciente não encontrado.</Alert>
    );
  }

  /** KPIs do perfil: vêm do resumo (dados reais no banco), não só das colunas total_visits/total_spent do paciente. */
  const profileConsultCount =
    healthcareSummary != null
      ? Number(healthcareSummary.stats?.completed_appointments ?? 0) || 0
      : Number(patient.total_visits) || 0;
  const profileTotalInvested =
    healthcareSummary != null
      ? Number(healthcareSummary.financial?.total_paid) || 0
      : Number(patient.total_spent) || 0;

  const primaryDiagnosisLabel = formatPrimaryDiagnosisLabel(patient);
  const needsPrimaryDiagnosisSelection = Boolean(patientDiagnoses?.needs_primary_selection);

  const allPatientTabItems: { key: PatientPanelTabKey; label: string; icon: typeof PersonIcon; badge?: number }[] = [
    { key: 'profile', label: 'Perfil', icon: PersonIcon },
    { key: 'history', label: 'Histórico', icon: ScheduleIcon },
    { key: 'evolutions', label: 'Evoluções', icon: TimelineIcon },
    ...(!isFisioterapia ? [{ key: 'medical_reports' as PatientPanelTabKey, label: 'Relatórios', icon: ReportIcon }] : []),
    ...(isFisioterapia ? [{ key: 'assessment' as PatientPanelTabKey, label: 'Avaliação', icon: AssessmentIcon }] : []),
    ...(isOdontologia ? [
      { key: 'odontogram' as PatientPanelTabKey, label: 'Odontograma', icon: OdontogramIcon },
      { key: 'dental_treatments' as PatientPanelTabKey, label: 'Tratamentos', icon: DentalTreatmentIcon },
    ] : []),
    { key: 'financial', label: 'Financeiro', icon: MoneyIcon },
    { key: 'docs', label: 'Docs', icon: FolderDocIcon, badge: documents.length || 0 },
    { key: 'procedures', label: 'Procedimentos', icon: MedicalIcon },
    {
      key: 'images',
      label: 'Imagens',
      icon: ImageIcon,
      badge: (patientImages.length || 0) + (idocEnabled && idocUnseen ? idocUnseen : 0) || undefined,
    },
    {
      key: 'diagnoses',
      label: 'Diagnósticos',
      icon: DiagnosisIcon,
      badge: needsPrimaryDiagnosisSelection ? 1 : undefined,
    },
  ];
  const filteredTabItems = allPatientTabItems.filter((item) => {
    if (item.key === 'evolutions' && !showClinicalEvolutions) return false;
    if (item.key === 'diagnoses' && !showClinicalEvolutions) return false;
    return true;
  });
  const visibleTabItems = filteredTabItems;

  const headerMetaItems = [
    patient.birth_date ? calculateAge(patient.birth_date) : '',
    patient.clinic_insurances?.name || patient.insurance_plan || '',
    patient.first_visit_date ? `1ª consulta ${formatDate(patient.first_visit_date)}` : '',
    patient.social_name ? `Nome social: ${patient.social_name}` : '',
  ].filter(Boolean);
  /** Mobile: idade e 1ª consulta ficam no bloco Identificação/Clínico — evita duplicar no topo. */
  const headerMetaItemsForPanel = isMobile
    ? headerMetaItems.filter(
        (item) => !item.endsWith(' anos') && !item.startsWith('1ª consulta '),
      )
    : headerMetaItems;

  const headerActions: PatientPanelHeaderAction[] = [];
  if (showClinicalEvolutions) {
    headerActions.push({ key: 'evolution', label: 'Evolução', tooltip: 'Nova evolução', icon: <TimelineIcon />, onClick: openNewEvolution, primary: true });
  }
  if (!isFisioterapia) {
    headerActions.push({
      key: 'prescription',
      label: 'Receituário',
      tooltip: 'Nova receita (abre aqui mesmo)',
      icon: <MedicationIcon />,
      showToolbarLabel: true,
      onClick: () => {
        setEditingPrescriptionId(null);
        setPrescriptionDialogOpen(true);
      },
    });
  } else if (plansEnabled) {
    headerActions.push({ key: 'plan', label: 'Plano', tooltip: 'Vender plano / mensalidade (paciente já selecionado)', icon: <PlanSellIcon />, onClick: () => setSellPlanDialogOpen(true) });
  }
  headerActions.push({
    key: 'budget',
    label: 'Orçamento',
    tooltip: onNewBudget ? 'Novo orçamento (abre aqui mesmo)' : 'Novo orçamento (paciente já selecionado)',
    icon: <DocIcon />,
    showToolbarLabel: true,
    onClick: openNewBudget,
  });
  if (isInboxEmbedded && onInboxSchedule) {
    headerActions.push({ key: 'schedule', label: 'Agendar', tooltip: 'Agendar consulta', icon: <CalendarIcon />, onClick: onInboxSchedule });
  } else if (!isAgendaEmbedded) {
    headerActions.push({ key: 'schedule', label: 'Agendar', tooltip: 'Agendar consulta', icon: <CalendarIcon />, onClick: () => window.open('/agenda', '_blank') });
  }
  if (!isInboxEmbedded && patient?.contact_id) {
    headerActions.push({
      key: 'whatsapp',
      label: openingWhatsAppInbox ? 'Abrindo…' : 'WhatsApp',
      tooltip: 'Abrir conversa no inbox',
      icon: <WhatsAppIcon sx={{ color: '#25D366' }} />,
      showToolbarLabel: true,
      disabled: openingWhatsAppInbox,
      onClick: () => void handleOpenWhatsAppConversation(),
    });
  }

  const headerMenuActions: PatientPanelHeaderAction[] = [
    { key: 'print', label: 'Imprimir ficha', icon: <PrintIcon />, onClick: () => void printPatient() },
  ];
  if (isAgendaEmbedded) {
    headerMenuActions.push(
      { key: 'upload', label: 'Enviar documento', icon: <UploadFileIcon />, onClick: openDocsUpload },
      {
        key: 'bulk-reschedule',
        label: 'Remarcar em lote',
        tooltip: upcomingForBulkReschedule.length === 0 ? 'Sem consultas futuras para remarcar' : BULK_RESCHEDULE_TOOLTIP,
        icon: <EventRepeatIcon />,
        onClick: openBulkRescheduleDialog,
        disabled: upcomingForBulkReschedule.length === 0,
      },
    );
  }
  if (isInboxEmbedded && onInboxHealthMessage) {
    headerMenuActions.push({ key: 'health-message', label: 'Mensagem de saúde', icon: <SendIcon />, onClick: onInboxHealthMessage });
  }
  if (isInboxEmbedded && onInboxUnlinkPatient) {
    headerMenuActions.push({ key: 'unlink', label: 'Desvincular paciente', tooltip: 'Desvincular paciente deste contato', icon: <LinkOffIcon />, onClick: onInboxUnlinkPatient, danger: true });
  }
  if (memedPrescription.available && !isFisioterapia && panelPatientId) {
    headerMenuActions.push({
      key: 'memed',
      label: 'Prescrição via Memed (opcional)',
      tooltip: 'Abre em nova aba — integração opcional',
      icon: <OpenInNewIcon fontSize="small" />,
      disabled: memedPrescription.opening,
      onClick: () => {
        void memedPrescription.openPrescription({
          patientId: panelPatientId,
          professionalId: linkedProfessionalId || user?.professional_id,
          appointmentId: linkedAppointmentId,
        });
      },
    });
  }

  const headerTabs = (
    <PatientPanelMobileTabs
      items={visibleTabItems}
      activeTab={activeTab}
      onChange={(key) => setActiveTab(key as PatientPanelTabKey)}
    />
  );

  const renderReceivableActionButtons = (
    rec: any,
    ctx: {
      isPaid: boolean;
      awaitingInsuranceSettlement: boolean;
      remaining: number;
      linkedNfse: any;
    },
  ) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, position: 'relative', zIndex: 1 }}>
      {ctx.awaitingInsuranceSettlement && (
        <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, width: '100%', textAlign: 'right' }}>
          {AWAITING_INSURANCE_SETTLEMENT_LABEL}
        </Typography>
      )}
      {!ctx.isPaid && !ctx.awaitingInsuranceSettlement && (
        <Tooltip title={ctx.remaining > 0 ? `Receber (saldo: ${formatCurrency(ctx.remaining)})` : 'Receber agora'}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openReceiveDialog(rec);
              }}
              sx={{ color: '#10b981' }}
              disabled={ctx.remaining <= 0}
            >
              <MoneyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {!ctx.isPaid && !ctx.awaitingInsuranceSettlement && asaasConnected && rec.status !== 'cancelled' && receivableOpenBalance(rec) >= 5 && (
        <AsaasReceivableChargeButton receivable={rec} onClick={() => setAsaasChargeReceivable(rec)} />
      )}
      {ctx.isPaid && (
        <>
          <Tooltip title="Imprimir recibo">
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  void printReceivableReceipt(rec);
                }}
                disabled={printingReceiptId === rec.id}
                sx={{ color: '#2563eb' }}
                aria-label="Imprimir recibo"
              >
                {printingReceiptId === rec.id
                  ? <CircularProgress size={16} sx={{ color: '#2563eb' }} />
                  : <PrintIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          {ctx.linkedNfse ? (
            <ClinicNfseActions
              nfse={ctx.linkedNfse}
              patientId={patientId}
              onNotify={setSnackMsg}
              onUpdated={() => void refreshNfseForReceivable(rec.id)}
              iconButtonSx={{ color: '#0f766e' }}
            />
          ) : canEmitNfseForReceivable({ ...rec, patient_id: rec.patient_id || patientId }) && (
            <Tooltip title="Emitir nota fiscal (NFSe)">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    void emitReceivableNfse(rec);
                  }}
                  disabled={nfseEmittingId === rec.id}
                  sx={{ color: '#0f766e' }}
                  aria-label="Emitir nota fiscal"
                >
                  {nfseEmittingId === rec.id
                    ? <CircularProgress size={16} sx={{ color: '#0f766e' }} />
                    : <NfseIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      )}
      {canDeleteFinancial && (
        <Tooltip title="Excluir">
          <span style={{ display: 'inline-flex', lineHeight: 0 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                deleteReceivable(rec);
              }}
              sx={{ color: '#dc2626' }}
              aria-label="Excluir conta a receber"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );

  // ── Render ──
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          p: 0,
          flexShrink: 0,
          ...(isMobile
            ? {
                position: 'relative',
                zIndex: 4,
                bgcolor: PATIENT_PANEL_THEME.headerBg,
                backgroundImage: 'none',
              }
            : {}),
        }}
      >
        <PatientPanelHeader
          patientId={patient.id}
          fullName={patient.full_name}
          avatarUrl={patient.avatar_url}
          onAvatarUpdated={(newUrl) => {
            const updated = { ...patient, avatar_url: newUrl };
            setPatient(updated);
            onPatientUpdated?.(updated);
          }}
          isMobile={isMobile}
          collapsed={headerCollapsed}
          backLabel={panelBackLabel}
          onBack={isAgendaEmbedded ? onPanelBack : undefined}
          appointmentNotes={isAgendaEmbedded ? linkedAppointmentNotes || undefined : undefined}
          badges={(
            <>
              {!isMobile && (
                <PatientActivityStatusChip
                  patientId={patient.id}
                  reloadToken={summaryReloadToken + paidSessionsReloadToken}
                  onChanged={() => {
                    void fetchSummary({ silent: true });
                    onRefresh?.();
                  }}
                />
              )}
              {showPaidSessionBadge && (
                <Chip label={PAID_SESSION_LABEL} size="small" sx={paidSessionChipSx} />
              )}
              <PatientPlanBadge plans={patient.active_plans} />
            </>
          )}
          metaItems={headerMetaItemsForPanel}
          diagnosis={showClinicalEvolutions ? {
            label: primaryDiagnosisLabel,
            needsSelection: needsPrimaryDiagnosisSelection,
            onSelect: () => setActiveTab('diagnoses'),
          } : null}
          tags={<ClinicPatientTags patientId={patient.id} compact inline headerInline={isMobile} />}
          actions={headerActions}
          menuActions={headerMenuActions}
          onEdit={onEdit ? () => onEdit(patient) : undefined}
          onRefresh={onRefresh}
          onClose={onClose && !onPanelBack ? onClose : undefined}
          tabs={headerTabs}
        />
      </DialogTitle>

      {/* ── Conteúdo ── */}
      <DialogContent
        className={`patient-panel-content-scroll${isMobile ? ` ${MOBILE_FORM_SURFACE_CLASS}` : ''}`}
        sx={{
          ...patientPanelContentSx,
          ...(isMobile
            ? {
                pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              }
            : {}),
        }}
        onScroll={isMobile ? undefined : handleContentScroll}
      >
        {summaryLoading && !patient ? (
          <Box sx={patientPanelLoadingSkeletonSx}>
            <Stack spacing={1.25}>
              <Skeleton variant="rounded" height={88} />
              <Skeleton variant="rounded" height={120} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          </Box>
        ) : (
          <>
            {/* ── TAB 0: PERFIL ── */}
            {activeTab === 'profile' && (
              <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#fafbfc' }}>
                <LeadAdOriginCard patientId={patient.id} fetchEnabled={Boolean(healthcareSummary)} />
                {/* Alertas de Saúde */}
                {(patient.allergies?.length || patient.chronic_conditions?.length || patient.medications?.length ||
                  healthcareSummary?.patient?.allergies?.length || healthcareSummary?.patient?.chronic_conditions?.length || healthcareSummary?.patient?.medications?.length) ? (
                  <Card sx={{ mb: 1.25, border: '1px solid #fbbf24', borderRadius: 2, bgcolor: '#fffbeb' }}>
                    <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                        <WarningIcon sx={{ color: '#d97706', fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', fontSize: '0.72rem' }}>Alertas de Saúde</Typography>
                      </Box>
                      {(healthcareSummary?.patient?.allergies || patient.allergies || []).length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626' }}>Alergias:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {(healthcareSummary?.patient?.allergies || patient.allergies || []).map((a: string, i: number) => (
                              <Chip key={i} label={a} size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontSize: '0.7rem', height: 22, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {(healthcareSummary?.patient?.chronic_conditions || []).length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706' }}>Condições Crônicas:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {(healthcareSummary?.patient?.chronic_conditions || []).map((c: string, i: number) => (
                              <Chip key={i} label={c} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.7rem', height: 22, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {(healthcareSummary?.patient?.medications || []).length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563eb' }}>Medicamentos em Uso:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {(healthcareSummary?.patient?.medications || []).map((m: string, i: number) => (
                              <Chip key={i} label={m} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontSize: '0.7rem', height: 22, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ) : null}

                {/* KPIs — resumo leve; total investido exato após resumo completo (aba Financeiro) */}
                {(() => {
                  const profileKpis = [
                    { label: 'Consultas', value: profileConsultCount, color: '#1d4ed8' },
                    { label: 'Orçamentos', value: healthcareSummary?.stats?.total_budgets || 0, color: '#2563eb' },
                    ...(!isFisioterapia ? [{ label: 'Receituários', value: healthcareSummary?.stats?.total_prescriptions || 0, color: '#7c3aed' }] : []),
                    { label: 'Total investido', value: formatCurrency(profileTotalInvested), color: '#059669', isText: true },
                  ];
                  const profileSectionTitleSx = {
                    fontWeight: 700,
                    color: '#334155',
                    mb: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  } as const;
                  const profileFieldsGridSx = {
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      sm: 'repeat(2, minmax(0, 1fr))',
                    },
                    gap: 1,
                    columnGap: 1.5,
                    rowGap: 0.85,
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                  } as const;
                  const profileValueSx = {
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    lineHeight: 1.35,
                    minWidth: 0,
                    maxWidth: '100%',
                  } as const;
                  const renderProfileField = (
                    label: string,
                    value: React.ReactNode,
                    key: string,
                    spanFull = false,
                  ) => {
                    const plain = typeof value === 'string' || typeof value === 'number';
                    const text = plain ? String(value) : '';
                    return (
                      <Box key={key} sx={{ minWidth: 0, maxWidth: '100%', ...(spanFull ? { gridColumn: '1 / -1' } : {}) }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.2, display: 'block' }}>
                          {label}
                        </Typography>
                        {plain ? (
                          <Tooltip title={text} enterDelay={500} disableInteractive>
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{
                                ...profileValueSx,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {text}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant="body2"
                            component="div"
                            sx={{ ...profileValueSx, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          >
                            {value}
                          </Typography>
                        )}
                      </Box>
                    );
                  };
                  const personalFields = [
                    { label: 'CPF', value: patient.cpf },
                    { label: 'RG', value: patient.rg },
                    { label: 'Nascimento', value: patient.birth_date ? `${formatDate(patient.birth_date)} (${calculateAge(patient.birth_date)})` : null },
                    { label: 'Gênero', value: getGenderLabel(patient.gender || '') },
                    { label: 'Estado civil', value: patient.marital_status },
                    { label: 'Profissão', value: patient.profession },
                    { label: 'Origem', value: patient.patient_source_ref?.name || patient.patient_source },
                  ].filter((f) => f.value && f.value !== '-');
                  const contactFields = [
                    { label: 'Telefone', value: patient.phone },
                    { label: 'WhatsApp', value: patient.whatsapp },
                    { label: 'Tel. adicional', value: patient.phone_secondary },
                    { label: 'E-mail', value: patient.email },
                  ].filter((f) => f.value);
                  const addressLine1 = [patient.address_street, patient.address_number, patient.address_complement].filter(Boolean).join(', ');
                  const addressLine2 = [
                    [patient.address_neighborhood, patient.address_city, patient.address_state].filter(Boolean).join(' - '),
                    patient.address_zip ? `CEP ${patient.address_zip}` : '',
                  ].filter(Boolean).join(' · ');
                  const hasAddress = Boolean(addressLine1 || addressLine2);
                  const hasInsurance = Boolean(patient.insurance_plan || patient.clinic_insurances?.name);
                  const medicalNotes = healthcareSummary?.patient?.medical_notes || patient.medical_notes;

                  return (
                    <>
                      {summaryLoading && !healthcareSummary ? (
                        <Skeleton variant="rounded" height={40} sx={{ borderRadius: 2, mb: 1.25 }} />
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            mb: 1.25,
                            border: '1px solid #e2e8f0',
                            borderRadius: 2,
                            bgcolor: '#fff',
                            overflow: 'hidden',
                          }}
                        >
                          {profileKpis.map((kpi, i) => (
                            <Box
                              key={kpi.label}
                              sx={{
                                flex: '1 1 72px',
                                textAlign: 'center',
                                py: 0.75,
                                px: 1,
                                borderRight: i < profileKpis.length - 1 ? '1px solid #e2e8f0' : 'none',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  color: kpi.color,
                                  fontSize: kpi.isText ? '0.8rem' : '1.05rem',
                                  lineHeight: 1.2,
                                }}
                              >
                                {kpi.value}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>
                                {kpi.label}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      )}

                      <Card
                        sx={{
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          bgcolor: '#fff',
                          mb: 1.25,
                          boxShadow: 'none',
                        }}
                      >
                        <CardContent sx={{ py: 1.25, px: 1.75, '&:last-child': { pb: 1.25 } }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} lg={6} sx={{ minWidth: 0, pr: { lg: 1.5 } }}>
                              <Typography variant="subtitle2" sx={profileSectionTitleSx}>
                                <PersonIcon sx={{ fontSize: 15, color: '#64748b' }} />
                                Identificação
                              </Typography>
                              {personalFields.length > 0 ? (
                                <Box sx={profileFieldsGridSx}>
                                  {personalFields.map((f) => renderProfileField(
                                    f.label,
                                    f.value,
                                    f.label,
                                    f.label === 'Origem' || f.label === 'Profissão' || f.label === 'Nascimento',
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}

                              {contactFields.length > 0 && (
                                <>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography variant="subtitle2" sx={profileSectionTitleSx}>
                                    <PhoneIcon sx={{ fontSize: 15, color: '#64748b' }} />
                                    Contato
                                  </Typography>
                                  <Box sx={profileFieldsGridSx}>
                                    {contactFields.map((f) => renderProfileField(f.label, f.value, f.label))}
                                  </Box>
                                </>
                              )}
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              lg={6}
                              sx={{
                                minWidth: 0,
                                pl: { lg: 1.5 },
                                borderLeft: { lg: '1px solid #e2e8f0' },
                              }}
                            >
                              <Typography variant="subtitle2" sx={profileSectionTitleSx}>
                                <HeartIcon sx={{ fontSize: 15, color: '#64748b' }} />
                                Clínico e convênio
                              </Typography>
                              <Box sx={profileFieldsGridSx}>
                                {patient.blood_type && renderProfileField(
                                  'Tipo sanguíneo',
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, color: '#dc2626' }}>
                                    <BloodIcon sx={{ fontSize: 14 }} />
                                    {patient.blood_type}
                                  </Box>,
                                  'blood',
                                )}
                                {patient.last_visit_date && renderProfileField('Última consulta', formatDate(patient.last_visit_date), 'last_visit')}
                                {patient.first_visit_date && renderProfileField('Primeira consulta', formatDate(patient.first_visit_date), 'first_visit')}
                                {hasInsurance && renderProfileField(
                                  'Plano',
                                  patient.clinic_insurances?.name || patient.insurance_plan,
                                  'plan',
                                  true,
                                )}
                                {patient.insurance_number && renderProfileField('Nº cartão', patient.insurance_number, 'ins_card')}
                                {patient.insurance_validity && renderProfileField('Validade', formatDate(patient.insurance_validity), 'ins_val')}
                              </Box>
                              {medicalNotes && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: 'block',
                                    mt: 0.75,
                                    fontSize: '0.72rem',
                                    lineHeight: 1.4,
                                    bgcolor: '#f8fafc',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    border: '1px solid #e2e8f0',
                                  }}
                                >
                                  <Box component="span" sx={{ fontWeight: 700, color: '#64748b' }}>Obs. médicas: </Box>
                                  {medicalNotes}
                                </Typography>
                              )}
                            </Grid>

                            {hasAddress && (
                              <Grid item xs={12} sx={{ minWidth: 0, pt: { xs: 0.5, lg: 0 } }}>
                                <Divider sx={{ mb: 1 }} />
                                <Typography variant="subtitle2" sx={profileSectionTitleSx}>
                                  <FolderIcon sx={{ fontSize: 15, color: '#64748b' }} />
                                  Endereço
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.78rem',
                                    lineHeight: 1.45,
                                    color: '#0f172a',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                  }}
                                >
                                  {addressLine1}
                                  {addressLine2 ? (
                                    <>
                                      {addressLine1 ? ' · ' : ''}
                                      {addressLine2}
                                    </>
                                  ) : null}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>

                      <PatientFamilyOverviewCard compact patient={{ ...healthcareSummary?.patient, ...patient }} />
                    </>
                  );
                })()}

                {/* Agenda (unificada ao perfil) */}
                <Card
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    bgcolor: '#fff',
                    mb: 1.5,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ py: 1.25, px: 1.75, '&:last-child': { pb: 1.25 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 1, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.8rem' }}>
                      <CalendarIcon sx={{ fontSize: 17, color: '#1d4ed8' }} /> Agenda
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.82rem' }}>
                        <EventIcon sx={{ color: '#3b82f6', fontSize: 18 }} /> Próximas consultas
                      </Typography>
                      {isAgendaEmbedded && (
                        <Tooltip title={BULK_RESCHEDULE_TOOLTIP}>
                          <span>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<EventRepeatIcon sx={{ fontSize: 18 }} />}
                              onClick={openBulkRescheduleDialog}
                              disabled={upcomingForBulkReschedule.length === 0}
                              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', minWidth: 0, px: 1 }}
                            >
                              Remarcar em lote
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                    {summaryLoading && !healthcareSummary ? (
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        <Skeleton variant="rounded" height={56} />
                        <Skeleton variant="rounded" height={56} />
                      </Stack>
                    ) : healthcareSummary?.upcoming_appointments?.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                        {healthcareSummary.upcoming_appointments.map((apt: any) => {
                          const st = getStatusConfig(apt.status);
                          const opening = openingUpcomingId === apt.id;
                          const profilePatient = healthcareSummary?.patient || patient;
                          return (
                            <PatientProfileAppointmentRow
                              key={apt.id}
                              apt={apt}
                              patient={profilePatient}
                              formatDate={formatDate}
                              variant="upcoming"
                              opening={opening}
                              statusChip={
                                <Chip
                                  label={st.label}
                                  size="small"
                                  sx={{
                                    bgcolor: st.bg,
                                    color: st.textColor ?? st.color,
                                    fontSize: '0.65rem',
                                    height: 22,
                                    fontWeight: 600,
                                  }}
                                />
                              }
                              onClick={() => { void openUpcomingAppointment(apt); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  void openUpcomingAppointment(apt);
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.82rem' }}>Nenhuma consulta agendada.</Typography>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: profileAppointmentHistoryOpen ? 1 : 0,
                        mt: 0.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() => setProfileAppointmentHistoryOpen((v) => !v)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setProfileAppointmentHistoryOpen((v) => !v);
                        }
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.82rem' }}>
                        <TimeIcon sx={{ color: '#64748b', fontSize: 18 }} /> Histórico de agendamentos
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label={profileAppointmentHistoryOpen ? 'Recolher histórico de agendamentos' : 'Expandir histórico de agendamentos'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setProfileAppointmentHistoryOpen((v) => !v);
                        }}
                      >
                        {profileAppointmentHistoryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={profileAppointmentHistoryOpen} unmountOnExit>
                      {panelPatientId ? (
                        <PatientAppointmentHistoryTable
                          patientId={panelPatientId}
                          formatDate={formatDate}
                          onOpenAppointment={(apt) => { void openUpcomingAppointment(apt); }}
                          openingAppointmentId={openingUpcomingId}
                          reloadKey={appointmentHistoryReloadKey}
                        />
                      ) : null}
                    </Collapse>
                  </CardContent>
                </Card>

                {patient.emergency_contact_name && (
                  <Card sx={{ borderRadius: 2, border: '1px solid #fca5a5', bgcolor: '#fff7f7', mt: 0 }}>
                    <CardContent sx={{ py: 1.5, px: 2.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WarningIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b' }}>Contato de Emergência</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {patient.emergency_contact_name}
                        {patient.emergency_contact_relation && ` (${patient.emergency_contact_relation})`}
                        {patient.emergency_contact_phone && ` — ${patient.emergency_contact_phone}`}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {patient.tags && patient.tags.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Tags</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {patient.tags.map((tag: string, i: number) => (
                        <Chip key={i} label={tag} size="small" sx={{ bgcolor: 'rgba(59, 130, 246,0.1)', color: '#2563eb', fontWeight: 600 }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* ── TAB: HISTÓRICO ── */}
            {activeTab === 'history' && (
              fullSummaryLoading && healthcareSummary?.summary_scope !== 'full' ? (
                <Box sx={{ ...patientPanelLoadingSkeletonSx, p: 2 }}>
                  <Stack spacing={1.25}>
                    <Skeleton variant="rounded" height={64} />
                    <Skeleton variant="rounded" height={120} />
                    <Skeleton variant="rounded" height={120} />
                  </Stack>
                </Box>
              ) : (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                {/* Orçamentos */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DocIcon sx={{ color: '#2563eb', fontSize: 20 }} /> Orçamentos
                    </Typography>
                    <Chip label={healthcareSummary?.recent_budgets?.length || 0} size="small" sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }} />
                  </Box>
                  {healthcareSummary?.recent_budgets?.length > 0 ? healthcareSummary.recent_budgets.map((b: any) => {
                    const st = getStatusConfig(b.status);
                    const rowKey = `bud:${b.id}`;
                    const isOpen = expandedHistoryRow === rowKey;
                    const bi = (b.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                    return (
                      <Box key={b.id} sx={{ py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f1f5f9', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', minWidth: 90, fontSize: '0.8rem' }}>{b.budget_number}</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', minWidth: 80 }}>{formatDate(b.issue_date)}</Typography>
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{formatCurrency(b.total)}</Typography>
                          <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.textColor ?? st.color, fontSize: '0.65rem', height: 22, fontWeight: 600 }} />
                          <Tooltip title={isOpen ? 'Recolher' : 'Ver itens'}>
                            <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b' }}>
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Collapse in={isOpen}>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', mx: -2, px: 2, pb: 1.5, borderRadius: '0 0 8px 8px' }}>
                            {bi.length === 0 ? <Typography variant="caption" color="text.secondary">Nenhum item neste resumo.</Typography>
                              : bi.map((it: any, idx: number) => (
                                <Box key={it.id || idx} sx={{ py: 0.5, borderBottom: idx < bi.length - 1 ? '1px dashed #cbd5e1' : 'none' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1d4ed8', fontSize: '0.8rem' }}>{it.procedure_name || 'Procedimento'}</Typography>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Qtd {it.quantity ?? 1} × {formatCurrency(it.unit_price ?? 0)} → <strong>{formatCurrency(it.total ?? 0)}</strong>
                                  </Typography>
                                </Box>
                              ))}
                            <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />} onClick={() => window.open('/clinic/budgets', '_blank')}
                              sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}>
                              Abrir módulo de orçamentos
                            </Button>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  }) : <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nenhum orçamento</Typography>}
                </Box>

                {!isFisioterapia && (
                <>
                <Divider sx={{ my: 2 }} />

                {/* Receituários */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MedicationIcon sx={{ color: '#7c3aed', fontSize: 20 }} /> Receituários
                    </Typography>
                    <Chip
                      label={healthcareSummary?.stats?.total_prescriptions ?? historyPrescriptions.length}
                      size="small"
                      sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 700 }}
                    />
                  </Box>
                  {selectedRxList.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1, px: 1.5, py: 1, borderRadius: 2, bgcolor: '#f5f3ff', border: '1px solid #ddd6fe' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#6d28d9', fontSize: '0.8rem', mr: 0.5 }}>
                        {selectedRxList.length} selecionada{selectedRxList.length > 1 ? 's' : ''}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SignIcon />}
                        disabled={selectedRxPendingSignature.length === 0}
                        onClick={() => openRxBatchSign(selectedRxList)}
                        sx={{ textTransform: 'none', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, fontSize: '0.75rem' }}
                      >
                        {selectedRxPendingSignature.length > 1 ? `Assinar ${selectedRxPendingSignature.length} de uma vez` : 'Assinar'}
                      </Button>
                      <Tooltip title="O paciente recebe um link só e escolhe qual receita abrir">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={selectedRxList.length < 2}
                            onClick={() => setRxBundleSendIds(selectedRxList.map((p: any) => p.id))}
                            sx={{ textTransform: 'none', borderColor: '#c4b5fd', color: '#6d28d9', fontSize: '0.75rem' }}
                          >
                            Enviar em um link
                          </Button>
                        </span>
                      </Tooltip>
                      <Button size="small" onClick={() => setSelectedRxRows({})} sx={{ textTransform: 'none', color: '#64748b', ml: 'auto', fontSize: '0.75rem' }}>
                        Limpar
                      </Button>
                    </Box>
                  )}
                  {prescriptionsLoading && historyPrescriptions.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : historyPrescriptions.length > 0 ? historyPrescriptions.map((p: any) => {
                    const rowKey = `rx:${p.id}`;
                    const isOpen = expandedHistoryRow === rowKey;
                    const meds = (p.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                    return (
                      <Box key={p.id} sx={{ py: 1, px: 2, borderRadius: 2, bgcolor: selectedRxRows[p.id] ? '#faf5ff' : '#fff', border: `1px solid ${selectedRxRows[p.id] ? '#ddd6fe' : '#f1f5f9'}`, mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Checkbox
                            size="small"
                            checked={Boolean(selectedRxRows[p.id])}
                            disabled={!isSelectableRx(p)}
                            onChange={() => toggleRxSelection(p)}
                            sx={{ p: 0.25, ml: -0.75, mr: -1 }}
                            inputProps={{ 'aria-label': `Selecionar receita ${p.prescription_number || ''}` }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#7c3aed', minWidth: 90, fontSize: '0.8rem' }}>{p.prescription_number}</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', minWidth: 80 }}>{formatDate(p.issue_date ?? p.prescription_date)}</Typography>
                          <Typography variant="body2" sx={{ flex: 1, color: '#475569', fontSize: '0.8rem' }}>{meds.length || 0} medicamento(s){p.professional_name && ` • Dr(a). ${p.professional_name}`}</Typography>
                          {isSignedPrescription(p) && (
                            <Chip size="small" label="Assinada" icon={<SignIcon sx={{ fontSize: '0.85rem !important' }} />}
                              sx={{ height: 22, bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: '0.65rem' }} />
                          )}
                          <PrescriptionActionsBar
                            prescription={p}
                            patientFallback={{
                              id: patientId,
                              email: patient?.email,
                              phone: patient?.phone,
                              name: patient?.full_name,
                            }}
                            onUpdated={() => {
                              void fetchSummary();
                              void loadPatientPrescriptions({ silent: true });
                            }}
                            onNotify={setSnackMsg}
                            onEdit={(prescription) => {
                              setEditingPrescriptionId(prescription.id);
                              setPrescriptionDialogOpen(true);
                              setActiveTab('history');
                            }}
                            iconSx={{ color: '#64748b' }}
                          />
                          <Tooltip title={isOpen ? 'Recolher' : 'Ver medicamentos'}>
                            <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b' }}>
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Collapse in={isOpen}>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e9d5ff', bgcolor: '#faf5ff', mx: -2, px: 2, pb: 1.5, borderRadius: '0 0 8px 8px' }}>
                            {meds.length === 0 ? <Typography variant="caption" color="text.secondary">Nenhum medicamento.</Typography>
                              : meds.map((m: any, idx: number) => (
                                <Box key={m.id || idx} sx={{ py: 0.5, borderBottom: idx < meds.length - 1 ? '1px dashed #ddd6fe' : 'none' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#5b21b6', fontSize: '0.8rem' }}>{m.medication_name || 'Medicamento'}</Typography>
                                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                    {[m.dosage, m.frequency, m.duration].filter(Boolean).join(' · ')}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  }) : <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nenhum receituário</Typography>}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Pedidos de Exames */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScienceIcon sx={{ color: '#059669', fontSize: 20 }} /> Pedidos de Exames
                    </Typography>
                    <Chip label={healthcareSummary?.recent_exam_orders?.length || 0} size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 700 }} />
                  </Box>
                  {healthcareSummary?.recent_exam_orders?.length > 0 ? healthcareSummary.recent_exam_orders.map((e: any) => {
                    const rowKey = `ex:${e.id}`;
                    const isOpen = expandedHistoryRow === rowKey;
                    const exItems = (e.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                    return (
                      <Box key={e.id} sx={{ py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f1f5f9', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', minWidth: 90, fontSize: '0.8rem' }}>{e.order_number}</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', minWidth: 80 }}>{formatDate(e.issue_date ?? e.order_date)}</Typography>
                          <Typography variant="body2" sx={{ flex: 1, color: '#475569', fontSize: '0.8rem' }}>{exItems.length || 0} exame(s){e.professional_name && ` • Dr(a). ${e.professional_name}`}</Typography>
                          <Tooltip title={isOpen ? 'Recolher' : 'Ver exames'}>
                            <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b' }}>
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Collapse in={isOpen}>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #a7f3d0', bgcolor: '#ecfdf5', mx: -2, px: 2, pb: 1.5, borderRadius: '0 0 8px 8px' }}>
                            {exItems.length === 0 ? <Typography variant="caption" color="text.secondary">Nenhum exame.</Typography>
                              : exItems.map((it: any, idx: number) => (
                                <Box key={it.id || idx} sx={{ py: 0.5, borderBottom: idx < exItems.length - 1 ? '1px dashed #6ee7b7' : 'none' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1d4ed8', fontSize: '0.8rem' }}>{it.exam_name || 'Exame'}</Typography>
                                </Box>
                              ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  }) : <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nenhum pedido de exame</Typography>}
                </Box>
                </>
                )}

                {healthcareSummary?.recent_refraction_prescriptions?.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VisibilityIcon sx={{ color: '#0284c7', fontSize: 20 }} /> Prescrição de Refração
                        </Typography>
                        <Chip label={healthcareSummary.recent_refraction_prescriptions.length} size="small" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }} />
                      </Box>
                      {healthcareSummary.recent_refraction_prescriptions.map((r: any) => {
                        const rowKey = `ref:${r.id}`;
                        const isOpen = expandedHistoryRow === rowKey;
                        const rx = r.refraction || {};
                        return (
                          <Box key={r.id} sx={{ py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f1f5f9', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284c7', minWidth: 90, fontSize: '0.8rem' }}>{r.title || 'Refração'}</Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', minWidth: 80 }}>{formatDate(r.issue_date)}</Typography>
                              <Typography variant="body2" sx={{ flex: 1, color: '#475569', fontSize: '0.8rem' }}>
                                OD {rx.odSph || '—'} / OS {rx.osSph || '—'}{r.professional_name && ` • Dr(a). ${r.professional_name}`}
                              </Typography>
                              <Tooltip title={isOpen ? 'Recolher' : 'Ver graus'}>
                                <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b' }}>
                                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Collapse in={isOpen}>
                              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #bae6fd', bgcolor: '#f0f9ff', mx: -2, px: 2, pb: 1.5, borderRadius: '0 0 8px 8px' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#0369a1', display: 'block', mb: 0.75 }}>Para longe</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#0f172a' }}>
                                  <strong>OD</strong> Esf {rx.odSph || '—'} · Cil {rx.odCyl || '—'} · Eixo {rx.odAxis || '—'}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#0f172a', mt: 0.5 }}>
                                  <strong>OE</strong> Esf {rx.osSph || '—'} · Cil {rx.osCyl || '—'} · Eixo {rx.osAxis || '—'}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#0f172a', mt: 1 }}>
                                  <strong>Adição (AO):</strong> {rx.nearAdd || '—'}
                                </Typography>
                                {rx.notes?.trim() && (
                                  <Typography variant="caption" sx={{ display: 'block', color: '#475569', mt: 1, whiteSpace: 'pre-wrap' }}>
                                    <strong>Obs.:</strong> {rx.notes.trim()}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                {healthcareSummary?.active_treatment_plans?.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingIcon sx={{ color: '#3b82f6', fontSize: 20 }} /> Planos de Tratamento Ativos
                      </Typography>
                      {healthcareSummary.active_treatment_plans.map((tp: any) => {
                        const st = getStatusConfig(tp.status);
                        return (
                          <Box key={tp.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid rgba(59, 130, 246,0.15)', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#3b82f6', minWidth: 90, fontSize: '0.8rem' }}>{tp.plan_number || tp.title}</Typography>
                            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>{formatCurrency(tp.total_value)}</Typography>
                            <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.textColor ?? st.color, fontSize: '0.65rem', height: 22, fontWeight: 600 }} />
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                {!isFisioterapia && healthcareSummary?.recent_certificates?.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventNoteIcon sx={{ color: '#f59e0b', fontSize: 20 }} /> Atestados Recentes
                      </Typography>
                      {healthcareSummary.recent_certificates.map((c: any) => (
                        <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #fef3c7', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#d97706', minWidth: 90, fontSize: '0.8rem' }}>{formatDate(c.issue_date)}</Typography>
                          <Typography variant="body2" sx={{ flex: 1, color: '#475569', fontSize: '0.8rem' }}>{c.purpose || c.certificate_type}</Typography>
                          {c.days_off && <Chip label={`${c.days_off} dia(s)`} size="small" sx={{ bgcolor: '#fef3c7', color: '#d97706', fontSize: '0.65rem', height: 22 }} />}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
              )
            )}

            {/* ── TAB 3: EVOLUÇÕES ── */}
            {showClinicalEvolutions && activeTab === 'evolutions' && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'space-between' },
                    alignItems: 'center',
                    mb: 2,
                    gap: 1,
                  }}
                >
                  {!isMobile && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimelineIcon sx={{ color: '#3b82f6', fontSize: 20 }} /> Timeline de Evoluções
                      {timelineEvolutions.length > 0 && (
                        <Chip label={timelineEvolutions.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                      )}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 0.5, sm: 1 },
                      flexWrap: 'nowrap',
                      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                      overflowX: { xs: 'auto', sm: 'visible' },
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: 0,
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={isMobile ? undefined : <AddIcon />}
                      onClick={openNewEvolution}
                      sx={{
                        bgcolor: '#3b82f6',
                        '&:hover': { bgcolor: '#2563eb' },
                        textTransform: 'none',
                        fontSize: { xs: '0.68rem', sm: '0.75rem' },
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        px: { xs: 1, sm: 1.5 },
                        minHeight: 30,
                      }}
                    >
                      Nova evolução
                    </Button>
                    <Tooltip title="Gera um PDF com todas as evoluções do paciente (Salvar como PDF na impressão)">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!timelineEvolutions.length || exportingEvolutions}
                          startIcon={
                            isMobile
                              ? undefined
                              : exportingEvolutions
                                ? (
                                  <CircularProgress size={14} color="inherit" />
                                )
                                : (
                                  <DownloadIcon />
                                )
                          }
                          onClick={() => void handleExportPatientEvolutions()}
                          sx={{
                            borderColor: '#64748b',
                            color: '#475569',
                            textTransform: 'none',
                            fontSize: { xs: '0.68rem', sm: '0.75rem' },
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            px: { xs: 1, sm: 1.5 },
                            minHeight: 30,
                          }}
                        >
                          {exportingEvolutions && isMobile ? (
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <CircularProgress size={12} color="inherit" />
                              Exportar
                            </Box>
                          ) : (
                            isMobile ? 'Exportar' : 'Exportar evoluções'
                          )}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Registra a entrega de cópia do prontuário e coleta a assinatura do recebedor no recibo">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={isMobile ? undefined : <AssignmentTurnedInOutlined />}
                        onClick={() => setRecordDeliveryOpen(true)}
                        sx={{
                          borderColor: '#0f766e',
                          color: '#0f766e',
                          textTransform: 'none',
                          fontSize: { xs: '0.68rem', sm: '0.75rem' },
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          px: { xs: 1, sm: 1.5 },
                          minHeight: 30,
                        }}
                      >
                        Registrar entrega
                      </Button>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="outlined"
                      href="/clinic/evolutions"
                      target="_self"
                      sx={{
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        textTransform: 'none',
                        fontSize: { xs: '0.68rem', sm: '0.75rem' },
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        px: { xs: 1, sm: 1.5 },
                        minHeight: 30,
                      }}
                    >
                      Ver todas
                    </Button>
                  </Box>
                </Box>
                {evolutionsLoading && timelineEvolutions.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : !timelineEvolutions.length ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>Nenhuma evolução registrada para este paciente.</Alert>
                ) : (
                  <Box sx={{ position: 'relative', pl: 3 }}>
                    <Box sx={{ position: 'absolute', left: 10, top: 0, bottom: 0, width: 2, bgcolor: '#e2e8f0' }} />
                    {timelineEvolutions.map((evo: any, evoIndex: number) => {
                      const evoKey = String(evo.id);
                      const isEvoExpanded = expandedEvolutionIds.includes(evoKey);
                      const nextEvo = timelineEvolutions[evoIndex + 1];
                      const nextEvoKey = nextEvo?.id != null ? String(nextEvo.id) : null;
                      return (
                      <Box key={evo.id} sx={{ position: 'relative', mb: 2 }}>
                        <Box sx={{ position: 'absolute', left: -25, top: 4, width: 12, height: 12, borderRadius: '50%',
                          bgcolor: isEvolutionInvalidated(evo) ? '#dc2626' : evo.is_signed ? '#22c55e' : '#f59e0b', border: '2px solid #fff', zIndex: 1 }} />
                        <Card
                          onClick={() => toggleEvolutionExpanded(evoKey)}
                          sx={{
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            cursor: 'pointer',
                            '&:hover': { borderColor: '#3b82f6' },
                            transition: 'all 0.2s',
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              mb: 0.5,
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: { xs: 1, sm: 0 },
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1d4ed8' }}>
                                  {evo.evolution_date ? new Date(evo.evolution_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}
                                </Typography>
                                {evo.evolution_time && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{evo.evolution_time.slice(0, 5)}</Typography>}
                                {isMobile && (
                                  <Chip
                                    label={isEvolutionInvalidated(evo) ? 'Invalidada' : evo.is_signed ? 'Assinada' : 'Rascunho'}
                                    size="small"
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: 20,
                                      bgcolor: isEvolutionInvalidated(evo) ? '#fee2e2' : evo.is_signed ? '#dcfce7' : '#fef3c7',
                                      color: isEvolutionInvalidated(evo) ? '#b91c1c' : evo.is_signed ? '#16a34a' : '#d97706',
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Box>
                              <Box
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                width: { xs: '100%', sm: 'auto' },
                                overflowX: { xs: 'auto', sm: 'visible' },
                                WebkitOverflowScrolling: 'touch',
                                pb: { xs: 0.25, sm: 0 },
                                '& .MuiIconButton-root': { flexShrink: 0 },
                              }}>
                                {!isMobile && (
                                <Chip
                                  label={isEvolutionInvalidated(evo) ? 'Invalidada' : evo.is_signed ? 'Assinada' : 'Rascunho'}
                                  size="small"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    bgcolor: isEvolutionInvalidated(evo) ? '#fee2e2' : evo.is_signed ? '#dcfce7' : '#fef3c7',
                                    color: isEvolutionInvalidated(evo) ? '#b91c1c' : evo.is_signed ? '#16a34a' : '#d97706',
                                    fontWeight: 600,
                                  }}
                                />
                                )}
                                {!evo.is_signed && !isEvolutionInvalidated(evo) && (
                                  <Tooltip title="Assinar evolução">
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={signingEvolutionId === String(evo.id)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void handleSignEvolution(String(evo.id));
                                        }}
                                        sx={{ color: '#22c55e' }}
                                        aria-label="Assinar evolução"
                                      >
                                        {signingEvolutionId === String(evo.id)
                                          ? <CircularProgress size={16} sx={{ color: '#22c55e' }} />
                                          : <CheckCircleIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                {canInvalidateEvolutionRecord(evolutionViewer, evo) && (
                                  <Tooltip title={evolutionInvalidateTooltip(evolutionViewer, evo)}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInvalidateError(null);
                                          setInvalidateTarget(evo);
                                        }}
                                        sx={{ color: '#b45309' }}
                                        aria-label="Invalidar evolução"
                                      >
                                        <BlockIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                <Tooltip title="Imprimir apenas o texto da evolução (sem receituários, solicitações ou indicações)">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={printingEvolutionId === String(evo.id)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void handlePrintEvolutionText(evo);
                                      }}
                                      sx={{ color: '#0f766e' }}
                                      aria-label="Imprimir texto da evolução"
                                    >
                                      {printingEvolutionId === String(evo.id)
                                        ? <CircularProgress size={16} sx={{ color: '#0f766e' }} />
                                        : <PrintIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {isEvolutionSigned(evo) && !isEvolutionInvalidated(evo) && (
                                  <Tooltip title="Informações adicionadas">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEvolutionAdditionsTarget(evo);
                                      }}
                                      sx={{ color: '#2563eb' }}
                                      aria-label="Informações adicionadas"
                                    >
                                      <PostAddIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {!isEvolutionInvalidated(evo) && (
                                <Tooltip title={
                                  canEditEvolutionRecord(evolutionViewer, evo)
                                    ? evolutionEditTooltip(evolutionViewer, evo)
                                    : 'Visualizar evolução (edição bloqueada)'
                                }>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEvolutionExistingId(String(evo.id));
                                      setEvolutionDialogOpen(true);
                                    }}
                                    sx={{ color: '#3b82f6' }}
                                    aria-label="Editar evolução"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                )}
                                {canDeleteEvolutionRecord(user?.role, evo) && (
                                  <Tooltip title={evolutionDeleteTooltip(user?.role, evo)}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={deletingEvolutionId === String(evo.id)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void handleDeleteEvolution(String(evo.id));
                                        }}
                                        sx={{ color: '#dc2626' }}
                                        aria-label="Excluir evolução"
                                      >
                                        {deletingEvolutionId === String(evo.id)
                                          ? <CircularProgress size={16} sx={{ color: '#dc2626' }} />
                                          : <DeleteIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                {!isMobile && (
                                  <Tooltip title={isEvoExpanded ? 'Recolher' : 'Expandir evolução'}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleEvolutionExpanded(evoKey);
                                      }}
                                      sx={{ color: '#64748b' }}
                                      aria-expanded={isEvoExpanded}
                                      aria-label={isEvoExpanded ? 'Recolher evolução' : 'Expandir evolução'}
                                    >
                                      {isEvoExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                              {evo.professional?.full_name || '-'}
                            </Typography>
                            {(evo.diagnosis || evo.diagnosis_code) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                {evo.diagnosis_code && <Chip label={evo.diagnosis_code} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 700 }} />}
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                                  {formatDiagnosisPair(evo.diagnosis, evo.diagnosis_code) || evo.diagnosis}
                                </Typography>
                              </Box>
                            )}
                            {(() => {
                              const planExecs = parseEvolutionPlanExecutions(evo.procedures_performed);
                              const nProc = normalizeEvolutionProcedureLabels(evo).length;
                              const indicationBody = String(getEvolutionIndicationAttachment(evo)?.bodyText || '').trim();
                              const nMat = isFisioterapia ? 0 : getEvolutionNonIndicationPrintables(evo).length;
                              const hasRet = evolutionViewerHasFollowUpSection(evo);
                              const nAdd = Array.isArray(evo.additions) ? evo.additions.length : 0;
                              if (!planExecs.length && !nProc && !indicationBody && !nMat && !hasRet && !nAdd) return null;
                              const bits: string[] = [];
                              if (planExecs.length) bits.push(`${planExecs.length} procedimento(s) executado(s)`);
                              if (nProc || indicationBody) bits.push(`${nProc || (indicationBody ? 1 : 0)} indicação(ões)`);
                              if (nMat) bits.push(`${nMat} impresso(s)`);
                              if (hasRet) bits.push('retorno');
                              if (nAdd) bits.push(`${nAdd} info. adicionada(s)`);
                              return (
                                <Typography variant="caption" sx={{ color: '#3b82f6', display: 'block', mb: 0.35, fontWeight: 600 }}>
                                  {bits.join(' · ')}
                                </Typography>
                              );
                            })()}
                            {resolveEvolutionEntryFormat(evo) === 'free_text' ? (
                              evo.evolution_free_text && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#64748b',
                                    overflow: 'hidden',
                                    maxWidth: isMobile ? '100%' : 400,
                                    ...(isMobile && !isEvoExpanded
                                      ? {
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          whiteSpace: 'normal',
                                        }
                                      : {
                                          display: 'block',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }),
                                  }}
                                >
                                  <strong>Texto livre:</strong>{' '}
                                  {clinicalHtmlToPlainPreview(evo.evolution_free_text, 300)}
                                </Typography>
                              )
                            ) : evo.subjective ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#64748b',
                                  overflow: 'hidden',
                                  maxWidth: isMobile ? '100%' : 400,
                                  ...(isMobile && !isEvoExpanded
                                    ? {
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        whiteSpace: 'normal',
                                      }
                                    : {
                                        display: 'block',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }),
                                }}
                              >
                                <strong>S:</strong> {evo.subjective}
                              </Typography>
                            ) : null}
                            {isMobile && (
                              <Button
                                fullWidth
                                size="small"
                                variant={isEvoExpanded ? 'text' : 'outlined'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleEvolutionExpanded(evoKey);
                                }}
                                endIcon={isEvoExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{
                                  mt: 1,
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                  ...(isEvoExpanded
                                    ? { color: '#64748b', justifyContent: 'center' }
                                    : { borderColor: '#93c5fd', color: '#1d4ed8', bgcolor: '#eff6ff' }),
                                }}
                                aria-expanded={isEvoExpanded}
                              >
                                {isEvoExpanded ? 'Recolher evolução' : 'Ver evolução completa'}
                              </Button>
                            )}
                            <Collapse in={isEvoExpanded} timeout="auto" unmountOnExit>
                              <Box onClick={(e) => e.stopPropagation()}>
                              <Divider sx={{ my: 1.25 }} />
                              <EvolutionInvalidationBanner evolution={evo} sx={{ mb: 1.25, py: 0.5 }} />
                              {resolveEvolutionEntryFormat(evo) === 'free_text' ? (
                                evo.evolution_free_text && (
                                  <Box sx={{ mb: 0.5 }}>
                                    <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 0.35 }}>
                                      Texto livre:
                                    </Typography>
                                    <ClinicalHtmlField
                                      html={evo.evolution_free_text}
                                      sx={{ fontSize: '0.75rem', color: '#475569', display: 'block' }}
                                    />
                                  </Box>
                                )
                              ) : (
                                <>
                                  {evo.objective && <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5, whiteSpace: 'pre-wrap' }}><strong>O:</strong> {evo.objective}</Typography>}
                                  {evo.assessment && <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5, whiteSpace: 'pre-wrap' }}><strong>A:</strong> {evo.assessment}</Typography>}
                                  {evo.plan && <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5, whiteSpace: 'pre-wrap' }}><strong>P:</strong> {evo.plan}</Typography>}
                                </>
                              )}
                              {parseEvolutionPlanExecutions(evo.procedures_performed).length > 0 && (
                                <Box sx={{ mb: 0.85 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f766e', display: 'block', mb: 0.25 }}>
                                    Procedimentos executados:
                                  </Typography>
                                  <Stack spacing={0.35}>
                                    {parseEvolutionPlanExecutions(evo.procedures_performed).map((rec) => (
                                      <Typography key={rec.procedure_execution_id} variant="caption" sx={{ color: '#065f46', display: 'block' }}>
                                        • {formatEvolutionPlanExecutionSummaryLine(rec)}
                                      </Typography>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                              {(normalizeEvolutionProcedureLabels(evo).length > 0 ||
                                String(getEvolutionIndicationAttachment(evo)?.bodyText || '').trim()) && (
                                <Box sx={{ mb: 0.85 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#3730a3', display: 'block', mb: 0.25 }}>
                                    Indicações:
                                  </Typography>
                                  {normalizeEvolutionProcedureLabels(evo).length > 0 ? (
                                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', whiteSpace: 'pre-wrap', mb: 0.5 }}>
                                      {normalizeEvolutionProcedureLabels(evo).join(' · ')}
                                    </Typography>
                                  ) : null}
                                  {String(getEvolutionIndicationAttachment(evo)?.bodyText || '').trim() ? (
                                    <Typography
                                      component="pre"
                                      variant="caption"
                                      sx={{
                                        color: '#475569',
                                        display: 'block',
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'ui-monospace, monospace',
                                        m: 0,
                                      }}
                                    >
                                      {getEvolutionIndicationAttachment(evo)?.bodyText}
                                    </Typography>
                                  ) : null}
                                </Box>
                              )}
                              {!isFisioterapia && getEvolutionNonIndicationPrintables(evo).length > 0 && (
                                <Box sx={{ mb: 0.85 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a', display: 'block', mb: 0.25 }}>
                                    Impressos / solicitações:
                                  </Typography>
                                  <Stack spacing={0.35}>
                                    {getEvolutionNonIndicationPrintables(evo).map((p) => (
                                      <Typography key={p.clientKey} variant="caption" sx={{ color: '#475569' }}>
                                        • {evolutionPrintableListLabel(p)}
                                      </Typography>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                              {evolutionViewerHasFollowUpSection(evo) && (
                                <Box sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#b45309', display: 'block', mb: 0.25 }}>
                                    Retorno:
                                  </Typography>
                                  {formatEvolutionReturnWorkflowLines(getEvolutionReturnWorkflow(evo)).map((line) => (
                                    <Typography key={line} variant="caption" sx={{ color: '#475569', display: 'block' }}>
                                      • {line}
                                    </Typography>
                                  ))}
                                  {evo.follow_up_notes && (
                                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', whiteSpace: 'pre-wrap', mt: 0.35 }}>
                                      {evo.follow_up_notes}
                                    </Typography>
                                  )}
                                  {evo.follow_up_date && (
                                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.25 }}>
                                      Data:{' '}
                                      {new Date(evo.follow_up_date + 'T12:00:00').toLocaleDateString('pt-BR')}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {isEvoExpanded && isEvolutionSigned(evo) && !isEvolutionInvalidated(evo) ? (
                                <EvolutionAdditionsSection
                                  evolutionId={evoKey}
                                  evolutionSnapshot={evo}
                                  patientId={patientId || patient?.id}
                                  viewer={evolutionViewer}
                                  professionalId={user?.professional_id || evo.professional_id || null}
                                  compact
                                />
                              ) : (
                                <EvolutionAdditionsDisplay
                                  evolutionId={evoKey}
                                  active={isEvoExpanded}
                                  initialItems={evo.additions}
                                  compact
                                />
                              )}
                              <EvolutionTimelineScrollSentinel
                                enabled={isEvoExpanded}
                                nextEvolutionId={nextEvoKey}
                                onReachEnd={expandEvolutionById}
                              />
                              </Box>
                            </Collapse>
                          </CardContent>
                        </Card>
                      </Box>
                    );})}
                  </Box>
                )}
              </Box>
            )}

            {/* ── TAB: AVALIAÇÃO FISIOTERAPÊUTICA ── */}
            {activeTab === 'assessment' && isFisioterapia && (
              <PatientPhysiotherapyAssessmentTab
                patientId={patientId}
                patient={patient}
                linkedProfessionalId={linkedProfessionalId}
                linkedAppointmentId={linkedAppointmentId}
              />
            )}

            {/* ── TAB: ODONTOGRAMA (mapa + periograma) ── */}
            {activeTab === 'odontogram' && isOdontologia && (
              <PatientOdontogramTab
                patientId={patientId}
                unitId={user?.current_unit?.id}
                linkedProfessionalId={linkedProfessionalId}
                section={odontogramSection}
                onSectionChange={setOdontogramSection}
              />
            )}

            {/* ── TAB: TRATAMENTOS ODONTOLÓGICOS ── */}
            {activeTab === 'dental_treatments' && isOdontologia && (
              <PatientDentalTreatmentsTab
                patientId={patientId}
                unitId={user?.current_unit?.id}
                linkedProfessionalId={linkedProfessionalId}
              />
            )}

            <MedicalRecordDeliveryDialog
              open={recordDeliveryOpen}
              onClose={() => setRecordDeliveryOpen(false)}
              patient={{
                id: patientId,
                full_name: patient.full_name,
                cpf: patient.cpf,
                phone: patient.phone || patient.whatsapp,
              }}
              defaultContentTypes={['evolutions']}
            />

            {/* ── TAB: RELATÓRIOS MÉDICOS ── */}
            {!isFisioterapia && activeTab === 'medical_reports' && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReportIcon sx={{ color: '#2563eb', fontSize: 20 }} /> Relatórios Médicos
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" startIcon={<AddIcon />}
                      onClick={() => {
                        setMedicalReportExistingId(null);
                        setMedicalReportDialogOpen(true);
                      }}
                      sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', fontSize: '0.75rem' }}>
                      Novo relatório
                    </Button>
                    <Button size="small" variant="outlined" href="/clinic/medical-reports" target="_self"
                      sx={{ borderColor: '#2563eb', color: '#2563eb', textTransform: 'none', fontSize: '0.75rem' }}>
                      Ver Todos
                    </Button>
                  </Box>
                </Box>
                {!healthcareSummary?.recent_medical_reports?.length ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>Nenhum relatório médico registrado para este paciente.</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {healthcareSummary.recent_medical_reports.map((report: any) => {
                      const invalidated = isEvolutionInvalidated(report);
                      const signed = isSignedMedicalReport(report);
                      const isExpanded = expandedMedicalReportId === report.id;
                      const bodyPreview = clinicalHtmlToPlainPreview(report.body_text, 300);
                      return (
                        <Card key={report.id} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', '&:hover': { borderColor: '#2563eb' }, transition: 'all 0.2s' }}>
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1d4ed8' }}>
                                  {report.title || 'Relatório Médico'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                  {report.issue_date ? new Date(report.issue_date + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                                  {report.report_number ? ` · ${report.report_number}` : ''}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                  {report.professional?.full_name || '—'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                <Chip
                                  label={invalidated ? 'Invalidado' : signed ? 'Assinado' : 'Rascunho'}
                                  size="small"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    bgcolor: invalidated ? '#fee2e2' : signed ? '#dcfce7' : '#fef3c7',
                                    color: invalidated ? '#b91c1c' : signed ? '#16a34a' : '#d97706',
                                    fontWeight: 600,
                                  }}
                                />
                                {signed && (
                                  <Tooltip title="Assinado digitalmente (ICP-Brasil)">
                                    <SignIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                                  </Tooltip>
                                )}
                                <Tooltip title="Abrir relatório">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMedicalReportExistingId(String(report.id));
                                      setMedicalReportDialogOpen(true);
                                    }}
                                    sx={{ color: '#2563eb' }}
                                    aria-label="Abrir relatório"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  onClick={() => setExpandedMedicalReportId((prev) => (prev === report.id ? null : report.id))}
                                  sx={{ color: '#64748b' }}
                                  aria-label={isExpanded ? 'Recolher relatório' : 'Expandir relatório'}
                                >
                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </Box>
                            </Box>
                            {report.diagnosis && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                {report.diagnosis_code && (
                                  <Chip
                                    label={report.diagnosis_code}
                                    size="small"
                                    sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 700 }}
                                  />
                                )}
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                                  {report.diagnosis}
                                </Typography>
                              </Box>
                            )}
                            {bodyPreview && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#64748b',
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100%',
                                }}
                              >
                                {bodyPreview}
                              </Typography>
                            )}
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Divider sx={{ my: 1.25 }} />
                              <EvolutionInvalidationBanner evolution={report} sx={{ mb: 1.25, py: 0.5 }} />
                              {report.body_text?.trim() ? (
                                <Box sx={{ mb: 0.5 }}>
                                  <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 0.35 }}>
                                    Conteúdo:
                                  </Typography>
                                  <ClinicalHtmlField
                                    html={report.body_text}
                                    sx={{ fontSize: '0.75rem', color: '#475569', display: 'block' }}
                                  />
                                </Box>
                              ) : (
                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                                  Sem conteúdo registrado.
                                </Typography>
                              )}
                            </Collapse>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {/* ── TAB 4: FINANCEIRO ── */}
            {activeTab === 'financial' && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <PatientPaidSessionsCard
                  patientId={patientId}
                  unitId={user?.current_unit?.id}
                  compact={isAgendaEmbedded}
                  onSessionsChanged={() => {
                    setPaidSessionsReloadToken((v) => v + 1);
                    void fetchSummary({ silent: true });
                  }}
                />
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #d1fae5', bgcolor: '#f0fdf4', textAlign: 'center' }}>
                      <CardContent sx={{ py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Total Pago</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#059669' }}>{formatCurrency(healthcareSummary?.financial?.total_paid || 0)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #fef3c7', bgcolor: '#fffbeb', textAlign: 'center' }}>
                      <CardContent sx={{ py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Pendente</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#d97706' }}>{formatCurrency(healthcareSummary?.financial?.total_pending || 0)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #fee2e2', bgcolor: '#fef2f2', textAlign: 'center' }}>
                      <CardContent sx={{ py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Em Atraso</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#dc2626' }}>{formatCurrency(healthcareSummary?.financial?.total_overdue || 0)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card sx={{ borderRadius: 2, border: '1px solid rgba(59, 130, 246,0.2)', mb: 2, bgcolor: 'rgba(59, 130, 246,0.04)' }}>
                  <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 }, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700, textTransform: 'uppercase' }}>Total Investido pelo Paciente</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#3b82f6', mt: 0.5 }}>{formatCurrency(profileTotalInvested)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      em {profileConsultCount} consulta(s){patient.first_visit_date && ` • desde ${formatDate(patient.first_visit_date)}`}
                    </Typography>
                  </CardContent>
                </Card>

                {pendingApprovalBudgets.length > 0 && (
                  <Card sx={{ borderRadius: 2, border: '1px solid #fde68a', bgcolor: '#fffbeb', mb: 2 }}>
                    <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <DocIcon sx={{ fontSize: 18, color: '#d97706' }} />
                        Orçamentos aguardando aprovação
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#a16207', display: 'block', mb: 1.25 }}>
                        As contas a receber e o plano de tratamento só são gerados depois da aprovação.
                      </Typography>
                      <Stack spacing={1}>
                        {pendingApprovalBudgets.map((b) => {
                          const busy = budgetDecisionId === b.id;
                          return (
                            <Box
                              key={b.id}
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
                                p: 1.25, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #fde68a',
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                  {b.budget_number} · {formatCurrency(Number(b.total) || 0)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(b.issue_date)}
                                  {b.professional_name ? ` · ${b.professional_name}` : ''}
                                  {Array.isArray(b.items) && b.items.length > 0 ? ` · ${b.items.length} procedimento(s)` : ''}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.75 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={busy}
                                  startIcon={<BlockIcon />}
                                  onClick={() => {
                                    const reason = window.prompt('Motivo da rejeição (opcional):');
                                    if (reason !== null) void decideBudget(b, 'rejected', reason || undefined);
                                  }}
                                  sx={{ textTransform: 'none', fontWeight: 600, color: '#dc2626', borderColor: '#fecaca' }}
                                >
                                  Rejeitar
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={busy}
                                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                                  onClick={() => { void decideBudget(b, 'approved'); }}
                                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                                >
                                  Aprovar
                                </Button>
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Contas a receber do paciente */}
                <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <MoneyIcon sx={{ fontSize: 18, color: '#10b981' }} />
                        Contas a Receber do Paciente
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        <Tooltip title="Atualizar">
                          <IconButton size="small" onClick={loadReceivables} disabled={receivablesLoading} sx={{ color: '#10b981' }}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {asaasConnected && (
                          <Button size="small" variant="outlined" startIcon={<QrCode2Icon />} onClick={() => setAsaasPickOpen(true)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}>
                            Cobrar via Asaas
                          </Button>
                        )}
                        <Button size="small" variant="contained" startIcon={<DocIcon />} onClick={openNewBudget}
                          sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', fontWeight: 600 }}>
                          Novo Orçamento
                        </Button>
                      </Box>
                    </Box>

                    {receivablesLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={22} sx={{ color: '#10b981' }} />
                      </Box>
                    ) : receivables.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #e2e8f0' }}>
                        <Typography variant="body2" color="text.secondary">Nenhuma conta a receber para este paciente.</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Crie um orçamento para lançar procedimentos, aprovar o tratamento e gerar as contas a receber automaticamente.
                        </Typography>
                      </Box>
                    ) : isMobile ? (
                      <Stack spacing={1.25}>
                        {receivables.map((rec: any) => {
                          const st = getReceivableStatusInfo(rec.status);
                          const isPaid = isReceivablePaidStatus(rec.status);
                          const awaitingInsuranceSettlement = isAwaitingInsuranceSettlement(rec);
                          const remaining = Math.max(0, (Number(rec.amount) || 0) - (Number(rec.amount_paid || rec.paid_amount) || 0));
                          const isBudget = isBudgetTransaction(rec);
                          const budgetDetail = budgetDetailsByAccountId[rec.id];
                          const loadingBudget = budgetLoadingAccountId === rec.id;
                          const paymentDate = resolveReceivablePaymentDate(rec);
                          const linkedNfse = nfseByReceivableId[rec.id];
                          const actionCtx = { isPaid, awaitingInsuranceSettlement, remaining, linkedNfse };
                          return (
                            <Paper
                              key={rec.id}
                              variant="outlined"
                              sx={{ borderRadius: 2, borderColor: '#e2e8f0', overflow: 'hidden' }}
                            >
                              <Box sx={{ p: 1.25, bgcolor: '#fafbfc', borderBottom: '1px solid #f1f5f9' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>
                                      {rec.description}
                                    </Typography>
                                    {rec.payment_method === 'convenio' && (
                                      <Chip
                                        label={rec.insurance_name || rec.clinic_insurances?.name || 'Convênio'}
                                        size="small"
                                        sx={{ mt: 0.5, height: 22, fontSize: '0.68rem', bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 700 }}
                                      />
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.35, flexShrink: 0 }}>
                                    <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700, fontSize: '0.65rem', height: 22 }} />
                                    <AsaasReceivableChargeChip receivable={rec} />
                                  </Box>
                                </Box>
                                {isBudget ? (
                                  <Box sx={{ mt: 0.75 }}>
                                    <BudgetItemsInlineList
                                      loading={loadingBudget}
                                      budgetDetail={budgetDetail}
                                      fallbackTotal={Number(rec.budget_total ?? rec.amount) || 0}
                                      formatCurrency={formatCurrency}
                                    />
                                  </Box>
                                ) : rec.category ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {RECEIVABLE_CATEGORIES.find((c) => c.value === rec.category)?.label || rec.category}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr',
                                  gap: 1,
                                  p: 1.25,
                                  fontSize: '0.8rem',
                                }}
                              >
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                    Valor
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(rec.amount)}</Typography>
                                  {(rec.amount_paid || rec.paid_amount) > 0 && (
                                    <Typography variant="caption" sx={{ color: '#059669', display: 'block' }}>
                                      Pago: {formatCurrency(rec.amount_paid || rec.paid_amount || 0)}
                                    </Typography>
                                  )}
                                  {rec.status === 'partial' && remaining > 0 && (
                                    <Typography variant="caption" sx={{ color: '#d97706', display: 'block', fontWeight: 600 }}>
                                      Saldo: {formatCurrency(remaining)}
                                    </Typography>
                                  )}
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                    Vencimento
                                  </Typography>
                                  <Typography variant="body2">{formatDate(rec.due_date)}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                    Pago em
                                  </Typography>
                                  <Typography variant="body2" sx={paymentDate ? { color: '#059669', fontWeight: 600 } : undefined}>
                                    {formatDate(paymentDate || undefined) || '—'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                    Pagamento
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
                                    {getPaymentMethodLabel(rec.payment_method)}
                                    {isCardInstallmentPaymentMethod(rec.payment_method)
                                      && parseCardInstallments(rec.installments) > 1
                                      && ` · ${parseCardInstallments(rec.installments)}x`}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ px: 1, py: 0.75, borderTop: '1px solid #f1f5f9', bgcolor: '#fff' }}>
                                {renderReceivableActionButtons(rec, actionCtx)}
                              </Box>
                            </Paper>
                          );
                        })}
                      </Stack>
                    ) : (
                      <TableContainer
                        className="patient-panel-table-scroll"
                        sx={{
                          width: '100%',
                          maxWidth: '100%',
                          overflowX: 'auto',
                          WebkitOverflowScrolling: 'touch',
                          overscrollBehaviorX: 'contain',
                          touchAction: 'pan-x pan-y',
                        }}
                      >
                        <Table size="small" sx={{ minWidth: 640 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Pago em</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Pagto</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {receivables.map((rec: any) => {
                              const st = getReceivableStatusInfo(rec.status);
                              const isPaid = isReceivablePaidStatus(rec.status);
                              const awaitingInsuranceSettlement = isAwaitingInsuranceSettlement(rec);
                              const remaining = Math.max(0, (Number(rec.amount) || 0) - (Number(rec.amount_paid || rec.paid_amount) || 0));
                              const isBudget = isBudgetTransaction(rec);
                              const budgetDetail = budgetDetailsByAccountId[rec.id];
                              const loadingBudget = budgetLoadingAccountId === rec.id;
                              const paymentDate = resolveReceivablePaymentDate(rec);
                              const linkedNfse = nfseByReceivableId[rec.id];
                              return (
                                <TableRow key={rec.id} hover>
                                    <TableCell>
                                      <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{rec.description}</Typography>
                                          {rec.payment_method === 'convenio' && (
                                            <Chip
                                              label={rec.insurance_name || rec.clinic_insurances?.name || 'Convênio'}
                                              size="small"
                                              sx={{ height: 22, fontSize: '0.68rem', bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 700 }}
                                            />
                                          )}
                                        </Box>
                                        {isBudget ? (
                                          <BudgetItemsInlineList
                                            loading={loadingBudget}
                                            budgetDetail={budgetDetail}
                                            fallbackTotal={Number(rec.budget_total ?? rec.amount) || 0}
                                            formatCurrency={formatCurrency}
                                          />
                                        ) : rec.category ? (
                                          <Typography variant="caption" color="text.secondary">
                                            {RECEIVABLE_CATEGORIES.find(c => c.value === rec.category)?.label || rec.category}
                                          </Typography>
                                        ) : null}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(rec.amount)}</Typography>
                                      {(rec.amount_paid || rec.paid_amount) > 0 && (
                                        <Typography variant="caption" sx={{ color: '#059669', display: 'block' }}>
                                          Pago: {formatCurrency(rec.amount_paid || rec.paid_amount || 0)}
                                        </Typography>
                                      )}
                                      {rec.status === 'partial' && remaining > 0 && (
                                        <Typography variant="caption" sx={{ color: '#d97706', display: 'block', fontWeight: 600 }}>
                                          Saldo: {formatCurrency(remaining)}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{formatDate(rec.due_date)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        sx={paymentDate ? { color: '#059669', fontWeight: 600 } : undefined}
                                      >
                                        {formatDate(paymentDate || undefined)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {getPaymentMethodLabel(rec.payment_method)}
                                        {isCardInstallmentPaymentMethod(rec.payment_method)
                                          && parseCardInstallments(rec.installments) > 1
                                          && ` · ${parseCardInstallments(rec.installments)}x`}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                                      <AsaasReceivableChargeChip receivable={rec} />
                                    </TableCell>
                                    <TableCell align="right">
                                      {renderReceivableActionButtons(rec, {
                                        isPaid,
                                        awaitingInsuranceSettlement,
                                        remaining,
                                        linkedNfse,
                                      })}
                                    </TableCell>
                                  </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* ── TAB: DOCS (contratos + anexos) ── */}
            {activeTab === 'docs' && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderDocIcon sx={{ color: '#2563eb' }} /> Documentos
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, maxWidth: 560 }}>
                    Arquivos anexados ao prontuário e contratos para assinatura digital, em um só lugar.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      Arquivos anexados
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      PDFs, exames, identidade, carteirinha e outros documentos enviados manualmente.
                    </Typography>
                  </Box>
                  <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setShowDocUpload(true)}
                    sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', borderRadius: 2 }}>
                    Anexar arquivo
                  </Button>
                </Box>

                {showDocUpload && (
                  <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid rgba(37,99,235,0.2)', bgcolor: 'rgba(37,99,235,0.03)' }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2563eb', mb: 2 }}>Novo Documento</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Tipo de Documento</InputLabel>
                            <Select value={docUploadForm.document_type} label="Tipo de Documento"
                              onChange={e => setDocUploadForm(p => ({ ...p, document_type: e.target.value }))}>
                              <MenuItem value="contract">Contrato</MenuItem>
                              <MenuItem value="consent_form">Termo de Consentimento</MenuItem>
                              <MenuItem value="id_document">Documento de Identidade</MenuItem>
                              <MenuItem value="insurance_card">Carteirinha de Plano</MenuItem>
                              <MenuItem value="exam_result">Resultado de Exame</MenuItem>
                              <MenuItem value="exam">Exame</MenuItem>
                              <MenuItem value="xray">Raio-X / Imagem Clínica</MenuItem>
                              <MenuItem value="other">Outro</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" label="Descrição (opcional)" value={docUploadForm.description}
                            onChange={e => setDocUploadForm(p => ({ ...p, description: e.target.value }))} />
                        </Grid>
                      </Grid>
                      <input type="file" ref={docFileInputRef} style={{ display: 'none' }} multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt,.xls,.xlsx"
                        onChange={e => {
                          addDocFiles(Array.from(e.target.files || []));
                          e.target.value = '';
                        }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={() => docFileInputRef.current?.click()}
                            sx={{ borderColor: '#2563eb', color: '#2563eb', textTransform: 'none', borderRadius: 2 }}>
                            {docUploadItems.length ? `Adicionar arquivos (${docUploadItems.length})` : 'Selecionar arquivo(s)'}
                          </Button>
                          {docUploadItems.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {(docUploadItems.reduce((sum, item) => sum + item.file.size, 0) / 1024).toFixed(0)} KB total
                            </Typography>
                          )}
                        </Box>
                        {docUploadItems.length > 0 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                              Defina o título de cada documento antes de salvar
                            </Typography>
                            {docUploadItems.map((item) => (
                              <Box key={item.key}
                                sx={{ px: 1.25, py: 1, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                  <FileIcon sx={{ fontSize: 18, color: '#64748b', mt: 0.25 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {item.file.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">{(item.file.size / 1024).toFixed(0)} KB</Typography>
                                  </Box>
                                  <IconButton size="small" onClick={() => removeDocUploadItem(item.key)} aria-label="Remover arquivo">
                                    <DeleteIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                  </IconButton>
                                </Box>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Título"
                                  value={item.title}
                                  onChange={(e) => updateDocUploadTitle(item.key, e.target.value)}
                                  placeholder={defaultDocUploadTitle(item.file)}
                                />
                              </Box>
                            ))}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Box sx={{ flex: 1 }} />
                          <Button onClick={resetDocUpload} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
                          <Button variant="contained" disabled={docUploadItems.length === 0 || docUploading} onClick={handleDocUpload}
                            sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', borderRadius: 2 }}>
                            {docUploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : docUploadItems.length > 1 ? `Salvar ${docUploadItems.length} arquivos` : 'Salvar'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {docsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#3b82f6' }} /></Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', borderRadius: 3, border: '2px dashed #e2e8f0' }}>
                    <FolderDocIcon sx={{ fontSize: 56, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>Nenhum documento anexado</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {documents.map((doc: any) => (
                      <Card key={doc.id} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <PatientDocumentThumbnail
                            doc={doc}
                            size={52}
                            accentColor={getDocTypeColor(doc.document_type)}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.title}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                              <Chip label={getDocTypeLabel(doc.document_type)} size="small"
                                sx={{ bgcolor: `${getDocTypeColor(doc.document_type)}15`, color: getDocTypeColor(doc.document_type), fontSize: '0.65rem', height: 20, fontWeight: 600 }} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {formatPatientDocumentDate(doc.created_at)}
                              </Typography>
                            </Box>
                          </Box>
                          {onSendMediaToChat && doc.file_url && (
                            <Tooltip title="Enviar no chat">
                              <IconButton
                                size="small"
                                onClick={() => onSendMediaToChat(doc.file_url, 'document', doc.title)}
                                sx={{ color: '#25D366' }}
                              >
                                <SendIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <PatientDocumentActions
                            doc={doc}
                            patientId={patientId}
                            onUpdated={(updated) =>
                              setDocuments((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)))
                            }
                            onDeleted={(docId) => setDocuments((prev) => prev.filter((d) => d.id !== docId))}
                            onNotify={setSnackMsg}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}

                <Card
                  variant="outlined"
                  sx={{
                    mt: 3,
                    borderRadius: 2.5,
                    borderColor: '#99f6e4',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: '#f0fdfa',
                      borderBottom: '1px solid #ccfbf1',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                    }}
                  >
                    <ContractIcon sx={{ color: '#0f766e', fontSize: 22, mt: 0.25 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>
                        Contratos e assinaturas
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.45 }}>
                        Envie termos, colete assinaturas no dispositivo e registre entrega de prontuário.
                      </Typography>
                    </Box>
                  </Box>
                  <PatientContractsTab
                    embedded
                    patientId={patientId}
                    patientName={patient.full_name}
                    patientCpf={patient.cpf}
                    patientPhone={patient.phone || patient.whatsapp}
                  />
                </Card>
              </Box>
            )}

            {/* ── TAB: IMAGENS (+ iDoc quando habilitado) ── */}
            {activeTab === 'images' && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                {imageSourcePickerNodes}
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon sx={{ color: '#3b82f6' }} /> Imagens
                  </Typography>
                  {idocEnabled && (
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                      Banco clínico da clínica e radiologia iDoc (Radio Memory), quando conectado.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Banco de imagens clínicas
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={`${patientImages.length} imagem(ns)`} size="small" sx={{ bgcolor: 'rgba(59, 130, 246,0.1)', color: '#3b82f6', fontWeight: 600 }} />
                    <Button
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      onClick={openImageSourceChooser}
                      sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', borderRadius: 2 }}
                    >
                      Anexar Imagem
                    </Button>
                  </Box>
                </Box>

                {showImageUpload && (
                  <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid rgba(59,130,246,0.2)', bgcolor: 'rgba(59,130,246,0.03)' }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3b82f6', mb: 2 }}>Nova Imagem</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small"
                            label={imageFiles.length > 1 ? 'Título (apenas 1 arquivo)' : 'Título'}
                            value={imageUploadForm.title}
                            disabled={imageFiles.length > 1}
                            helperText={imageFiles.length > 1 ? 'Com várias imagens, cada uma usa o nome do arquivo como título.' : undefined}
                            onChange={(e) => setImageUploadForm((p) => ({ ...p, title: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Tipo de Imagem</InputLabel>
                            <Select value={imageUploadForm.image_type} label="Tipo de Imagem"
                              onChange={(e) => setImageUploadForm((p) => ({ ...p, image_type: e.target.value }))}>
                              <MenuItem value="clinical_photo">Foto Clínica</MenuItem>
                              <MenuItem value="intraoral">Intraoral</MenuItem>
                              <MenuItem value="extraoral">Extraoral</MenuItem>
                              <MenuItem value="xray">Radiografia</MenuItem>
                              <MenuItem value="panoramic">Panorâmica</MenuItem>
                              <MenuItem value="periapical">Periapical</MenuItem>
                              <MenuItem value="before_after">Antes/Depois</MenuItem>
                              <MenuItem value="other">Outro</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth size="small" label="Descrição (opcional)" value={imageUploadForm.description}
                            onChange={(e) => setImageUploadForm((p) => ({ ...p, description: e.target.value }))} />
                        </Grid>
                      </Grid>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            startIcon={<AttachFileIcon />}
                            onClick={openImageSourceChooser}
                            sx={{ borderColor: '#3b82f6', color: '#3b82f6', textTransform: 'none', borderRadius: 2 }}
                          >
                            {imageFiles.length ? `Adicionar imagens (${imageFiles.length})` : 'Selecionar imagem(ns)'}
                          </Button>
                          {imageFiles.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {(imageFiles.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(0)} KB total
                            </Typography>
                          )}
                        </Box>
                        {imageFiles.length > 0 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {imageFiles.map((file, idx) => (
                              <Box key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                <ImageIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{(file.size / 1024).toFixed(0)} KB</Typography>
                                </Box>
                                <IconButton size="small" onClick={() => setImageFiles((prev) => prev.filter((_, i) => i !== idx))} aria-label="Remover imagem">
                                  <DeleteIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Box sx={{ flex: 1 }} />
                          <Button onClick={resetImageUpload} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
                          <Button variant="contained" disabled={imageFiles.length === 0 || imageUploading} onClick={handleImageUpload}
                            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', borderRadius: 2 }}>
                            {imageUploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : imageFiles.length > 1 ? `Salvar ${imageFiles.length} imagens` : 'Salvar'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {imagesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#3b82f6' }} /></Box>
                ) : patientImages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', borderRadius: 3, border: '2px dashed #e2e8f0' }}>
                    <ImageIcon sx={{ fontSize: 56, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>Nenhuma imagem clínica</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Clique em &quot;Anexar Imagem&quot; para adicionar fotos e radiografias do paciente
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {patientImages.map((img: any) => (
                      <Grid item xs={6} sm={4} md={3} key={img.id}>
                        <Card sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderColor: '#3b82f6' } }}>
                          <Box sx={{ position: 'relative', paddingTop: '75%', bgcolor: '#f8fafc', cursor: 'pointer' }}
                            onClick={() => setExpandedImage(img.file_url)}>
                            <img src={img.file_url} alt={img.title} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                            <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                              {img.is_favorite && <StarIcon sx={{ fontSize: 16, color: '#f59e0b', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />}
                              <ZoomInIcon sx={{ fontSize: 16, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' }} />
                            </Box>
                          </Box>
                          <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{img.title}</Typography>
                            <Tooltip title="Baixar">
                              <IconButton size="small" onClick={() => downloadPatientImage(img)} sx={{ color: '#2563eb' }}>
                                <DownloadIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            {onSendMediaToChat && (
                              <Tooltip title="Enviar no chat">
                                <IconButton size="small" onClick={() => onSendMediaToChat(img.file_url, 'image', img.title)} sx={{ color: '#25D366' }}>
                                  <SendIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Excluir">
                              <IconButton size="small" onClick={() => handleImageDelete(img)} sx={{ color: '#dc2626' }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {idocEnabled && panelPatientId && (
                  <>
                    <Divider sx={{ my: 4 }} />
                    <PatientIdocTab
                      embedded
                      patientId={panelPatientId}
                      patientName={patient?.full_name}
                      canManage={user?.role === 'admin' || user?.role === 'admin_master' || !!user?.professional_id}
                      onEditPatient={onEdit && patient ? () => onEdit(patient) : undefined}
                      onSeen={() => setIdocUnseen(0)}
                    />
                  </>
                )}
              </Box>
            )}

            {/* ── TAB 7: PROCEDIMENTOS ── */}
            {activeTab === 'procedures' && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MedicalIcon sx={{ color: '#3b82f6' }} /> Procedimentos do Paciente
                  </Typography>
                  <IconButton size="small" onClick={loadPatientProcedures}><RefreshIcon sx={{ fontSize: 18 }} /></IconButton>
                </Box>
                {proceduresLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#3b82f6' }} /></Box>
                ) : (
                  <>
                    {/* Aprovados */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e40af' }}>
                          Liberados — aprovados para executar ({patientProcedures.approved.length})
                        </Typography>
                      </Box>
                      {patientProcedures.approved.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                          <Typography variant="body2" color="text.secondary">Nenhum liberado.</Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={1}>
                          {patientProcedures.approved.map((item: any) => (
                            <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #3b82f6' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.procedure_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.budget_number ? `Orçamento ${item.budget_number}` : `Plano: ${item.plan_title || item.plan_number}`}
                                    {Number(item.quantity_required) > 1
                                      ? ` · ${Number(item.execution_count) || 0}/${item.quantity_required} executada(s)`
                                      : ''}
                                  </Typography>
                                  {(item.executions || []).length > 0 && (
                                    <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                      {(item.executions || []).map((ex: any, idx: number) => {
                                        const isLast = idx === (item.executions || []).length - 1;
                                        return (
                                          <Box key={ex.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                            <Typography variant="caption" sx={{ color: '#065f46' }}>
                                              Sessão {idx + 1}: {ex.executed_at ? new Date(ex.executed_at).toLocaleDateString('pt-BR') : '—'}
                                              {ex.clinic_professionals?.full_name ? ` · Executor: ${ex.clinic_professionals.full_name}` : ''}
                                              {ex.referring_professional?.full_name ? ` · Indicador: ${ex.referring_professional.full_name}` : ''}
                                            </Typography>
                                            {isLast && (
                                              <Tooltip title="Desfazer esta sessão">
                                                <IconButton size="small" onClick={() => handleUndoExecution(item.id)} sx={{ color: '#ef4444', p: 0.25 }}>
                                                  <UndoIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                                  <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select label="Status" value={item.status === 'cancelled' ? 'cancelled' : item.status || 'in_progress'}
                                      onChange={(e) => handleProcedureItemStatusChange(item.id, e.target.value as string)}>
                                      <MenuItem value="pending">Pendente</MenuItem>
                                      <MenuItem value="in_progress">Aprovado para executar</MenuItem>
                                      <MenuItem value="cancelled">Cancelar item</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <Button size="small" variant="contained" startIcon={<PlayIcon />}
                                    disabled={
                                      item.status !== 'in_progress'
                                      || (Number(item.quantity_required) > 1
                                        && (Number(item.execution_count) || 0) >= Number(item.quantity_required))
                                    }
                                    onClick={() => handleExecuteProcedure(item)}
                                    sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', fontWeight: 600 }}>
                                    {Number(item.quantity_required) > 1
                                      ? (() => {
                                          const done = Number(item.execution_count) || 0;
                                          const total = Number(item.quantity_required) || 1;
                                          const next = Math.min(done + 1, total);
                                          return done >= total ? 'Concluído' : `Executar (${next}/${total})`;
                                        })()
                                      : 'Executar'}
                                  </Button>
                                </Box>
                              </Box>
                              <InventoryPlanItemReservations
                                planItemId={item.id}
                                patientId={patient?.id}
                                treatmentPlanId={item.plan_id}
                                procedureId={item.procedure_id}
                                reservations={item.inventory_reservations}
                                onChanged={loadPatientProcedures}
                              />
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Box>

                    {/* Pendentes */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#92400e' }}>
                          Pendentes — aguardando liberação ({patientProcedures.pending.length})
                        </Typography>
                      </Box>
                      {patientProcedures.pending.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                          <Typography variant="body2" color="text.secondary">Nenhum pendente</Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={1}>
                          {patientProcedures.pending.map((item: any) => (
                            <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #f59e0b', opacity: 0.85 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.procedure_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.budget_number ? `Orçamento ${item.budget_number}` : `Plano: ${item.plan_title || item.plan_number}`}
                                    {Number(item.quantity_required) > 1
                                      ? ` · ${Number(item.execution_count) || 0}/${item.quantity_required} executada(s)`
                                      : ''}
                                  </Typography>
                                </Box>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <InputLabel>Status</InputLabel>
                                  <Select label="Status" value={item.status === 'cancelled' ? 'cancelled' : item.status || 'pending'}
                                    onChange={(e) => handleProcedureItemStatusChange(item.id, e.target.value as string)}>
                                    <MenuItem value="pending">Pendente</MenuItem>
                                    <MenuItem value="in_progress">Aprovado para executar</MenuItem>
                                    <MenuItem value="cancelled">Cancelar item</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Box>

                    {/* Executados */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#065f46' }}>
                          Executados ({patientProcedures.completed.length})
                        </Typography>
                      </Box>
                      {patientProcedures.completed.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                          <Typography variant="body2" color="text.secondary">Nenhum executado ainda</Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={1}>
                          {patientProcedures.completed.map((item: any) => {
                            const exec = item.execution;
                            const consumed: any[] = exec?.consumed_materials || [];
                            const executorName = exec?.clinic_professionals?.full_name || null;
                            const referringName = exec?.referring_professional?.full_name || null;
                            const executedAt = exec?.executed_at
                              ? new Date(exec.executed_at).toLocaleString('pt-BR')
                              : null;
                            const detailParts = [
                              item.plan_title || item.plan_number ? `Plano: ${item.plan_title || item.plan_number}` : null,
                              item.budget_number ? `Orçamento: ${item.budget_number}` : null,
                              item.tooth_number ? `Dente: ${item.tooth_number}${item.tooth_surface ? ` (${item.tooth_surface})` : ''}` : null,
                              item.quantity && item.quantity > 1 ? `Qtd: ${item.quantity}` : null,
                            ].filter(Boolean);
                            return (
                              <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #10b981' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.procedure_name}</Typography>
                                      {item.clinic_procedures?.code && (
                                        <Chip label={item.clinic_procedures.code} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                                      )}
                                    </Box>
                                    {detailParts.length > 0 && (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                        {detailParts.join(' · ')}
                                      </Typography>
                                    )}
                                    {(item.executions || []).length > 1 ? (
                                      (item.executions || []).map((ex: any, idx: number) => (
                                        <Typography key={ex.id} variant="caption" color="text.secondary" display="block">
                                          Sessão {idx + 1}: {ex.executed_at ? new Date(ex.executed_at).toLocaleString('pt-BR') : '—'}
                                          {ex.clinic_professionals?.full_name ? ` · ${ex.clinic_professionals.full_name}` : ''}
                                        </Typography>
                                      ))
                                    ) : executedAt && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Executado em: {executedAt}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" display="block" sx={{ color: '#065f46', fontWeight: 600, mt: 0.25 }}>
                                      Executor: {executorName || 'Não informado'}
                                    </Typography>
                                    {referringName && (
                                      <Typography variant="caption" display="block" sx={{ color: '#475569' }}>
                                        Indicador: {referringName}
                                      </Typography>
                                    )}
                                    {exec?.notes && (
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#334155', whiteSpace: 'pre-wrap' }}>
                                        Obs.: {exec.notes}
                                      </Typography>
                                    )}
                                    {item.notes && (
                                      <Typography variant="caption" display="block" sx={{ mt: 0.25, color: '#64748b', whiteSpace: 'pre-wrap' }}>
                                        Nota do item: {item.notes}
                                      </Typography>
                                    )}
                                    {consumed.length > 0 && (
                                      <Box sx={{ mt: 0.75 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280' }}>Materiais consumidos:</Typography>
                                        {consumed.map((c: any, idx: number) => (
                                          <Typography key={idx} variant="caption" display="block" sx={{ ml: 2, color: '#374151' }}>• {c.item_name}: {c.quantity} {c.unit}</Typography>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                  <Tooltip title="Desfazer execução">
                                    <IconButton size="small" onClick={() => handleUndoExecution(item.id)} sx={{ color: '#ef4444' }}>
                                      <UndoIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>

                    {patientProcedures.pending.length === 0 && patientProcedures.approved.length === 0 && patientProcedures.completed.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', borderRadius: 3, border: '2px dashed #e2e8f0' }}>
                        <MedicalIcon sx={{ fontSize: 56, color: '#e2e8f0', mb: 1.5 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>Nenhum procedimento vinculado</Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* ── TAB: DIAGNÓSTICOS ── */}
            {showClinicalEvolutions && activeTab === 'diagnoses' && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DiagnosisIcon sx={{ color: '#7c3aed' }} /> Diagnósticos do paciente
                  </Typography>
                  <IconButton size="small" onClick={() => loadPatientDiagnoses()}><RefreshIcon sx={{ fontSize: 18 }} /></IconButton>
                </Box>

                {primaryDiagnosisLabel && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Diagnóstico principal no painel: {primaryDiagnosisLabel}
                    </Typography>
                  </Alert>
                )}

                {needsPrimaryDiagnosisSelection && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Este paciente tem mais de um diagnóstico nas evoluções. Selecione qual deve aparecer no topo do painel.
                  </Alert>
                )}

                {diagnosesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#7c3aed' }} /></Box>
                ) : !patientDiagnoses?.entries?.length ? (
                  <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                    <DiagnosisIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Nenhum diagnóstico registrado nas evoluções
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Informe o diagnóstico ao criar ou editar uma evolução.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {patientDiagnoses.entries.map((entry) => {
                      const label = formatDiagnosisPair(entry.diagnosis, entry.diagnosis_code) || '—';
                      const dateLabel = entry.evolution_date
                        ? formatPatientDocumentDate(entry.evolution_date)
                        : 'Data não informada';
                      const timeLabel = entry.evolution_time ? ` · ${entry.evolution_time.slice(0, 5)}` : '';
                      const isSetting = settingPrimaryEvolutionId === entry.evolution_id;
                      return (
                        <Paper
                          key={entry.evolution_id}
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            borderLeft: entry.is_primary ? '4px solid #7c3aed' : '4px solid #e2e8f0',
                            bgcolor: entry.is_primary ? '#faf5ff' : '#fff',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>{label}</Typography>
                                {entry.is_primary && (
                                  <Chip label="Principal" size="small" sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 700, height: 22 }} />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Evolução de {dateLabel}{timeLabel}
                                {entry.professional_name ? ` · ${entry.professional_name}` : ''}
                              </Typography>
                            </Box>
                            {!entry.is_primary && (
                              <Button
                                size="small"
                                variant={needsPrimaryDiagnosisSelection ? 'contained' : 'outlined'}
                                disabled={Boolean(settingPrimaryEvolutionId)}
                                onClick={() => handleSetPrimaryDiagnosis(entry.evolution_id)}
                                sx={needsPrimaryDiagnosisSelection ? { bgcolor: '#7c3aed' } : undefined}
                              >
                                {isSetting ? 'Salvando…' : 'Definir como principal'}
                              </Button>
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* ── Sub-dialogs ── */}

      {/* Executar procedimento */}
      <Dialog open={executeDialogOpen} onClose={() => setExecuteDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Executar Procedimento
          <IconButton onClick={() => setExecuteDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers className={isMobile ? MOBILE_FORM_SURFACE_CLASS : undefined}>
          {executingItem && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{executingItem.procedure_name}</Typography>
                {(executingItem.stage_name || executingItem.stage || executingItem.clinic_procedures?.default_stage) && (
                  <Chip
                    size="small"
                    label={`Etapa: ${executingItem.stage_name || executingItem.stage || executingItem.clinic_procedures?.default_stage}`}
                    sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
                  />
                )}
              </Box>
              {Number(executingItem.quantity_required) > 1 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Será registrada apenas a sessão {Math.min((Number(executingItem.execution_count) || 0) + 1, Number(executingItem.quantity_required))} de {executingItem.quantity_required}.
                  {' '}As demais continuam liberadas para executar em outro dia.
                </Alert>
              )}
              {executingItem.required_materials?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: '#8b5cf6' }} /> Materiais que serão consumidos
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Consumo</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Estoque</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Após</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {executingItem.required_materials.map((m: any) => {
                          const inv = m.clinic_inventory_items;
                          const needed = Number(m.quantity_per_execution || 1);
                          const current = Number(inv?.current_stock ?? 0);
                          const after = current - needed;
                          const negative = after < 0;
                          const perExecution = Math.max(Number(m.quantity_per_execution || 1), 0.001);
                          const remainingCoverage = Math.max(0, Math.floor(after / perExecution));
                          return (
                            <TableRow key={m.id}>
                              <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{inv?.name || '?'}</Typography></TableCell>
                              <TableCell align="center">{needed} {inv?.unit || ''}</TableCell>
                              <TableCell align="center">{current}</TableCell>
                              <TableCell align="center" sx={{ color: negative ? '#dc2626' : '#059669', fontWeight: 700 }}>
                                {after.toFixed(1)} {negative && <WarningIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />}
                                {!negative && (
                                  <Typography variant="caption" display="block" sx={{ color: '#047857', fontSize: '0.62rem' }}>
                                    cobre +{remainingCoverage} execução(ões)
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Profissional executor *</InputLabel>
                <Select value={executeForm.professional_id} label="Profissional executor *"
                  onChange={(e) => setExecuteForm({ ...executeForm, professional_id: e.target.value })}>
                  {professionals.map((p: any) => (
                    <MenuItem key={p.id} value={p.id}>{p.full_name}{p.specialty ? ` — ${p.specialty}` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Profissional indicador *</InputLabel>
                <Select value={executeForm.referring_professional_id} label="Profissional indicador *"
                  onChange={(e) => setExecuteForm({ ...executeForm, referring_professional_id: e.target.value })}>
                  <MenuItem value=""><em>Nenhum</em></MenuItem>
                  {professionals.map((p: any) => (
                    <MenuItem key={p.id} value={p.id}>{p.full_name}{p.specialty ? ` — ${p.specialty}` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
                O indicador recebe comissão de indicação executada (ex.: valor fixo por Faco/Refrativa), conforme regras cadastradas.
              </Typography>
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#334155', mb: 0.5 }}>
                  Data da execução
                </FormLabel>
                <RadioGroup
                  row
                  value={executeForm.date_mode}
                  onChange={(e) => setExecuteForm({
                    ...executeForm,
                    date_mode: e.target.value as 'today' | 'custom',
                    executed_date: e.target.value === 'today'
                      ? todayLocalISODate()
                      : executeForm.executed_date,
                  })}
                >
                  <FormControlLabel value="today" control={<Radio size="small" />} label="Executar Hoje" />
                  <FormControlLabel value="custom" control={<Radio size="small" />} label="Data Personalizada" />
                </RadioGroup>
              </FormControl>
              {executeForm.date_mode === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  required
                  type="date"
                  label="Data da execução"
                  InputLabelProps={{ shrink: true }}
                  value={executeForm.executed_date}
                  onChange={(e) => setExecuteForm({ ...executeForm, executed_date: e.target.value })}
                  sx={{ mb: 2 }}
                />
              )}
              <TextField fullWidth size="small" label="Observações (opcional)" multiline rows={2}
                value={executeForm.notes} onChange={(e) => setExecuteForm({ ...executeForm, notes: e.target.value })} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setExecuteDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={confirmExecute}
            disabled={executeSaving || !executeForm.professional_id || (executeForm.date_mode === 'custom' && !executeForm.executed_date)}
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', fontWeight: 600 }}
          >
            {executeSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirmar Execução'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox imagens */}
      <Dialog open={!!expandedImage} onClose={() => setExpandedImage(null)} maxWidth="lg" fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.92)', boxShadow: 'none', m: isMobile ? 0 : 1 } }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton onClick={() => setExpandedImage(null)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
            <CloseIcon />
          </IconButton>
          {expandedImage && <img src={expandedImage} alt="Ampliada" style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block', objectFit: 'contain' }} />}
          {expandedImage && onSendMediaToChat && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => {
                  onSendMediaToChat(expandedImage, 'image');
                  setExpandedImage(null);
                }}
                sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, textTransform: 'none', borderRadius: 2 }}
              >
                Enviar no chat
              </Button>
            </Box>
          )}
        </Box>
      </Dialog>

      <Dialog open={bulkRescheduleOpen} onClose={() => !bulkRescheduleSaving && setBulkRescheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remarcar em lote</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {BULK_RESCHEDULE_TOOLTIP}
          </Typography>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Serão alterados <strong>{upcomingForBulkReschedule.length}</strong> agendamento(s) futuro(s) deste paciente.
            Marque o que deseja mudar em todos eles.
          </Alert>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkRescheduleChangeTime}
                  onChange={(e) => setBulkRescheduleChangeTime(e.target.checked)}
                />
              }
              label="Alterar horário de início"
            />
            {bulkRescheduleChangeTime && (
              <TextField
                label="Novo horário de início"
                type="time"
                fullWidth
                value={bulkRescheduleTime}
                onChange={(e) => setBulkRescheduleTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkRescheduleChangeWeekday}
                  onChange={(e) => setBulkRescheduleChangeWeekday(e.target.checked)}
                />
              }
              label="Alterar dia da semana"
            />
            {bulkRescheduleChangeWeekday && (
              <FormControl fullWidth>
                <InputLabel id="bulk-reschedule-weekday-label">Novo dia da semana</InputLabel>
                <Select
                  labelId="bulk-reschedule-weekday-label"
                  label="Novo dia da semana"
                  value={bulkRescheduleWeekday}
                  onChange={(e) => setBulkRescheduleWeekday(Number(e.target.value))}
                >
                  {BULK_WEEKDAY_OPTIONS.map((w) => (
                    <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {bulkRescheduleWeekendHits.length > 0 && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {bulkRescheduleWeekendHits.length === 1
                  ? `Uma consulta ficará em ${bulkRescheduleWeekendHits[0].formatted} (${bulkRescheduleWeekendHits[0].weekdayLabel}).`
                  : `${bulkRescheduleWeekendHits.length} consulta(s) cairão em fim de semana após a mudança.`}
              </Alert>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkRescheduleChangeProfessional}
                  onChange={(e) => setBulkRescheduleChangeProfessional(e.target.checked)}
                />
              }
              label="Alterar profissional"
            />
            {bulkRescheduleChangeProfessional && (
              <FormControl fullWidth>
                <InputLabel id="bulk-reschedule-prof-label">Profissional</InputLabel>
                <Select
                  labelId="bulk-reschedule-prof-label"
                  label="Profissional"
                  value={bulkRescheduleProfessionalId}
                  onChange={(e) => setBulkRescheduleProfessionalId(String(e.target.value))}
                >
                  {professionals.map((p: any) => (
                    <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkRescheduleOpen(false)} disabled={bulkRescheduleSaving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => void runBulkReschedule(false)}
            disabled={bulkRescheduleSaving || !bulkRescheduleCanSubmit}
            startIcon={bulkRescheduleSaving ? <CircularProgress size={18} color="inherit" /> : <EventRepeatIcon />}
          >
            {bulkRescheduleSaving ? 'Remarcando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {showClinicalEvolutions && (
      <>
      <Dialog
        open={Boolean(continueTodayEvolution)}
        onClose={() => setContinueTodayEvolution(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Já existe uma evolução sua hoje</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {continueTodayEvolution && isEvolutionSigned(continueTodayEvolution)
              ? 'Você concluiu uma evolução para este paciente hoje e ainda está no prazo de edição. Deseja continuar nela em vez de abrir outra?'
              : 'Você tem um rascunho de evolução para este paciente hoje. Deseja continuar nele em vez de abrir outra?'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => openEvolutionDialog(null)} sx={{ textTransform: 'none' }}>
            Criar nova
          </Button>
          <Button
            variant="contained"
            onClick={() => openEvolutionDialog(String(continueTodayEvolution?.id))}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Continuar evolução de hoje
          </Button>
        </DialogActions>
      </Dialog>

      <HealthcareEvolutionDialog
        open={evolutionDialogOpen}
        onClose={() => {
          setEvolutionDialogOpen(false);
          setEvolutionExistingId(null);
          // O autosave já gravou no servidor; sem isso o rascunho só aparecia após F5.
          void fetchSummary();
          void loadPatientEvolutions({ silent: true });
        }}
        existingEvolutionId={evolutionExistingId}
        patient={patient ? {
          id: patient.id,
          full_name: patient.full_name,
          primary_diagnosis: patient.primary_diagnosis,
          primary_diagnosis_code: patient.primary_diagnosis_code,
        } : null}
        onSaved={async () => {
          await fetchSummary();
          void loadPatientEvolutions({ silent: true });
          void loadPatientDiagnoses({ silent: true });
          setSnackMsg(evolutionExistingId ? 'Evolução atualizada com sucesso!' : 'Evolução criada com sucesso!');
        }}
        onError={(msg) => setSnackMsg(msg)}
      />

      <EvolutionAdditionsDialog
        open={Boolean(evolutionAdditionsTarget)}
        onClose={() => {
          setEvolutionAdditionsTarget(null);
          void fetchSummary();
          void loadPatientEvolutions({ silent: true });
        }}
        evolution={evolutionAdditionsTarget || { id: '' }}
        patientId={patientId || patient?.id}
        viewer={evolutionViewer}
        professionalId={user?.professional_id || evolutionAdditionsTarget?.professional_id || null}
        onError={(msg) => setSnackMsg(msg)}
      />

      <EvolutionInvalidateDialog
        open={Boolean(invalidateTarget)}
        subtitle={evolutionInvalidateDialogSubtitle(invalidateTarget)}
        submitting={invalidatingEvolution}
        error={invalidateError}
        onClose={() => {
          if (invalidatingEvolution) return;
          setInvalidateTarget(null);
          setInvalidateError(null);
        }}
        onConfirm={handleConfirmInvalidateEvolution}
      />
      </>
      )}

      {!isFisioterapia && (
      <HealthcareMedicalReportDialog
        open={medicalReportDialogOpen}
        onClose={() => {
          setMedicalReportDialogOpen(false);
          setMedicalReportExistingId(null);
        }}
        existingReportId={medicalReportExistingId}
        patientId={patient?.id || ''}
        patientName={patient?.full_name || ''}
        onSaved={async () => {
          await fetchSummary();
          setSnackMsg(medicalReportExistingId ? 'Relatório atualizado com sucesso!' : 'Relatório criado com sucesso!');
        }}
      />
      )}

      <AsaasChargeDialog
        open={!!asaasChargeReceivable || asaasPickOpen}
        onClose={() => { setAsaasChargeReceivable(null); setAsaasPickOpen(false); }}
        patient={patient ? { id: patientId, name: patient.full_name, phone: patient.whatsapp || patient.phone } : null}
        receivable={asaasChargeReceivable}
        allowPickReceivable={asaasPickOpen && !asaasChargeReceivable}
        onChanged={() => void loadReceivables()}
      />

      {/* ── Dialog: Receber Agora ── */}
      <Dialog open={receiveDialogOpen} onClose={() => !receivingReceivable && setReceiveDialogOpen(false)}
        maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon sx={{ color: '#10b981' }} /> Registrar Recebimento
          </Box>
          <IconButton onClick={() => !receivingReceivable && setReceiveDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers className={isMobile ? MOBILE_FORM_SURFACE_CLASS : undefined}>
          {activeReceivable && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{activeReceivable.description}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total: {formatCurrency(activeReceivable.amount)} · Pago: {formatCurrency(activeReceivable.amount_paid || 0)}
              </Typography>
            </Box>
          )}
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField fullWidth size="small" required label="Valor recebido (R$)" type="number" inputProps={{ min: 0, step: '0.01' }}
              value={receiveForm.amount}
              onChange={(e) => setReceiveForm({ ...receiveForm, amount: e.target.value })} />
            <TextField fullWidth size="small" required label="Data" type="date" InputLabelProps={{ shrink: true }}
              value={receiveForm.payment_date}
              onChange={(e) => setReceiveForm({ ...receiveForm, payment_date: e.target.value })} />
            <FormControl fullWidth size="small">
              <InputLabel>Forma de Recebimento</InputLabel>
              <Select label="Forma de Recebimento" value={receiveForm.payment_method}
                onChange={(e) => {
                  const method = String(e.target.value);
                  setReceiveForm({
                    ...receiveForm,
                    payment_method: method,
                    card_installments: isCardInstallmentPaymentMethod(method) ? receiveForm.card_installments : '1',
                  });
                }}>
                {receivablePaymentOptions.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
            {receiveIsCard && (
              <FormControl fullWidth size="small">
                <InputLabel>Parcelas no cartão</InputLabel>
                <Select label="Parcelas no cartão" value={receiveForm.card_installments}
                  onChange={(e) => setReceiveForm({ ...receiveForm, card_installments: String(e.target.value) })}>
                  {CARD_INSTALLMENT_OPTIONS.map(n => (
                    <MenuItem key={n} value={String(n)}>
                      {formatCardInstallmentOption(n, parseFloat(receiveForm.amount) || 0)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {receiveNeedsBrand && (
              <CardBrandSelect
                value={receiveForm.card_brand}
                enabledBrands={receiveEnabledCardBrands}
                error={!receiveForm.card_brand}
                onChange={(brand) => setReceiveForm({ ...receiveForm, card_brand: brand })}
              />
            )}
            <TextField fullWidth size="small" label="Observações (opcional)" multiline rows={2}
              value={receiveForm.notes}
              onChange={(e) => setReceiveForm({ ...receiveForm, notes: e.target.value })} />
            {receiveMultiSessionItems.length > 0 && receiveWillCompleteBudget && (
              <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: '#bfdbfe', bgcolor: '#f8fafc' }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={receiveForm.execute_session_today}
                      onChange={(e) => setReceiveForm({ ...receiveForm, execute_session_today: e.target.checked })}
                    />
                  )}
                  label={(
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Paciente já realiza a 1ª sessão hoje
                    </Typography>
                  )}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: receiveForm.execute_session_today ? 1 : 0, pl: 4 }}>
                  O pacote tem mais de uma sessão. Marque se não precisa agendar a primeira — o próximo agendamento já será da 2ª sessão.
                </Typography>
                {receiveForm.execute_session_today && (
                  <Stack spacing={1.25} sx={{ pl: 1 }}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Profissional da sessão</InputLabel>
                      <Select
                        label="Profissional da sessão"
                        value={receiveForm.session_professional_id}
                        onChange={(e) => setReceiveForm({ ...receiveForm, session_professional_id: String(e.target.value) })}
                      >
                        {professionals.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {receiveMultiSessionItems.length > 1 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                          Procedimentos (1ª sessão hoje)
                        </Typography>
                        <Stack sx={{ mt: 0.5 }}>
                          {receiveMultiSessionItems.map((item) => {
                            const itemId = String(item.id || '');
                            const checked = receiveForm.session_budget_item_ids.includes(itemId);
                            return (
                              <FormControlLabel
                                key={itemId || item.procedure_name}
                                control={(
                                  <Checkbox
                                    size="small"
                                    checked={checked}
                                    disabled={!itemId}
                                    onChange={(e) => {
                                      const next = e.target.checked
                                        ? [...receiveForm.session_budget_item_ids, itemId]
                                        : receiveForm.session_budget_item_ids.filter((id) => id !== itemId);
                                      setReceiveForm({ ...receiveForm, session_budget_item_ids: next });
                                    }}
                                  />
                                )}
                                label={(
                                  <Typography variant="body2">
                                    {item.procedure_name}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                      {' '}· {getBudgetItemSessionCount(item)} sessões
                                    </Typography>
                                  </Typography>
                                )}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
            )}
            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
              Ao confirmar, o valor é lançado como entrada no <strong>caixa do dia</strong> (precisa estar aberto).
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setReceiveDialogOpen(false)} disabled={receivingReceivable} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={submitReceive} disabled={receivingReceivable}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, textTransform: 'none', fontWeight: 700 }}>
            {receivingReceivable ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirmar Recebimento'}
          </Button>
        </DialogActions>
      </Dialog>

      {!isFisioterapia && (
      <>
      <PrescriptionFormDialog
        open={prescriptionDialogOpen}
        onClose={() => {
          setPrescriptionDialogOpen(false);
          setEditingPrescriptionId(null);
        }}
        patientId={patientId}
        professionalId={linkedProfessionalId || user?.professional_id || ''}
        existingPrescriptionId={editingPrescriptionId}
        initialPatient={patient}
        initialProfessional={
          professionals.find((p) => p.id === (linkedProfessionalId || user?.professional_id))
          || (linkedProfessionalId && linkedProfessionalName
            ? { id: linkedProfessionalId, full_name: linkedProfessionalName }
            : undefined)
        }
        onMemedSaved={refreshAfterMemedPrescription}
        onSuccess={({ completion, savedPrescriptions, printFirstViaOnly }) => {
          const wasEditing = Boolean(editingPrescriptionId);
          setPrescriptionDialogOpen(false);
          setEditingPrescriptionId(null);
          setActiveTab('history');
          void fetchSummary({ silent: true });
          void loadPatientPrescriptions({ silent: true });
          void loadReceivables();

          const baseMsg = wasEditing ? 'Receituário atualizado.' : 'Receituário salvo.';

          if (completion === 'print') {
            void (async () => {
              try {
                const queue = await buildPrescriptionPrintQueueFromIds(
                  async (id) => {
                    const res = await axios.get(getApiUrl(`/clinic/prescriptions/${id}/print`));
                    return res.data?.data ?? null;
                  },
                  savedPrescriptions,
                );
                if (!queue.length) {
                  setSnackMsg('Receituário salvo, mas não foi possível gerar a impressão.');
                  return;
                }
                setRxPrintFirstViaOnly(Boolean(printFirstViaOnly));
                setRxPrintQueue(queue);
                setRxPrintDialogOpen(true);
                setSnackMsg(`${baseMsg} Pré-visualização aberta.`);
              } catch {
                setSnackMsg('Receituário salvo, mas não foi possível gerar a impressão.');
              }
            })();
            return;
          }

          if (completion === 'sign' && savedPrescriptions.length > 1) {
            void (async () => {
              const fresh = await Promise.all(
                savedPrescriptions.filter((saved) => saved?.id).map(async (saved) => {
                  try {
                    const { data } = await axios.get(getApiUrl(`/clinic/prescriptions/${saved.id}`));
                    return data?.data ?? saved;
                  } catch {
                    return saved;
                  }
                }),
              );
              setSnackMsg(baseMsg);
              openRxBatchSign(fresh);
            })();
            return;
          }

          if (completion === 'sign') {
            const primary = savedPrescriptions[0];
            void (async () => {
              if (primary?.id) {
                try {
                  const { data } = await axios.get(getApiUrl(`/clinic/prescriptions/${primary.id}`));
                  const full = data?.data ?? primary;
                  if (full?.professional_id) {
                    setSignPrescriptionTarget(full);
                    setSignPrescriptionOpen(true);
                    setSnackMsg(baseMsg);
                    return;
                  }
                } catch {
                  /* usa registro parcial */
                }
                if (primary.professional_id) {
                  setSignPrescriptionTarget(primary);
                  setSignPrescriptionOpen(true);
                  setSnackMsg(baseMsg);
                  return;
                }
              }
              setSnackMsg(`${baseMsg} Defina o prescritor para assinar.`);
            })();
            return;
          }

          setSnackMsg(baseMsg);
        }}
      />

      <SignPrescriptionDialog
        open={signPrescriptionOpen}
        prescription={signPrescriptionTarget}
        documents={signPrescriptionBatch}
        onClose={closeRxSignDialog}
        onSigned={(info) => {
          closeRxSignDialog();
          setActiveTab('history');
          void fetchSummary({ silent: true });
          void loadReceivables();
          void loadPatientPrescriptions({ silent: true });
          setSnackMsg(`Receituário assinado. Código: ${info?.signature_verification_code || '—'}`);
          if (info?.id) {
            void openSignedPrescriptionPdf(info.id, info.signed_pdf_url).catch(() => undefined);
          }
        }}
        onBatchSigned={(result: BatchSignResult) => {
          closeRxSignDialog();
          setSelectedRxRows({});
          setActiveTab('history');
          void fetchSummary({ silent: true });
          void loadPatientPrescriptions({ silent: true });
          const failedNote = result.failed.length > 0
            ? ` ${result.failed.length} não assinada(s): ${result.failed[0].error}`
            : '';
          setSnackMsg(`${result.signed.length} receita(s) assinada(s) com uma única autorização.${failedNote}`);
          // Próximo passo natural: mandar tudo ao paciente num link só.
          if (result.signed.length > 1) setRxBundleSendIds(result.signed.map((s) => s.id));
        }}
      />

      <PrescriptionBatchPrintDialog
        open={rxPrintDialogOpen}
        onClose={() => setRxPrintDialogOpen(false)}
        items={rxPrintQueue}
        initialFirstViaOnly={rxPrintFirstViaOnly}
      />

      <SendDocumentDialog
        open={Boolean(rxBundleSendIds)}
        onClose={() => setRxBundleSendIds(null)}
        documentType="prescription"
        documentId={rxBundleSendIds?.[0] || ''}
        documentIds={rxBundleSendIds || undefined}
        patientId={patientId || undefined}
        patientEmail={patient?.email}
        patientPhone={patient?.phone}
        patientWhatsapp={patient?.whatsapp}
        patientName={patient?.full_name}
        documentLabel="Receituário"
      />
      </>
      )}

      <MemedPrescriptionFeedback
        error={memedPrescription.error}
        notice={memedPrescription.notice}
        clearError={memedPrescription.clearError}
        clearNotice={memedPrescription.clearNotice}
      />

      {/* Feedback rápido em tela */}
      {snackMsg && (
        <Alert
          severity="info"
          onClose={() => setSnackMsg('')}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, maxWidth: 400 }}
        >
          {snackMsg}
        </Alert>
      )}

      {plansEnabled && isFisioterapia && (
        <SellPlanDialog
          open={sellPlanDialogOpen}
          onClose={() => setSellPlanDialogOpen(false)}
          initialPatient={patientId ? { id: patientId, full_name: patient?.full_name } : null}
          onSold={() => {
            refreshActivePlans();
            void loadReceivables();
            void fetchSummary({ silent: true });
            setSnackMsg('Plano vendido com sucesso.');
          }}
        />
      )}

      {cashRegisterPromptUi}
      {nfseServiceCodeDialog}
    </Box>
  );
};

export default PatientDetailPanel;
